import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDM } from '../contexts/DMContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { BrandLoader } from '../components/BrandLoader';
import { FeaCoordinatorPanel } from '../components/FeaCoordinatorPanel';
import { 
  requestsApi, 
  documentsApi, 
  assignmentsApi, 
  reviewsApi,
  approvalsApi,
  auditLogsApi,
  feasibilityApi,
  changeRequestsApi,
  usersApi,
  specialtiesApi,
  commentsApi,
  handoversApi,
  coordinatorsApi,
  directorApi,
  execDirectorApi,
  signaturesApi,
  ctaFinalApprovalApi,
  adminApi,
  profileApi,
  requestTypeSettingsApi
} from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { DiscussionThread } from '../components/DiscussionThread';
import { isSignaturesTabStatus } from '../lib/signaturesTabStatuses';
import CTAFinalApprovalModal from '../components/CTAFinalApprovalModal';
import FtChairmanApprovalModal from '../components/ft/FtChairmanApprovalModal';
import GenerateBundleDialog from '../components/GenerateBundleDialog';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileText, 
  Upload, 
  User, 
  Clock, 
  CheckCircle2,
  XCircle,
  AlertCircle,
  Send,
  Download,
  History,
  MessageSquare,
  MessageCircle,
  Users,
  Edit,
  X,
  ArrowRightLeft,
  AlertTriangle,
  Undo2,
  Filter,
  Eye,
  ChevronDown,
  PenTool,
  RefreshCw,
  Star,
  FlaskConical,
  Loader2,
  Plus,
  Trash2,
  Settings2,
  GripVertical,
  ShieldAlert,
  Mail,
  Search,
  Bell,
  ListChecks
} from 'lucide-react';
import { SpecialtyMultiSelect } from '../components/SpecialtyMultiSelect';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { FtWorkflowActions } from '../components/ft/FtWorkflowActions';
import { ReviewStagesPanel } from '../components/ReviewStagesPanel';
import { ReviewStagesEditor, stagesFromRequest } from '../components/ReviewStagesEditor';
import { 
  getStatusColor, 
  getStatusLabel, 
  getTypeColor, 
  formatDate, 
  formatDateTime,
  getRoleLabel 
} from '../lib/utils';

// Extracted tab components
import { 
  OverviewTab, 
  DocumentsTab, 
  AssignmentsTab, 
  ApprovalsTab, 
  ResponsesTab, 
  SignaturesTab, 
  AuditTab 
} from './request-details';

export default function RequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isCompanyRep, isChairman, isCoordinator, isCRD, isReviewer, isPI, isConsultant, isInvestigator, isAdmin, isDirector, isExecDirector } = useAuth();
  // iter163i — DM deep-link helper for the "Discuss in DM" button.
  const { eligible: dmEligible, startDMWith } = useDM();
  
  // Get tab and comment from URL params
  const initialTab = searchParams.get('tab') || 'overview';
  const highlightedCommentId = searchParams.get('comment');
  const highlightStyle = searchParams.get('highlight'); // e.g. 'nudge' → red ring
  
  // Controlled tab state
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const [request, setRequest] = useState(null);
  const [overviewLayout, setOverviewLayout] = useState(null);
  // iter125 — Shadcn dialog state for the friendly Approve confirmation modal
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [bundleTool, setBundleTool] = useState({ available: false });
  const [showBundleDialog, setShowBundleDialog] = useState(false);
  // iter112 — visible-rows count bubbled up from DocumentsTab so the tab badge matches
  // the in-tab header (Documents (N)).
  const [docsTabCount, setDocsTabCount] = useState(0);
  // iter110 — when "Upload New Version" is clicked inside an existing bundle thread, we
  // open the GenerateBundleDialog with the chain's doc_type pre-selected & locked.
  const [bundleDialogLockedDocType, setBundleDialogLockedDocType] = useState(null);
  useEffect(() => {
    if (request?.type && ['FEA', 'CTA', 'FT'].includes(request.type)) {
      requestTypeSettingsApi.getOverviewLayout(request.type)
        .then((res) => setOverviewLayout(res.data))
        .catch(() => {});
    }
    if (request?.type && ['CTA', 'FT'].includes(request.type)) {
      requestsApi.getBundleTool(id)
        .then((res) => setBundleTool(res.data))
        .catch(() => {});
    }
  }, [request?.type, id]);
  const [accessDenied, setAccessDenied] = useState(null);
  const [documents, setDocuments] = useState([]);
  // iter112 — pre-seed docsTabCount from documents.length so the tab badge doesn't
  // briefly read (0) before DocumentsTab mounts and bubbles the precise visible-rows count.
  useEffect(() => {
    if (Array.isArray(documents)) setDocsTabCount((c) => c || documents.length);
  }, [documents]);
  const [assignments, setAssignments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [feasibilityResponses, setFeasibilityResponses] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  // iter143 — unassigned doc-thread banner.
  const [docThreadIndex, setDocThreadIndex] = useState([]);
  const [unassignedBannerDismissed, setUnassignedBannerDismissed] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [tabsConfig, setTabsConfig] = useState(null);  // Role-Visibility per-tab config (Phase A)
  const [loading, setLoading] = useState(true);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [regeneratingPdf, setRegeneratingPdf] = useState(null);
  const [responseFields, setResponseFields] = useState([]);  // Customizable FEA response fields
  const [showResponseFieldsSection, setShowResponseFieldsSection] = useState(false);
  // iter166e — First-time pulse hint for "Customize Response Fields". Stored
  // in localStorage so the gentle attention-grabber only fires on the
  // user's first interaction with the Route-to-Consultants dialog.
  const [responseFieldsHintActive, setResponseFieldsHintActive] = useState(false);
  const [routingConsultants, setRoutingConsultants] = useState(false);  // Loading state for routing
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignPIModal, setShowAssignPIModal] = useState(false);
  const [showAssignReviewerModal, setShowAssignReviewerModal] = useState(false);
  // iter166c — Document Viewer (read-only) toggles per dialog.
  //   - Route to Consultants (FEA): ON by default — the team default is that
  //     every consultant gets full doc-tab visibility.
  //   - Assign Investigators (CTA): OFF by default — investigators should
  //     only see docs they were specifically threaded into until promoted.
  //   - Assign Reviewer (CTA/FEA): OFF by default — same rationale.
  const [routeViewerEnabled, setRouteViewerEnabled] = useState(true);
  const [piViewerEnabled, setPiViewerEnabled] = useState(false);
  const [reviewerViewerEnabled, setReviewerViewerEnabled] = useState(false);
  // iter166d — Search filter state for the three assignment dialogs.
  // Cleared whenever the dialog closes via onOpenChange. Filtering is
  // case-insensitive across name + email.
  const [routeSearch, setRouteSearch] = useState('');
  const [piSearch, setPiSearch] = useState('');
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [showRouteConsultantModal, setShowRouteConsultantModal] = useState(false);
  const [showAutoRouteConfirm, setShowAutoRouteConfirm] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [showFeasibilityModal, setShowFeasibilityModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditSpecialtyModal, setShowEditSpecialtyModal] = useState(false);
  // Company inline edit (FT only, admin-level)
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [editCompanyData, setEditCompanyData] = useState({ company_account_id: '', reason: '' });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [savingCompany, setSavingCompany] = useState(false);
  const [showCTAFinalApprovalModal, setShowCTAFinalApprovalModal] = useState(false);
  // Pre-emptive skip flow (Chairman/Admin/CRD on CTA before Coordinator approval)
  const [showSkipApprovalsDialog, setShowSkipApprovalsDialog] = useState(false);
  const [pendingApprovers, setPendingApprovers] = useState([]);
  const [skippingApprovals, setSkippingApprovals] = useState(false);
  // iter143 — Edit E-Signatures modal focus doc (filters to one doc when set).
  const [editEsigFocusDocId, setEditEsigFocusDocId] = useState(null);
  const [showEditESigModal, setShowEditESigModal] = useState(false);
  // iter142 — Edit Request Review dialog (standalone review-stages editor).
  const [showEditReviewStagesModal, setShowEditReviewStagesModal] = useState(false);
  const [editReviewStages, setEditReviewStages] = useState([]);
  const [editReviewMode, setEditReviewMode] = useState('sequential');
  const [savingReviewStages, setSavingReviewStages] = useState(false);
  // Phase 3: additional signature rounds (CTA)
  const [showStartRoundModal, setShowStartRoundModal] = useState(false);
  // iter167 Issue 27 — FT new-signature-round opener is owned by
  // `FtWorkflowActions` (it controls its own per-doc modal). RequestDetails
  // stashes its `openModal` callback here so the skip-approvals success
  // handler can call it after the chairman decides to skip pending
  // approvals — keeping the FT gate flow at parity with CTA.
  const ftStartRoundOpenerRef = useRef(null);
  const [ctaRoundsConfig, setCtaRoundsConfig] = useState(null);
  // Phase 4: per-document (default for CTA) vs bundle e-sign mode — controls which modal renders
  const [ctaEsignBundle, setCtaEsignBundle] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [showDocCommentsModal, setShowDocCommentsModal] = useState(false);
  const [selectedDocForComments, setSelectedDocForComments] = useState(null);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [showRejectHandoverModal, setShowRejectHandoverModal] = useState(false);
  // CTA Chairman → Coordinator doc-thread auto-assign confirmation
  const [showCoordAutoAssign, setShowCoordAutoAssign] = useState(false);
  const [assignedCoordName, setAssignedCoordName] = useState('');
  const [coordAutoAssigning, setCoordAutoAssigning] = useState(false);
  // Doc-gate preview (side-by-side approve)
  const [gatePreviewDoc, setGatePreviewDoc] = useState(null);
  // Doc preview triggered from inside the Discussion tab's thread "Preview" button (soft-blue go-to-thread)
  const [threadPreviewDoc, setThreadPreviewDoc] = useState(null);
  
  // Form states
  const [assignData, setAssignData] = useState({ assignee_user_id: '', assignment_role: '' });
  const [eligiblePIs, setEligiblePIs] = useState([]);
  const [eligibleConsultants, setEligibleConsultants] = useState([]);
  const [allReviewers, setAllReviewers] = useState([]);
  const [selectedReviewerIds, setSelectedReviewerIds] = useState([]);
  const [autoRouteLoading, setAutoRouteLoading] = useState(false);
  const [reviewData, setReviewData] = useState({ decision: '', comment: '', visible_to_company: false });
  const [changeRequestData, setChangeRequestData] = useState({ message: '', visible_to_company: true });
  const [feasibilityData, setFeasibilityData] = useState({ can_participate: true });
  const [feasibilityResponseFields, setFeasibilityResponseFields] = useState([]);
  const [feasibilityFieldsLoading, setFeasibilityFieldsLoading] = useState(false);
  const [uploadData, setUploadData] = useState({ 
    file: null, 
    doc_type: '', 
    custom_doc_type: '', 
    visibility: 'COMPANY',
    visibility_groups: ['@internal'],
    visibility_users: [],
    is_attachment: false,
    parent_document_id: ''
  });
  const [customDocTypes, setCustomDocTypes] = useState([]); // Custom doc types for this request
  // Upload dialog doc type options — configurable per request type by Admin
  const [uploadDocTypes, setUploadDocTypes] = useState(['Protocol', 'ICF', 'Budget', 'CTA_Draft', 'Insurance', 'Synopsis']);
  const [editSpecialtyData, setEditSpecialtyData] = useState({ specialty_ids: [], reason: '' });
  const [editSpecSearch, setEditSpecSearch] = useState('');
  const [selectedConsultantId, setSelectedConsultantId] = useState('');
  // Unified routing UI (FEA)
  const [routeSelectedIds, setRouteSelectedIds] = useState([]);
  const [externalEmails, setExternalEmails] = useState([]);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [unifiedRouteLoading, setUnifiedRouteLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);

  // Unified Assign Investigators UI (CTA) — mirrors FEA route-to-consultants
  const [piRouteSelectedIds, setPiRouteSelectedIds] = useState([]);
  const [piRoleByUser, setPiRoleByUser] = useState({}); // { [user_id]: 'PI' | 'COI' }
  const [piExternalEmails, setPiExternalEmails] = useState([]);
  const [piExternalEmailRoles, setPiExternalEmailRoles] = useState({}); // { [email]: 'PI' | 'COI' }
  const [piEmailInputValue, setPiEmailInputValue] = useState('');
  const [piRouteLoading, setPiRouteLoading] = useState(false);
  const [piPendingInvites, setPiPendingInvites] = useState([]);
  
  // Document thread access states
  const [threadAccessInfo, setThreadAccessInfo] = useState(null);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showVisibilityWarningModal, setShowVisibilityWarningModal] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [accessRequestSending, setAccessRequestSending] = useState(false);
  
  // Document filter state
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [availableDocTypes, setAvailableDocTypes] = useState([]);
  
  // Handover states
  const [pendingHandover, setPendingHandover] = useState(null);
  const [handoverStatusForOldCoord, setHandoverStatusForOldCoord] = useState(null);
  const [showHandoverBanner, setShowHandoverBanner] = useState(true);
  const [allCoordinators, setAllCoordinators] = useState([]);
  const [handoverData, setHandoverData] = useState({ new_coordinator_id: '', reason: '' });
  const [handoverLoading, setHandoverLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [handoverHistory, setHandoverHistory] = useState([]);
  const [isHandoverTimelineExpanded, setIsHandoverTimelineExpanded] = useState(false);
  
  // Additional loading states for action buttons
  const [submitLoading, setSubmitLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [assignReviewersLoading, setAssignReviewersLoading] = useState(false);
  const [manualRouteLoading, setManualRouteLoading] = useState(false);
  const [singleAssignPILoading, setSingleAssignPILoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [feasibilitySubmitting, setFeasibilitySubmitting] = useState(false);
  const [directorApproveLoading, setDirectorApproveLoading] = useState(false);
  const [directorRejectLoading, setDirectorRejectLoading] = useState(false);
  const [execApproveLoading, setExecApproveLoading] = useState(false);
  const [execRejectLoading, setExecRejectLoading] = useState(false);
  // Admin override
  const [showAdminOverrideModal, setShowAdminOverrideModal] = useState(false);
  const [adminOverrideStatus, setAdminOverrideStatus] = useState('');
  const [adminOverrideReason, setAdminOverrideReason] = useState('');
  const [adminOverrideLoading, setAdminOverrideLoading] = useState(false);
  const [validStatuses, setValidStatuses] = useState([]);
  const [requestDocAlerts, setRequestDocAlerts] = useState([]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [id, user?.id]);

  // Polling for handover status updates (every 10 seconds)
  useEffect(() => {
    if (!request || request.type !== 'CTA') return;
    
    const pollHandoverStatus = async () => {
      try {
        // For pending handover target
        const pendingRes = await requestsApi.getPendingHandover(id);
        setPendingHandover(pendingRes.data);
        
        // For old coordinator - get handover status
        if (isCoordinator()) {
          const statusRes = await requestsApi.getHandoverStatus(id);
          if (statusRes.data && !statusRes.data.old_coordinator_acknowledged) {
            setHandoverStatusForOldCoord(statusRes.data);
            setShowHandoverBanner(true);
          }
        }
      } catch (e) {
        // Ignore polling errors
      }
    };

    const interval = setInterval(pollHandoverStatus, 10000);
    return () => clearInterval(interval);
  }, [id, request?.type, isCoordinator]);

  // Signature rounds availability (CTA Phase 3) — drives the "Start New Signature Round" button
  useEffect(() => {
    let on = true;
    if (request?.type === 'CTA' && ['PENDING_SIGNATURES', 'READY_TO_EXECUTE', 'EXECUTED'].includes(request?.status)) {
      requestTypeSettingsApi.getSignatureRoundsConfig('CTA')
        .then(res => { if (on) setCtaRoundsConfig(res.data); })
        .catch(() => {});
    }
    return () => { on = false; };
  }, [request?.type, request?.status]);

  const fetchData = async () => {
    try {
      const [reqRes, docsRes, assignRes, reviewsRes, approvalsRes, logsRes, specsRes] = await Promise.all([
        requestsApi.getById(id),
        documentsApi.getAll(id),
        assignmentsApi.getAll(id),
        reviewsApi.getAll(id),
        approvalsApi.getAll(id),
        auditLogsApi.getAll(id),
        specialtiesApi.getAll()
      ]);
      
      setRequest(reqRes.data);
      setDocuments(docsRes.data);
      setAssignments(assignRes.data);
      setReviews(reviewsRes.data);
      setApprovals(approvalsRes.data);
      setAuditLogs(logsRes.data);
      setSpecialties(specsRes.data);

      // Fetch doc alerts for this request
      try {
        const alertsRes = await profileApi.getRequestDocAlerts(id);
        setRequestDocAlerts(alertsRes.data || []);
      } catch (e) {}

      // Fetch available document types for filter
      try {
        const docTypesRes = await documentsApi.getDocTypes(id);
        setAvailableDocTypes(docTypesRes.data.doc_types || []);
      } catch (e) {
        // Ignore
      }

      // Fetch the admin-configured upload doc type options for this request type
      try {
        const uploadTypesRes = await requestTypeSettingsApi.getUploadDocTypes(reqRes.data.type);
        if (uploadTypesRes.data.doc_types?.length) setUploadDocTypes(uploadTypesRes.data.doc_types);
      } catch (e) {
        // Ignore — fall back to defaults
      }

      // Fetch comments count
      try {
        const commentsRes = await commentsApi.getAll(id, {});
        setCommentsCount(commentsRes.data.length);
        // iter143 — capture every doc thread (top-level threads representing a document)
        // so the unassigned-thread banner can pinpoint them by doc-type.
        const docThreads = (commentsRes.data || []).filter(c => c.is_document_thread && !c.parent_id);
        setDocThreadIndex(docThreads);
      } catch (e) {
        // Ignore
      }

      // Fetch change requests
      try {
        const changeReqRes = await changeRequestsApi.getAll(id);
        setChangeRequests(changeReqRes.data);
      } catch (e) {
        // Ignore
      }

      // Fetch feasibility responses for FEA (visible to CRD, Coordinator, Company, Consultant)
      if (reqRes.data.type === 'FEA') {
        try {
          const feaRes = await feasibilityApi.getAll(id);
          setFeasibilityResponses(feaRes.data);
        } catch (e) {
          // User might not have access, ignore
          console.log('Feasibility responses not accessible for this user');
        }
      }

      // Fetch users for assignment
      if (isChairman() || isCoordinator() || isCRD() || isAdmin()) {
        const usersRes = await usersApi.getAll();
        setUsers(usersRes.data);
      }
      
      // Fetch pending handover for CTA requests
      if (reqRes.data.type === 'CTA') {
        try {
          const handoverRes = await requestsApi.getPendingHandover(id);
          setPendingHandover(handoverRes.data);
        } catch (e) {
          // Ignore
        }
        
        // Fetch handover history
        try {
          const historyRes = await requestsApi.getHandoverHistory(id);
          setHandoverHistory(historyRes.data || []);
        } catch (e) {
          // Ignore
        }
        
        // Fetch handover status for old coordinator
        if (isCoordinator()) {
          try {
            const statusRes = await requestsApi.getHandoverStatus(id);
            if (statusRes.data && !statusRes.data.old_coordinator_acknowledged) {
              setHandoverStatusForOldCoord(statusRes.data);
              setShowHandoverBanner(true);
            } else {
              setHandoverStatusForOldCoord(null);
            }
          } catch (e) {
            // Ignore
          }
        }
      }
      
      // Fetch signatures as soon as e-sig config exists (CTA: from PENDING_DIRECTOR_REVIEW;
      // FT: from FT_PENDING_FINANCE — right after Chairman approves & configures the bundle).
      // iter167 Issue 23 — single source of truth: see lib/signaturesTabStatuses.js.
      const sigFetchEligible = isSignaturesTabStatus(reqRes.data.type, reqRes.data.status);
      if (sigFetchEligible) {
        try {
          const sigStatusRes = await signaturesApi.getStatus(id);
          setSignatureStatus(sigStatusRes.data);
          setSignatures(sigStatusRes.data.signatures || []);
        } catch (e) {
          console.log('Signatures not accessible');
        }
      }

      // Phase A: fetch per-tab Role Visibility for the current user × request type
      try {
        const visRes = await api.get(`/me/role-visibility?type=${reqRes.data.type}`);
        setTabsConfig(visRes.data?.tabs || null);
      } catch (e) {
        setTabsConfig(null);
      }

      // iter126 — load pending invitations (consultant/investigator) tied to this request
      // so the Assignments tab can surface an "Awaiting registration" row for each.
      try {
        const invRes = await requestsApi.getPendingConsultantInvites(id);
        setPendingInvites(invRes.data || []);
      } catch (e) {
        setPendingInvites([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 403) {
        setAccessDenied({ status, detail });
      } else if (status === 404) {
        setAccessDenied({ status, detail: detail || 'Request not found' });
      } else {
        toast.error('Failed to load request details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    setSubmitLoading(true);
    try {
      await requestsApi.submit(id);
      toast.success('Request submitted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleResubmit = async () => {
    setSubmitLoading(true);
    try {
      await requestsApi.resubmit(id);
      toast.success('Request resubmitted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit request');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawLoading(true);
    try {
      await requestsApi.withdraw(id);
      toast.success('Request withdrawn');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw request');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleClose = async (status, body = null) => {
    setCloseLoading(true);
    try {
      await requestsApi.close(id, status, body);
      toast.success(`Request closed as ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close request');
      throw error;
    } finally {
      setCloseLoading(false);
    }
  };

  // ===== FEA Complete & Close modal state + handlers =====
  const [showCompleteCloseModal, setShowCompleteCloseModal] = useState(false);
  const [piCandidates, setPiCandidates] = useState([]);
  const [selectedPiIds, setSelectedPiIds] = useState([]);
  const [selectedCoiIds, setSelectedCoiIds] = useState([]);
  const [completeCloseSearch, setCompleteCloseSearch] = useState('');

  const openCompleteCloseModal = async () => {
    try {
      const res = await requestsApi.getEligibleFeaPiCandidates(id);
      setPiCandidates(res.data || []);
      setSelectedPiIds([]);
      setSelectedCoiIds([]);
      setCompleteCloseSearch('');
      setShowCompleteCloseModal(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load candidates');
    }
  };

  const togglePi = (uid) => {
    setSelectedPiIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
    setSelectedCoiIds(prev => prev.filter(x => x !== uid));
  };
  const toggleCoi = (uid) => {
    setSelectedCoiIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
    setSelectedPiIds(prev => prev.filter(x => x !== uid));
  };

  const handleCompleteClose = async () => {
    try {
      await handleClose('COMPLETED', {
        pi_user_ids: selectedPiIds,
        co_investigator_user_ids: selectedCoiIds,
      });
      setShowCompleteCloseModal(false);
    } catch (_) {/* toast already shown */}
  };

  // Director actions
  const handleDirectorApprove = async () => {
    setDirectorApproveLoading(true);
    try {
      await directorApi.approve(id);
      toast.success('Request approved. Sent to Executive Director for review.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setDirectorApproveLoading(false);
    }
  };

  const handleDirectorReject = async () => {
    if (!window.confirm('Are you sure you want to reject this request? This action cannot be undone.')) {
      return;
    }
    setDirectorRejectLoading(true);
    try {
      await directorApi.reject(id);
      toast.success('Request has been rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setDirectorRejectLoading(false);
    }
  };

  // Executive Director actions
  const handleExecDirectorApprove = async () => {
    setExecApproveLoading(true);
    try {
      await execDirectorApi.approve(id);
      toast.success('Request approved. Signature phase initiated.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setExecApproveLoading(false);
    }
  };

  const handleExecDirectorReject = async () => {
    if (!window.confirm('Are you sure you want to reject this request? This action cannot be undone.')) {
      return;
    }
    setExecRejectLoading(true);
    try {
      await execDirectorApi.reject(id);
      toast.success('Request has been rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setExecRejectLoading(false);
    }
  };

  // iter148d — Execute Request readiness modal. Before iter148d the action was
  // gated by a barebones window.confirm. Per user: "show a model for missed doc
  // thread approvals, missed request approvals, and pending actions to any
  // assigned users". The modal aggregates three buckets so the Chairman can see
  // exactly what's outstanding before locking the request.
  const [showExecuteConfirm, setShowExecuteConfirm] = useState(false);

  const handleExecuteRequest = () => {
    setShowExecuteConfirm(true);
  };

  const submitExecuteRequest = async () => {
    setShowExecuteConfirm(false);
    setExecuteLoading(true);
    try {
      await ctaFinalApprovalApi.executeRequest(id);
      toast.success('Request executed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute request');
    } finally {
      setExecuteLoading(false);
    }
  };

  // ========== ADMIN OVERRIDE ==========
  const handleOpenAdminOverride = async () => {
    try {
      const res = await adminApi.getValidStatuses(id);
      setValidStatuses(res.data.valid_statuses);
      setAdminOverrideStatus('');
      setAdminOverrideReason('');
      setShowAdminOverrideModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load status options');
    }
  };

  const handleAdminForceStatus = async () => {
    if (!adminOverrideStatus || !adminOverrideReason.trim()) {
      toast.error('Please select a status and provide a reason');
      return;
    }
    setAdminOverrideLoading(true);
    try {
      await adminApi.forceStatus(id, { new_status: adminOverrideStatus, reason: adminOverrideReason.trim() });
      toast.success(`Status changed to ${adminOverrideStatus}`);
      setShowAdminOverrideModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setAdminOverrideLoading(false);
    }
  };

  // Open Route to Consultants modal - now also fetches response fields
  const openRouteConsultantModal = async () => {
    try {
      const [consultantsRes, fieldsRes] = await Promise.all([
        requestsApi.getEligibleConsultants(id),
        requestsApi.getResponseFields(id)
      ]);
      setEligibleConsultants(consultantsRes.data);
      setResponseFields(fieldsRes.data?.response_fields || []);
      setSelectedConsultantId('');
      setRouteSelectedIds([]);
      setExternalEmails([]);
      setEmailInputValue('');
      // Load pending invitations (best-effort)
      try {
        const invRes = await requestsApi.getPendingConsultantInvites(id);
        setPendingInvites(invRes.data || []);
      } catch (_) {
        setPendingInvites([]);
      }
      setShowRouteConsultantModal(true);
      // iter166e — First-time pulse hint on "Customize Response Fields".
      // Only fires the first time the user opens this dialog. After that the
      // localStorage flag is set permanently and the hint goes silent.
      try {
        if (typeof window !== 'undefined' && !window.localStorage.getItem('cf:hint-customize-response-fields-seen')) {
          setResponseFieldsHintActive(true);
          window.localStorage.setItem('cf:hint-customize-response-fields-seen', '1');
          // Auto-dismiss after 8s so the pulse doesn't loop forever in the background.
          setTimeout(() => setResponseFieldsHintActive(false), 8000);
        }
      } catch (_) { /* localStorage unavailable — silently skip the hint */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load modal data');
    }
  };

  // Auto-route to all matching consultants
  const handleAutoRouteConsultants = async () => {
    setAutoRouteLoading(true);
    try {
      // Pass response fields if customized
      const data = responseFields.length > 0 ? { response_fields: responseFields } : {};
      const response = await requestsApi.routeToConsultants(id, data);
      const blocked = response.data?.blocked_consultants || [];
      const msg = response.data?.message || 'Routed to consultants';
      
      if (blocked.length > 0 && msg.includes('Routed to 0')) {
        // All blocked — show error
        toast.error(`All consultants blocked due to missing documents: ${blocked.map(b => `${b.name} (${b.missing.join(', ')})`).join('; ')}`, { duration: 8000 });
      } else if (blocked.length > 0) {
        // Some blocked — show warning
        toast.warning(msg, { duration: 6000 });
      } else {
        toast.success(msg);
      }
      setShowAutoRouteConfirm(false);
      setShowRouteConsultantModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to route to consultants');
    } finally {
      setAutoRouteLoading(false);
    }
  };

  // Route to single consultant manually
  const handleManualRouteConsultant = async () => {
    if (!selectedConsultantId) {
      toast.error('Please select a consultant');
      return;
    }
    setManualRouteLoading(true);
    try {
      const res = await requestsApi.routeSingleConsultant(id, selectedConsultantId);
      toast.success(res.data?.message || 'Consultant assigned');
      setSelectedConsultantId('');
      // Refresh eligible consultants list
      const refreshRes = await requestsApi.getEligibleConsultants(id);
      setEligibleConsultants(refreshRes.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to route to consultant');
    } finally {
      setManualRouteLoading(false);
    }
  };

  // Unified router: routes to selected consultants AND invites external emails in one call
  const fetchPendingInvites = async () => {
    try {
      const res = await requestsApi.getPendingConsultantInvites(id);
      setPendingInvites(res.data || []);
    } catch (e) {
      // non-fatal; older backends may not have this endpoint
      setPendingInvites([]);
    }
  };

  const handleUnifiedRoute = async () => {
    // Flush any unsubmitted email in the input box
    let emails = [...externalEmails];
    const tail = emailInputValue.trim().toLowerCase();
    if (tail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tail) && !emails.includes(tail)) {
      emails.push(tail);
    }
    if (routeSelectedIds.length === 0 && emails.length === 0) {
      toast.error('Pick at least one consultant or add an email to invite');
      return;
    }
    setUnifiedRouteLoading(true);
    try {
      const payload = {
        consultant_ids: routeSelectedIds,
        external_emails: emails,
      };
      if (responseFields && responseFields.length > 0) {
        payload.response_fields = responseFields;
      }
      // iter166c — When the "Grant view-only access" toggle is on (default
      // in FEA), promote the selected consultants to read-only viewers on
      // the doc tab. External emails can't be viewers yet (no user_id);
      // they'll only get viewer access once admin approves their account.
      if (routeViewerEnabled && routeSelectedIds.length > 0) {
        payload.viewer_user_ids = routeSelectedIds;
      }
      const response = await requestsApi.routeToConsultants(id, payload);
      const d = response.data || {};
      const blocked = d.blocked_consultants || [];
      const lines = [];
      if (d.routed_count) lines.push(`${d.routed_count} consultant(s) routed`);
      if ((d.invited_emails || []).length) lines.push(`${d.invited_emails.length} invited by email`);
      if ((d.matched_existing_emails || []).length) lines.push(`${d.matched_existing_emails.length} existing matched`);
      if ((d.invalid_emails || []).length) lines.push(`${d.invalid_emails.length} skipped`);
      const msg = lines.length ? lines.join(' · ') : (d.message || 'Done');
      if (blocked.length > 0 && d.routed_count === 0) {
        toast.error(`All consultants blocked: ${blocked.map(b => `${b.name} (${b.missing.join(', ')})`).join('; ')}`, { duration: 8000 });
      } else if (blocked.length > 0) {
        toast.warning(`${msg}. ${blocked.length} blocked due to missing documents.`);
      } else {
        toast.success(msg);
      }
      // Reset and refresh
      setRouteSelectedIds([]);
      setExternalEmails([]);
      setEmailInputValue('');
      const refreshRes = await requestsApi.getEligibleConsultants(id);
      setEligibleConsultants(refreshRes.data);
      await fetchPendingInvites();
      fetchData();
      // If everything's routed AND no remaining invites, close the modal
      const allRouted = (refreshRes.data || []).every(c => c.already_assigned);
      if (allRouted && (d.invited_emails || []).length === 0) {
        setShowRouteConsultantModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to route');
    } finally {
      setUnifiedRouteLoading(false);
    }
  };

  const [assigning, setAssigning] = useState(false);
  
  const handleAssign = async () => {
    if (assigning) return;
    setAssigning(true);
    try {
      await assignmentsApi.create(id, assignData);
      toast.success('Assignment created');
      setShowAssignModal(false);
      // CTA Chairman: surface the same doc-thread auto-assign popup as FT's FO flow
      const isCtaCoordAssign =
        request?.type === 'CTA' &&
        assignData.assignment_role === 'COORDINATOR' &&
        isChairman();
      if (isCtaCoordAssign) {
        const coord = users.find(u => u.id === assignData.assignee_user_id);
        setAssignedCoordName(coord?.name || 'The Coordinator');
        setShowCoordAutoAssign(true);
      }
      setAssignData({ assignee_user_id: '', assignment_role: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setAssigning(false);
    }
  };

  const handleCoordAutoAssign = async () => {
    setCoordAutoAssigning(true);
    try {
      await requestsApi.coordinatorThreadAutoassign(id);
      toast.success(`${assignedCoordName} auto-assigned to all document threads (existing & future)`);
      setShowCoordAutoAssign(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-assignment failed');
    } finally {
      setCoordAutoAssigning(false);
    }
  };

  // Fetch eligible PIs when opening the PI modal
  const [feaPiSuggestions, setFeaPiSuggestions] = useState({ pi_user_ids: [], co_investigator_user_ids: [], pi_sources: {}, co_investigator_sources: {} });
  const openAssignPIModal = async () => {
    try {
      const res = await assignmentsApi.getEligiblePIs(id);
      setEligiblePIs(res.data);
      // Best-effort: pull PI/Co-I suggestions from completed FEAs in the same study
      if (request?.study_id) {
        try {
          const sugRes = await requestsApi.getStudyFeaPiSuggestions(request.study_id);
          setFeaPiSuggestions(sugRes.data || { pi_user_ids: [], co_investigator_user_ids: [], pi_sources: {}, co_investigator_sources: {} });
        } catch (e) {
          setFeaPiSuggestions({ pi_user_ids: [], co_investigator_user_ids: [], pi_sources: {}, co_investigator_sources: {} });
        }
      }
      // Reset multi-select state
      setPiRouteSelectedIds([]);
      setPiRoleByUser({});
      setPiExternalEmails([]);
      setPiExternalEmailRoles({});
      setPiEmailInputValue('');
      // Fetch pending invites (same endpoint as FEA — works for both)
      try {
        const invRes = await requestsApi.getPendingConsultantInvites(id);
        setPiPendingInvites(invRes.data || []);
      } catch (e) {
        setPiPendingInvites([]);
      }
      setShowAssignPIModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load eligible investigators');
    }
  };

  // Unified assign-investigators-batch handler (mirrors FEA handleUnifiedRoute)
  const handleAssignInvestigatorsBatch = async () => {
    // Flush any unsubmitted email in the input box
    let emails = [...piExternalEmails];
    const tail = piEmailInputValue.trim().toLowerCase();
    if (tail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tail) && !emails.includes(tail)) {
      emails.push(tail);
    }
    if (piRouteSelectedIds.length === 0 && emails.length === 0) {
      toast.error('Pick at least one investigator or add an email to invite');
      return;
    }
    setPiRouteLoading(true);
    try {
      const pi_user_ids = piRouteSelectedIds.filter(uid => piRoleByUser[uid] === 'PI');
      const co_investigator_user_ids = piRouteSelectedIds.filter(uid => piRoleByUser[uid] === 'COI');
      const external_email_roles = {};
      emails.forEach(e => {
        const r = piExternalEmailRoles[e];
        if (r === 'PI') external_email_roles[e] = 'PI';
        else if (r === 'COI') external_email_roles[e] = 'CO_INVESTIGATOR';
      });
      const payload = {
        investigator_ids: piRouteSelectedIds,
        pi_user_ids,
        co_investigator_user_ids,
        external_emails: emails,
        external_email_roles,
      };
      // iter166c — When the "Grant view-only access" toggle is on (OFF by
      // default for CTA investigators), give the selected platform users
      // read-only doc-tab visibility on top of their regular assignment.
      if (piViewerEnabled && piRouteSelectedIds.length > 0) {
        payload.viewer_user_ids = piRouteSelectedIds;
      }
      const response = await assignmentsApi.assignInvestigatorsBatch(id, payload);
      const d = response.data || {};
      const blocked = d.blocked_investigators || [];
      const lines = [];
      if (d.assigned_count) lines.push(`${d.assigned_count} investigator(s) assigned`);
      if ((d.invited_emails || []).length) lines.push(`${d.invited_emails.length} invited by email`);
      if ((d.matched_existing_emails || []).length) lines.push(`${d.matched_existing_emails.length} existing matched`);
      if ((d.invalid_emails || []).length) lines.push(`${d.invalid_emails.length} skipped`);
      const msg = lines.length ? lines.join(' · ') : (d.message || 'Done');
      if (blocked.length > 0 && (d.assigned_count || 0) === 0) {
        toast.error(`All investigators blocked: ${blocked.map(b => `${b.name} (${(b.missing || []).join(', ')})`).join('; ')}`, { duration: 8000 });
      } else if (blocked.length > 0) {
        toast.warning(`${msg}. ${blocked.length} blocked due to missing documents.`);
      } else {
        toast.success(msg);
      }
      // Reset & refresh
      setPiRouteSelectedIds([]);
      setPiRoleByUser({});
      setPiExternalEmails([]);
      setPiExternalEmailRoles({});
      setPiEmailInputValue('');
      const refreshRes = await assignmentsApi.getEligiblePIs(id);
      setEligiblePIs(refreshRes.data);
      try {
        const invRes = await requestsApi.getPendingConsultantInvites(id);
        setPiPendingInvites(invRes.data || []);
      } catch (_) { /* ignore */ }
      fetchData();
      // Close modal if everyone's now assigned and there are no pending invites
      const allAssigned = (refreshRes.data || []).every(p => p.already_assigned);
      if (allAssigned && (d.invited_emails || []).length === 0) {
        setShowAssignPIModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign investigators');
    } finally {
      setPiRouteLoading(false);
    }
  };

  // Fetch all reviewers when opening the Reviewer modal
  const openAssignReviewerModal = async () => {
    try {
      const res = await usersApi.getAll({ role: 'REVIEWER' });
      // Filter to only active reviewers
      const reviewers = res.data.filter(u => u.role === 'REVIEWER' && u.is_active);
      
      // Get already assigned reviewers
      const assignedReviewerIds = assignments
        .filter(a => a.assignment_role === 'REVIEWER')
        .map(a => a.assignee_user_id);
      
      // Mark which are already assigned
      const reviewersWithStatus = reviewers.map(r => ({
        ...r,
        already_assigned: assignedReviewerIds.includes(r.id)
      }));
      
      setAllReviewers(reviewersWithStatus);
      setSelectedReviewerIds([]);
      setShowAssignReviewerModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load reviewers');
    }
  };

  // Bulk assign reviewers
  const handleBulkAssignReviewers = async () => {
    if (selectedReviewerIds.length === 0) {
      toast.error('Please select at least one reviewer');
      return;
    }
    setAssignReviewersLoading(true);
    try {
      // iter166c — Pass viewer_user_ids only when the toggle is ON (OFF by
      // default for reviewers). Selected reviewers gain read-only access to
      // every doc in the Documents tab in addition to their thread work.
      const viewerIds = reviewerViewerEnabled ? selectedReviewerIds : null;
      const res = await assignmentsApi.bulkAssignReviewers(id, selectedReviewerIds, viewerIds);
      toast.success(res.data.message);
      setShowAssignReviewerModal(false);
      setSelectedReviewerIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign reviewers');
    } finally {
      setAssignReviewersLoading(false);
    }
  };

  // Toggle reviewer selection
  const toggleReviewerSelection = (reviewerId) => {
    setSelectedReviewerIds(prev => {
      if (prev.includes(reviewerId)) {
        return prev.filter(id => id !== reviewerId);
      } else {
        return [...prev, reviewerId];
      }
    });
  };

  const handleSubmitReview = async () => {
    try {
      await reviewsApi.create(id, reviewData);
      toast.success('Review submitted');
      setShowReviewModal(false);
      setReviewData({ decision: '', comment: '', visible_to_company: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const handleRequestChanges = async () => {
    try {
      await changeRequestsApi.create(id, changeRequestData);
      toast.success('Change request sent');
      setShowChangeRequestModal(false);
      setChangeRequestData({ message: '', visible_to_company: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request changes');
    }
  };

  const handleSubmitFeasibility = async () => {
    setFeasibilitySubmitting(true);
    try {
      // Build the submission data from dynamic fields
      const submissionData = {
        can_participate: feasibilityData.can_participate || false,
        // Legacy fields for backward compatibility
        estimated_patient_pool: feasibilityData.estimated_patient_pool ? parseInt(feasibilityData.estimated_patient_pool) : null,
        resources_available: feasibilityData.resources_available || '',
        notes: feasibilityData.notes || '',
        willing_to_participate_as_pi: feasibilityData.willing_to_participate_as_pi || false,
        // Custom responses for dynamic fields
        custom_responses: {}
      };
      
      // Add all field values to custom_responses
      feasibilityResponseFields.forEach(field => {
        if (field.id !== 'can_participate') {
          submissionData.custom_responses[field.id] = feasibilityData[field.id] || null;
        }
      });
      
      await feasibilityApi.submit(id, submissionData);
      toast.success('Feasibility response submitted');
      setShowFeasibilityModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setFeasibilitySubmitting(false);
    }
  };

  // Check doc thread access when doc_type changes
  const checkDocThreadAccess = async (docType) => {
    if (!docType || docType === 'Other') {
      setThreadAccessInfo(null);
      return;
    }
    
    try {
      const res = await requestsApi.checkDocThreadAccess(id, docType);
      setThreadAccessInfo(res.data);
      
      // If thread exists and user is NOT in visibility, show blocked modal
      if (res.data.thread_exists && !res.data.can_upload) {
        setShowBlockedModal(true);
      }
    } catch (error) {
      console.error('Failed to check thread access:', error);
      setThreadAccessInfo(null);
    }
  };

  // Handle doc type selection with access check
  const handleDocTypeChange = async (value) => {
    setUploadData({ ...uploadData, doc_type: value, custom_doc_type: '' });
    await checkDocThreadAccess(value);
  };

  // Send access request and create auto-thread
  const handleSendAccessRequest = async (reason = 'upload', requestedUsers = []) => {
    setAccessRequestSending(true);
    try {
      const docType = uploadData.doc_type === 'Other' ? uploadData.custom_doc_type.trim() : uploadData.doc_type;
      const response = await requestsApi.createAccessRequest(id, {
        doc_type: docType,
        requested_users: requestedUsers,
        request_reason: reason
      });
      
      // Close all modals first
      setShowBlockedModal(false);
      setShowVisibilityWarningModal(false);
      setShowUploadModal(false);
      
      // Reset upload data
      setUploadData({ 
        file: null, 
        doc_type: '', 
        custom_doc_type: '', 
        visibility: 'COMPANY',
        visibility_groups: ['@internal'],
        visibility_users: [],
        is_attachment: false,
        parent_document_id: ''
      });
      setThreadAccessInfo(null);
      
      // Navigate to Discussion tab to show the new access request thread
      setActiveTab('discussion');
      
      // Show success message with guidance
      toast.success('Access request created! The privileged users have been notified.');
      
      // If we got a thread_id back, we could scroll to it after a short delay
      if (response.data?.thread_id) {
        setTimeout(() => {
          const threadElement = document.querySelector(`[data-comment-id="${response.data.thread_id}"]`);
          if (threadElement) {
            threadElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            threadElement.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
            setTimeout(() => {
              threadElement.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
            }, 3000);
          }
        }, 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send access request');
    } finally {
      setAccessRequestSending(false);
    }
  };

  // Check if selected visibility includes users outside thread
  const getNewUsersOutsideThread = () => {
    if (!threadAccessInfo || !threadAccessInfo.thread_exists) return [];
    
    const threadUserIds = threadAccessInfo.visible_to || [];
    const selectedUserIds = uploadData.visibility_users || [];
    
    // Find users selected but not in thread
    return selectedUserIds.filter(uid => !threadUserIds.includes(uid));
  };

  const handleUpload = async () => {
    if (uploadingDoc) return; // guard against rapid double-clicks before state propagates
    if (!uploadData.file || !uploadData.doc_type) {
      toast.error('Please select a file and document type');
      return;
    }
    // If "Other" is selected, custom_doc_type is required
    if (uploadData.doc_type === 'Other' && !uploadData.custom_doc_type.trim()) {
      toast.error('Please enter a custom document type name');
      return;
    }
    // If attachment, parent document is required
    if (uploadData.is_attachment && !uploadData.parent_document_id) {
      toast.error('Please select which document this is an attachment to');
      return;
    }
    
    // Validate visibility - must have at least one group or specific user selected (except for Company Rep)
    if (!isCompanyRep() && uploadData.visibility_groups.length === 0 && uploadData.visibility_users.length === 0) {
      toast.error('Please select at least one visibility option (group or specific user)');
      return;
    }
    
    // Check if user is trying to add users outside thread visibility
    if (threadAccessInfo && threadAccessInfo.thread_exists && threadAccessInfo.user_in_visibility) {
      const newUsers = getNewUsersOutsideThread();
      
      if (newUsers.length > 0) {
        // Check if user is privileged
        if (!threadAccessInfo.is_privileged) {
          // Non-privileged user trying to add new users - block
          setShowVisibilityWarningModal(true);
          setPendingUpload({ newUsers, reason: 'visibility' });
          return;
        } else {
          // Privileged user - show warning for confirmation
          setShowVisibilityWarningModal(true);
          setPendingUpload({ newUsers, reason: 'expand', canProceed: true });
          return;
        }
      }
    }
    
    // Proceed with upload
    await executeUpload();
  };

  const executeUpload = async () => {
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      // Use custom name if "Other" is selected, otherwise use selected type
      const docType = uploadData.doc_type === 'Other' ? uploadData.custom_doc_type.trim() : uploadData.doc_type;
      formData.append('doc_type', docType);
      
      // Handle visibility based on groups/users selection
      if (isCompanyRep()) {
        formData.append('visibility', 'COMPANY');
      } else {
        // Determine visibility from selected groups
        const hasCompany = uploadData.visibility_groups.includes('@company');
        // Set to COMPANY visibility if @company is selected, otherwise INTERNAL
        formData.append('visibility', hasCompany ? 'COMPANY' : 'INTERNAL');
        formData.append('visibility_groups', JSON.stringify(uploadData.visibility_groups));
        formData.append('visibility_users', JSON.stringify(uploadData.visibility_users));
      }
      
      // Handle attachment linking
      if (uploadData.is_attachment && uploadData.parent_document_id) {
        formData.append('is_attachment', 'true');
        formData.append('parent_document_id', uploadData.parent_document_id);
      }
      
      await documentsApi.upload(id, formData);
      toast.success('Document uploaded');
      
      // Save custom doc type for this request if "Other" was selected
      if (uploadData.doc_type === 'Other' && uploadData.custom_doc_type.trim()) {
        const newType = uploadData.custom_doc_type.trim();
        if (!customDocTypes.includes(newType)) {
          setCustomDocTypes([...customDocTypes, newType]);
        }
      }
      
      setShowUploadModal(false);
      setShowVisibilityWarningModal(false);
      setPendingUpload(null);
      setThreadAccessInfo(null);
      setUploadData({ 
        file: null, 
        doc_type: '', 
        custom_doc_type: '', 
        visibility: 'COMPANY',
        visibility_groups: ['@internal'],
        visibility_users: [],
        is_attachment: false,
        parent_document_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Handle approval actions
  // Doc-thread gating state: if the user has open doc threads assigned to them,
  // block the green Approve button and open a modal listing them with quick links.
  const [docGateOpen, setDocGateOpen] = useState(false);
  // iter144 — track WHAT the user is trying to do when the doc-thread / skip-approvals
  // gates fire. Default 'cta-final' opens the e-signature modal after clearing the gates;
  // 'start-round' opens the Start New Round modal instead.
  const [skipApprovalsTarget, setSkipApprovalsTarget] = useState('cta-final');
  const [nudgingDocId, setNudgingDocId] = useState(null);
  // iter148c — Confirm dialog before firing handleDocGateApprove. Holds the target
  // thread (`t`) and the as_override flag so we know which button the user clicked
  // ("Approve" → asOverride=true | "Approve from my side" → asOverride=false).
  const [docGateConfirm, setDocGateConfirm] = useState(null);
  // iter132 (#3c) — pulse-glow ring + sticky hint on the main Approve button after the
  // user clears their doc-thread gate. Auto-clears after a few seconds.
  const [approveButtonPulse, setApproveButtonPulse] = useState(false);
  const [approveNextStepHint, setApproveNextStepHint] = useState(false);
  // iter127 — per-approver nudge button (skip dialog + future inline placements).
  const [nudgingApproverId, setNudgingApproverId] = useState(null);
  const handleNudgeApprover = async (p) => {
    setNudgingApproverId(p.assignment_id);
    try {
      const res = await requestsApi.nudgeApprover(id, p.assignment_id);
      const status = res.data?.email_status;
      // iter128 — reflect the ACTUAL delivery outcome in the toast instead of always
      // claiming success. send_notification_with_email returns email_status:
      //   sent | mock | failed | skipped_disabled | skipped_no_addr | skipped_no_vars
      if (status === 'sent') {
        toast.success(`Reminder email sent to ${p.user_name}`);
      } else if (status === 'mock') {
        toast.warning(`In-app reminder delivered to ${p.user_name}. Email is in MOCK mode (no SMTP).`);
      } else if (status === 'failed') {
        toast.error(`In-app reminder delivered to ${p.user_name}, but the email failed: ${res.data?.email_error || 'unknown error'}`);
      } else if (status === 'skipped_no_addr') {
        toast.warning(`In-app reminder delivered. ${p.user_name} has no email on file.`);
      } else if (status === 'skipped_disabled') {
        toast.warning(`In-app reminder delivered. Email is disabled for this user/template.`);
      } else {
        toast.success(`Reminder sent to ${p.user_name}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setNudgingApproverId(null);
    }
  };
  const [pendingDocThreads, setPendingDocThreads] = useState([]);
  const [docGateApproving, setDocGateApproving] = useState(null); // document_id being approved inline
  const [docGateApproved, setDocGateApproved] = useState([]); // document_ids approved from the gate dialog
  const [discussionRefreshKey, setDiscussionRefreshKey] = useState(0); // bump to force the Discussion tab to refetch
  const [highlightedThreadId, setHighlightedThreadId] = useState(null);

  // Approve a pending doc directly from the gate dialog — identical to manually
  // changing the doc-thread status to "Approved" in the Discussion tab.
  const handleDocGateApprove = async (t, asOverride = true) => {
    setDocGateApproving(t.document_id);
    try {
      // iter134 — Coordinator (and any blanket role) approves via the gate with override=true
      // by default → doc thread fully flips to APPROVED. The optional secondary button
      // "Approve from my side" sends asOverride=false → only this user's slot ticks; doc
      // stays Pending Review until the rest of the assignees approve too.
      await documentsApi.updateStatus(t.document_id, 'APPROVED', { as_override: asOverride });
      const approvedNow = [...docGateApproved, t.document_id];
      setDocGateApproved(approvedNow);
      const left = pendingDocThreads.filter(p => !approvedNow.includes(p.document_id));
      if (left.length === 0) {
        // iter132 (#3c) — guide the user back to the main green Approve button so they
        // don't get lost after clearing the gate. Three coordinated cues:
        //   ① toast with a clickable CTA that scrolls to + focuses the Approve button
        //   ② pulse-glow ring on the Approve button for ~6s
        //   ③ sticky "Next step ↑" inline hint under the button, auto-fades after 10s
        const focusApprove = () => {
          const btn = document.querySelector('[data-testid="approve-btn"]')
            || document.querySelector('[data-testid="ft-fo-approve-btn"]')
            || document.querySelector('[data-testid="ft-finance-approve-btn"]')
            || document.querySelector('[data-testid="ft-director-approve-btn"]')
            || document.querySelector('[data-testid="ft-execdir-approve-btn"]');
          if (btn) {
            btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Defer focus until smooth scroll lands so the visual jump isn't jarring.
            setTimeout(() => btn.focus({ preventScroll: true }), 350);
          }
        };
        toast.success(
          (tCtx) => (
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <div className="font-medium">All your thread reviews are done</div>
                <button
                  type="button"
                  className="text-emerald-700 underline underline-offset-2 text-xs mt-0.5"
                  onClick={() => { focusApprove(); toast.dismiss(tCtx); }}
                  data-testid="approve-cta-toast-link"
                >
                  Approve the request to proceed →
                </button>
              </div>
            </div>
          ),
          { duration: 8000 }
        );
        setDocGateOpen(false);
        setApproveButtonPulse(true);
        setApproveNextStepHint(true);
        // Snap to the button after a brief beat so the user sees the dialog close first.
        setTimeout(focusApprove, 250);
        setTimeout(() => setApproveButtonPulse(false), 10_000);
        setTimeout(() => setApproveNextStepHint(false), 10_000);
      } else {
        toast.success(`${t.document_name} approved`);
      }
      fetchData();
      setDiscussionRefreshKey(k => k + 1); // show the new status + green marker in the Discussion tab immediately
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve document');
    } finally {
      setDocGateApproving(null);
    }
  };

  // Returns true (and opens the gate dialog) when there are still unapproved doc threads.
  // `scope='mine'` (default) = threads where the current user is assigned;
  // `scope='all'` = every non-approved doc thread on the request (used by the Chairman
  // before opening e-sign configuration, so the gate fires regardless of who reviews each doc).
  const checkDocThreadGate = async (scope = 'mine') => {
    try {
      const res = await api.get(`/requests/${id}/my-pending-doc-threads`, { params: { scope } });
      const pending = res.data?.pending || [];
      if (pending.length > 0) {
        setPendingDocThreads(pending);
        setDocGateApproved([]);
        setDocGateOpen(true);
        return true;
      }
    } catch (e) {
      // iter129 — if the gate endpoint fails we don't block (better than freezing the
      // workflow) but we surface a warning so the user knows the safety net didn't run.
      // The next click will retry the gate.
      toast.warning('Could not verify document threads — please retry. If this persists, contact an admin.');
    }
    return false;
  };

  // CTA Chairman entry: same protection as FT — block opening the e-sign modal until
  // every doc thread on the request is APPROVED. Uses scope='all'.
  const handleCTAFinalApproveClick = async () => {
    if (await checkDocThreadGate('all')) return;
    setShowCTAFinalApprovalModal(true);
  };

  // One-click nudge from the gate dialog: emails + in-app pings the doc's assignee(s).
  // Throttled server-side per (doc, assignee) for 10 minutes — UI toasts the result.
  const handleNudgeAssignee = async (t) => {
    setNudgingDocId(t.document_id);
    try {
      const res = await api.post(`/requests/${id}/documents/${t.document_id}/nudge-assignees`);
      const { nudged = [], throttled = [] } = res.data || {};
      const parts = [];
      if (nudged.length) parts.push(`Nudged ${nudged.map(n => n.name).filter(Boolean).join(', ') || nudged.length}`);
      if (throttled.length) parts.push(`${throttled.length} already nudged in the last 10 min`);
      toast.success(parts.join(' · ') || 'Reminder sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the reminder');
    } finally {
      setNudgingDocId(null);
    }
  };

  // iter144 — Start New Signature Round behaves exactly like the main green Approve button:
  // it runs the same pre-flight gates (unapproved doc threads, pending request approvers)
  // and only opens the round modal once those are cleared / overridden by the Chairman.
  const openStartRoundWithGates = async () => {
    if (await checkDocThreadGate('all')) return;
    try {
      const res = await approvalsApi.getPendingApprovals(id);
      const pending = res.data?.pending || [];
      if (pending.length > 0) {
        setPendingApprovers(pending);
        setSkipApprovalsTarget('start-round');
        setShowSkipApprovalsDialog(true);
        return;
      }
    } catch (_e) { /* fall through to open the modal directly */ }
    setCtaEsignBundle(false);
    setShowStartRoundModal(true);
  };

  const handleApproval = async (action) => {
    // iter129 — intentionally uses default scope='mine' here: this path is the per-user
    // reviewer Approve (PI / Investigator / Reviewer / Coordinator own approval). A regular
    // reviewer should only be gated on threads they're assigned to, not request-wide.
    // The privileged "skip all + approve" flow uses scope='all' inside openSkipApprovalsDialog.
    if (action === 'APPROVE' && await checkDocThreadGate()) return; // BLOCK
    // iter125 — confirmation step for non-privileged users replaced with a Shadcn
    // dialog (better UX than window.confirm). Privileged staff bypass entirely.
    if (action === 'APPROVE' && !isPrivilegedAlways) {
      setShowApproveConfirm(true);
      return;
    }
    await submitApproval(action);
  };

  const submitApproval = async (action) => {
    setApproveLoading(true);
    try {
      await approvalsApi.submit(id, action);
      const actionLabels = {
        'APPROVE': 'Approval submitted',
        'MARK_FAILED': 'Marked for failure review',
        'FINAL_APPROVE': 'Request approved',
        'FINAL_FAIL': 'Request marked as failed'
      };
      toast.success(actionLabels[action] || 'Action completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit approval');
    } finally {
      setApproveLoading(false);
      setShowApproveConfirm(false);
    }
  };

  const navigateToDocThread = (thread) => {
    setDocGateOpen(false);
    setActiveTab('discussion');
    setHighlightedThreadId(thread.thread_id);
    // Set URL with red 'nudge' highlight — gate navigation is a "please review this NOW"
    // context so we use the same red signal as the inbox nudge notifications.
    setSearchParams({ tab: 'discussion', comment: thread.thread_id, highlight: 'nudge' });
    setTimeout(() => {
      const el = document.querySelector(`[data-thread-id="${thread.thread_id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
    setTimeout(() => setHighlightedThreadId(null), 6000);
  };

  // Admin-level pre-emptive Approve on a CTA: fetch outstanding approvers and open
  // the confirmation dialog. After confirmation we call skip-pending-approvals which
  // moves the request to PENDING_FINAL_APPROVAL, then we open the E-Sig modal.
  const formatAssignmentRoleLabel = (role) => {
    const map = {
      COORDINATOR: 'Coordinator',
      PI: 'Principal Investigator',
      INVESTIGATOR: 'Investigator',
      REVIEWER: 'Reviewer',
      FINANCIAL_OFFICER: 'Financial Officer',
    };
    return map[role] || role || 'Unknown';
  };

  const openSkipApprovalsDialog = async () => {
    try {
      // iter134 — gate scope restored to 'all'. Coordinators are now privileged: their
      // approve fully overrides the doc thread, so once they tick off the gate doesn't
      // re-fire (the doc flips to APPROVED in one shot). 'all' also correctly fires the
      // gate for Chairman who isn't a thread assignee — they must approve docs (which
      // they can do via override) before the skip-approvers dialog opens.
      if (await checkDocThreadGate('all')) return;
      const res = await approvalsApi.getPendingApprovals(id);
      // iter126 — when the active Coordinator triggers this flow, exclude their own row
      // from the "will be skipped" list because the backend records their APPROVE instead.
      const allPending = res.data?.pending || [];
      const coordinatorFlow = canApproveAsCoordinator && !isAdminLevel;
      const filtered = coordinatorFlow
        ? allPending.filter((p) => p.user_id !== user?.id)
        : allPending;
      setPendingApprovers(filtered);
      setShowSkipApprovalsDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load pending approvers');
    }
  };

  const handleConfirmSkipApprovals = async () => {
    setSkippingApprovals(true);
    try {
      const res = await approvalsApi.skipPendingApprovals(id);
      const n = res.data?.skipped_count ?? 0;
      const coordinatorFlow = canApproveAsCoordinator && !isAdminLevel;
      if (coordinatorFlow) {
        toast.success(
          n > 0
            ? `Approval submitted. ${n} pending reviewer${n === 1 ? '' : 's'} skipped — request sent to Chairman for final approval.`
            : 'Approval submitted — request sent to Chairman for final approval.'
        );
      } else {
        toast.success(
          n > 0
            ? `Skipped ${n} pending approval${n === 1 ? '' : 's'}. Configure e-signature to proceed.`
            : 'Proceeding to Final Approval & E-Signature Configuration.'
        );
      }
      setShowSkipApprovalsDialog(false);
      await fetchData();
      // iter144 — branch by target: 'start-round' jumps into the new-round picker;
      // otherwise the existing 'cta-final' path opens the E-Signature modal.
      if (skipApprovalsTarget === 'start-round') {
        // iter167 Issue 27 — FT routes via FtWorkflowActions' local modal
        // (stashed in ftStartRoundOpenerRef during the gate trigger); CTA
        // uses the parent's setShowStartRoundModal.
        if (request?.type === 'FT' && typeof ftStartRoundOpenerRef.current === 'function') {
          const opener = ftStartRoundOpenerRef.current;
          ftStartRoundOpenerRef.current = null;
          opener();
        } else {
          setCtaEsignBundle(false);
          setShowStartRoundModal(true);
        }
      } else if (!coordinatorFlow) {
        // Only Chairman/Admin/CRD can configure e-signature — open the modal for them only.
        setShowCTAFinalApprovalModal(true);
      }
      setSkipApprovalsTarget('cta-final');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip pending approvals');
    } finally {
      setSkippingApprovals(false);
    }
  };

  // Handle undo approval
  const handleUndoApproval = async (approvalId) => {
    try {
      await approvalsApi.undo(id, approvalId);
      toast.success('Approval undone');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo approval');
    }
  };

  // Check if current user has already approved.
  // FT is exempt from the post-approval doc-thread lock: FT approvals are workflow-stage
  // records and assignees (e.g. the Financial Officer) keep working on doc threads after
  // their stage approval.
  const userApproval = approvals.find(a => a.user_id === user?.id);
  // iter123 — privileged staff are NEVER locked by their own approval, so the gate
  // only applies to non-privileged users (PI / Coord / Investigator / Reviewer / etc).
  const isPrivilegedAlways = isAdmin() || isCRD() || isChairman() || isDirector() || isExecDirector();
  // iter148b — Smarter post-approval lock: the user is "locked by own approval"
  // ONLY when they have NO open doc-thread work left. As soon as they are
  // re-assigned to a not-yet-APPROVED doc (or their Coordinator/Chairman undoes
  // their approval), `hasOpenDocThreadTasks` flips to true (or `userApproval`
  // disappears) and the lock releases — green Approve button reappears, banner
  // hides, upload/reply/status-change/assign-to all unlock.
  //
  // (Previous iter147b unconditionally unlocked doc-thread assignees which meant
  // the button NEVER hid even right after "Approve & lock". User flagged the
  // regression — lock must engage on confirm and only release on the new task.)
  const isCurrentDocThreadAssignee = (docThreadIndex || []).some(t =>
    (t.document_status_assignees || []).includes(user?.id)
  );
  const hasOpenDocThreadTasks = (documents || []).some(d =>
    !d.is_ft_bundle && d.status !== 'APPROVED' && (d.status_assignees || []).includes(user?.id)
  );
  const hasUserApproved = !!userApproval && request?.type !== 'FT' && !isPrivilegedAlways && !hasOpenDocThreadTasks;

  // iter148h — Active request-review gate. The "Sign Now" header button hides while ANY
  // request-level review stage is still in flight (Director / Exec Director / Custom /
  // Finance) so signatories don't sign until reviewers clear it. Driven by the
  // request's status — these are the wait-states the workflow can stall on.
  // iter167 Issue 26 — Extended to cover the FT-specific cascade
  // (`FT_PENDING_DIRECTOR` and `FT_PENDING_EXEC_DIR_REVIEW`) so FT
  // requests gate signing identically to CTA when a Request Review is
  // open. `PENDING_CUSTOM_REVIEW` already covers chairman-added Review
  // Step blocking on either request type.
  const REVIEW_STAGE_STATUSES = [
    'PENDING_DIRECTOR_REVIEW',
    'PENDING_EXEC_DIRECTOR_REVIEW',
    'PENDING_CUSTOM_REVIEW',
    'FT_PENDING_FINANCE',
    'FT_PENDING_DIRECTOR',
    'FT_PENDING_EXEC_DIR_REVIEW',
  ];
  const hasActiveReviewStage = REVIEW_STAGE_STATUSES.includes(request?.status);

  const handleDownloadDocument = async (doc) => {
    setDownloadingDoc(doc.id);
    try {
      // Get signed download URL
      const response = await documentsApi.getDownloadUrl(doc.id);
      const { download_url } = response.data;
      
      // Create hidden link and trigger download
      const link = document.createElement('a');
      link.href = process.env.REACT_APP_BACKEND_URL + download_url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloading ${doc.filename}`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const openEditSpecialtyModal = () => {
    setEditSpecialtyData({
      specialty_ids: request?.specialty_ids || [],
      reason: ''
    });
    setShowEditSpecialtyModal(true);
  };

  const openEditCompanyModal = async () => {
    setEditCompanyData({ company_account_id: request?.company_account_id || '', reason: '' });
    if (companyOptions.length === 0) {
      try {
        const res = await api.get('/company-accounts');
        setCompanyOptions(res.data || []);
      } catch (e) {
        console.error('Failed to load companies:', e);
      }
    }
    setShowEditCompanyModal(true);
  };

  const handleUpdateCompany = async () => {
    if (!editCompanyData.company_account_id) {
      toast.error('Please select a company');
      return;
    }
    if (!editCompanyData.reason || editCompanyData.reason.trim() === '') {
      toast.error('Please provide a reason for this change');
      return;
    }
    setSavingCompany(true);
    try {
      await requestsApi.updateCompany(id, {
        company_account_id: editCompanyData.company_account_id,
        reason: editCompanyData.reason.trim(),
      });
      toast.success('Company assigned');
      setShowEditCompanyModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSavingCompany(false);
    }
  };

  const toggleSpecialtyInEdit = (specialtyId) => {
    setEditSpecialtyData(prev => {
      const ids = prev.specialty_ids || [];
      if (ids.includes(specialtyId)) {
        return { ...prev, specialty_ids: ids.filter(id => id !== specialtyId) };
      } else {
        return { ...prev, specialty_ids: [...ids, specialtyId] };
      }
    });
  };

  const handleUpdateSpecialties = async () => {
    if (!editSpecialtyData.specialty_ids || editSpecialtyData.specialty_ids.length === 0) {
      toast.error('Please select at least one specialty');
      return;
    }
    if (!editSpecialtyData.reason || editSpecialtyData.reason.trim() === '') {
      toast.error('Please provide a reason for this change');
      return;
    }
    try {
      await requestsApi.updateSpecialties(id, editSpecialtyData);
      toast.success('Specialties updated successfully');
      setShowEditSpecialtyModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update specialties');
    }
  };

  // Handover functions
  const openHandoverModal = async () => {
    try {
      const res = await coordinatorsApi.getAll();
      // Filter out current user if they are a coordinator
      const currentCoordAssignment = assignments.find(a => a.assignment_role === 'COORDINATOR' && a.status !== 'HANDED_OVER');
      const filtered = res.data.filter(c => c.id !== currentCoordAssignment?.assignee_user_id);
      setAllCoordinators(filtered);
      setHandoverData({ new_coordinator_id: '', reason: '' });
      setShowHandoverModal(true);
    } catch (error) {
      toast.error('Failed to load coordinators');
    }
  };

  const handleCreateHandover = async () => {
    if (!handoverData.new_coordinator_id) {
      toast.error('Please select a coordinator');
      return;
    }
    if (!handoverData.reason || handoverData.reason.trim() === '') {
      toast.error('Please provide a reason for the handover');
      return;
    }
    setHandoverLoading(true);
    try {
      const res = await requestsApi.createHandover(id, handoverData);
      toast.success(res.data.message);
      setShowHandoverModal(false);
      setHandoverData({ new_coordinator_id: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create handover');
    } finally {
      setHandoverLoading(false);
    }
  };

  const handleAcceptHandover = async () => {
    if (!pendingHandover) return;
    setHandoverLoading(true);
    try {
      await handoversApi.accept(pendingHandover.id);
      toast.success('Handover accepted. You are now the coordinator.');
      setPendingHandover(null); // Clear immediately
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept handover');
    } finally {
      setHandoverLoading(false);
    }
  };

  const handleRejectHandover = async () => {
    if (!pendingHandover) return;
    setHandoverLoading(true);
    try {
      await handoversApi.reject(pendingHandover.id, rejectReason);
      toast.success('Handover rejected. The request has been escalated to the Chairman.');
      setShowRejectHandoverModal(false);
      setRejectReason('');
      setPendingHandover(null); // Clear immediately
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject handover');
    } finally {
      setHandoverLoading(false);
    }
  };

  // Close handover status banner (for old coordinator)
  // iter143 — sessionStorage-backed dismiss for the unassigned-thread banner.
  useEffect(() => {
    if (!id) return;
    try {
      const k = `unassigned-doc-thread-banner-dismissed:${id}`;
      if (sessionStorage.getItem(k) === '1') setUnassignedBannerDismissed(true);
    } catch (_e) { /* sessionStorage unavailable */ }
  }, [id]);

  const dismissUnassignedBanner = () => {
    setUnassignedBannerDismissed(true);
    try { sessionStorage.setItem(`unassigned-doc-thread-banner-dismissed:${id}`, '1'); } catch (_e) { /* sessionStorage unavailable */ }
  };

  const coordinatorUserId = (assignments || []).find(a => a.assignment_role === 'COORDINATOR' && a.status !== 'HANDED_OVER')?.assignee_user_id;
  const unassignedDocThreads = (docThreadIndex || []).filter(t => {
    const assignees = t.document_status_assignees || [];
    // Only consider PENDING / CHANGES_REQUESTED docs — fully approved ones don't need
    // reassignment.
    if (t.document_status === 'APPROVED' || t.document_status === 'COMPLETED') return false;
    // iter148c — banner surfaces when there's no NON-COORDINATOR reviewer on the
    // doc thread. Coordinator-only assignments still leave the thread "owned" by
    // the Coordinator until they bring in a real reviewer, so the chip stays
    // until any non-Coordinator gets added.
    const nonCoordinatorAssignees = assignees.filter(uid => uid !== coordinatorUserId);
    return nonCoordinatorAssignees.length === 0;
  });
  const showUnassignedBanner = !unassignedBannerDismissed
    && unassignedDocThreads.length > 0
    && (isChairman() || isCoordinator())
    // iter163c — On Financial Transaction requests, the originating Coordinator
    // is also the one expected to wire up reviewers themselves, so this nag
    // banner is noise for the FT creator. Hide it for the Coordinator who
    // created the FT request (Chairman + other Coordinators still see it).
    && !(request?.type === 'FT' && isCoordinator() && request?.created_by === user?.id);

  const scrollToDocThread = (threadId) => {
    // Switch to Discussion tab, then scroll + flash highlight on the target thread card.
    setActiveTab('discussion');
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="thread-${threadId}"], [data-thread-id="${threadId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-amber-400', 'transition-all');
        setTimeout(() => el.classList.remove('ring-4', 'ring-amber-400'), 2200);
      }
    }, 250);
  };

  const handleCloseHandoverBanner = () => {
    setShowHandoverBanner(false);
  };

  // Check if current user is the target of a pending handover
  const isPendingHandoverTarget = pendingHandover && pendingHandover.to_coordinator_id === user?.id;
  
  // Check if current user is the CURRENT active coordinator (has non-HANDED_OVER assignment)
  const currentCoordAssignment = assignments.find(a => a.assignment_role === 'COORDINATOR' && a.status !== 'HANDED_OVER');
  const isCurrentCoordinator = currentCoordAssignment?.assignee_user_id === user?.id;

  // Check if the old coordinator's handover was accepted (they should no longer have actions)
  // BUT only if they are NOT the current active coordinator (they may have been reassigned back)
  const wasHandoverAccepted = handoverStatusForOldCoord?.status === 'ACCEPTED' && 
    handoverStatusForOldCoord?.from_coordinator_id === user?.id &&
    !isCurrentCoordinator; // If they're the current coordinator again, they should have actions

  // Should show handover banner:
  // - PENDING: Always show to the old coordinator (they initiated it, need to see status)
  // - REJECTED: Always show to the old coordinator (they need to know it was rejected)
  // - ACCEPTED: Only show if they're NOT the current coordinator (hide if reassigned back)
  const shouldShowHandoverBanner = showHandoverBanner && 
    handoverStatusForOldCoord && 
    handoverStatusForOldCoord.from_coordinator_id === user?.id &&
    (handoverStatusForOldCoord.status === 'PENDING' || 
     handoverStatusForOldCoord.status === 'REJECTED' || 
     !isCurrentCoordinator);

  if (loading) {
    return (
      <DashboardLayout title="Request Details">
        <div className="flex items-center justify-center h-64">
          <BrandLoader size="md" label="Loading request details…" />
        </div>
      </DashboardLayout>
    );
  }

  if (accessDenied) {
    const isFinance = user?.role === 'FINANCE';
    const isPrivilegeMsg = (accessDenied.detail || '').toLowerCase().includes('finance');
    return (
      <DashboardLayout title="Access Restricted">
        <div className="max-w-2xl mx-auto py-12" data-testid="request-access-denied">
          <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/50 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {accessDenied.status === 404 ? 'Request Not Found' : 'Not Privileged for This Request'}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
              {isFinance || isPrivilegeMsg ? (
                <>
                  You are not privileged to view this request type.
                  <br />
                  Please contact an Administrator to request visibility for this request.
                </>
              ) : (
                accessDenied.detail || 'You do not have access to this request.'
              )}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => navigate('/requests')} data-testid="access-denied-back-btn">
                Back to Requests
              </Button>
              {(isFinance || isPrivilegeMsg) && (
                <Button
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => window.location.href = 'mailto:admin@clinicalflow.com?subject=Request%20Access%20Privilege'}
                  data-testid="access-denied-contact-admin-btn"
                >
                  Contact Administrator
                </Button>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!request) {
    return (
      <DashboardLayout title="Request Details">
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold text-slate-700">Request not found</h2>
          <Button onClick={() => navigate('/requests')} className="mt-4">
            Back to Requests
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const canSubmit = (isCompanyRep() && request.status === 'DRAFT') ||
    (isCoordinator() && request.type === 'FT' && request.status === 'DRAFT' && request.created_by === user?.id);
  const canResubmit = isCompanyRep() && request.status === 'CHANGES_REQUESTED';
  // Company can only withdraw requests in DRAFT status (before submission)
  const canWithdraw = isCompanyRep() && request.status === 'DRAFT';
  // iter160 — Admin / CRD share the Chairman's Assign-Coordinator capability.
  const canAssignCoordinator = (isChairman() || isAdmin() || isCRD()) && request.type !== 'FT' && request.status === 'AWAITING_CHAIRMAN_ASSIGNMENT';
  // Signatures tab visibility — CTA from PENDING_DIRECTOR_REVIEW onwards; FT from Chairman e-sign config onwards.
  // iter167 Issue 23 — single source of truth: see lib/signaturesTabStatuses.js.
  const showSignaturesTab = isSignaturesTabStatus(request.type, request.status);
  
  // Coordinator can only take actions if:
  // 1. They have the COORDINATOR role
  // 2. They ARE the current active coordinator for THIS request (isCurrentCoordinator)
  // 3. They're not a pending handover target (hasn't accepted yet)
  // 4. Their previous handover wasn't accepted (they're no longer active)
  const canTakeCoordinatorActions = isCoordinator() && isCurrentCoordinator && !isPendingHandoverTarget && !wasHandoverAccepted;
  
  // Coordinator can assign Investigators (PI/Co-I) and Reviewers when request is in appropriate status
  // iter148d — Coordinator gets the Assign Investigators / Assign to Reviewer(s) buttons
  // at ALL active stages until the request is fully executed/failed/withdrawn (same as
  // the privileged admin-level routes get). Per user: "Assign Investigators / Assign to
  // Reviewer(s) — add these 2 buttons to the coordinator in the same way as they were
  // added to the chairman".
  const coordinatorAssignAllowedStatuses = !['EXECUTED', 'FAILED', 'WITHDRAWN', 'REJECTED', 'DRAFT', 'SUBMITTED', 'APPROVED'].includes(request.status);
  const canAssignPIReviewer = canTakeCoordinatorActions && request.type === 'CTA' && coordinatorAssignAllowedStatuses;
  
  // Admin-level users (Chairman, Admin, CRD) have full access
  const isAdminLevel = isChairman() || isAdmin() || isCRD();
  
  // Approvals for CTA - PI/Reviewer can approve when assigned and request is in any
  // post-assignment "awaiting approval" state. INVESTIGATORS_ASSIGNED was previously
  // excluded which forced a Reviewer assignment before the PI could approve.
  // iter147 — Coordinator+doc-thread-assignee dual role would render TWO Approve buttons.
  // Compute the Coordinator flag first, then suppress the PI/Reviewer flavour when active.
  // (isCurrentDocThreadAssignee is computed above for use in hasUserApproved.)
  const isDocThreadAssignee = isCurrentDocThreadAssignee;
  const canApproveAsCoordinator = canTakeCoordinatorActions && request.type === 'CTA' &&
    !['APPROVED', 'FAILED', 'WITHDRAWN', 'DRAFT', 'SUBMITTED', 'PENDING_FINAL_APPROVAL', 'PENDING_DIRECTOR_REVIEW', 'PENDING_EXEC_DIRECTOR_REVIEW', 'PENDING_SIGNATURES', 'EXECUTED', 'REJECTED'].includes(request.status);
  // iter147 — doc-thread assignees can approve at ANY active workflow stage (regardless
  // of whether the request has moved into PENDING_FINAL_APPROVAL or PENDING_SIGNATURES).
  // Per user: "anyone assigned to doc thread regardless of the stage should have the
  // main green approval button even if he clicked on the main green approval button
  // before". Pure PI/Reviewer (not currently a doc-thread assignee) still gated to the
  // INVESTIGATORS_ASSIGNED / UNDER_REVIEW / RESUBMITTED window.
  const piReviewerStatuses = ['INVESTIGATORS_ASSIGNED', 'UNDER_REVIEW', 'RESUBMITTED'];
  const docThreadApprovableStatuses = [
    'INVESTIGATORS_ASSIGNED', 'UNDER_REVIEW', 'RESUBMITTED',
    'PENDING_FINAL_APPROVAL', 'PENDING_DIRECTOR_REVIEW', 'PENDING_EXEC_DIRECTOR_REVIEW',
    'PENDING_CUSTOM_REVIEW', 'PENDING_SIGNATURES', 'READY_TO_EXECUTE'
  ];
  const canApproveAsPIReviewer = !canApproveAsCoordinator && request.type === 'CTA' && (
    ((isReviewer() || isPI()) && piReviewerStatuses.includes(request.status)) ||
    (isDocThreadAssignee && docThreadApprovableStatuses.includes(request.status))
  );
  
  // Admin-level can give final approval
  const canGiveFinalApproval = isAdminLevel && request.type === 'CTA' && 
    request.status === 'PENDING_FINAL_APPROVAL';
  
  // Admin-level can also directly approve/fail without waiting (but not after Chairman final approval).
  // Once the request is in PENDING_DIRECTOR_REVIEW / PENDING_EXEC_DIRECTOR_REVIEW / PENDING_SIGNATURES /
  // READY_TO_EXECUTE / EXECUTED, no further Approve action is permitted.
  // iter143 — for CTA, the green "Approve" button is REMOVED at AWAITING_CHAIRMAN_ASSIGNMENT
  // and PENDING_FINAL_APPROVAL. Chairman/Admin/CRD must use Assign Coordinator → then the
  // Coordinator drives the request through approvals → Chairman uses Final Approve at the
  // proper stage. This prevents accidental short-circuiting of the workflow.
  const canDirectApproval = isAdminLevel && request.type === 'CTA' &&
    !['APPROVED', 'FAILED', 'WITHDRAWN', 'DRAFT', 'SUBMITTED', 'AWAITING_CHAIRMAN_ASSIGNMENT', 'PENDING_FINAL_APPROVAL', 'PENDING_DIRECTOR_REVIEW', 'PENDING_EXEC_DIRECTOR_REVIEW', 'PENDING_SIGNATURES', 'READY_TO_EXECUTE', 'EXECUTED', 'REJECTED'].includes(request.status);
  
  // For FEA Complete & Close: require IN_PROGRESS (or legacy ROUTED_TO_CONSULTANTS) + at least one response
  // Chairman has parity with CRD for FEA management.
  const canCompleteCloseFEA = (isCRD() || isChairman()) && request.type === 'FEA' &&
    ['ROUTED_TO_CONSULTANTS', 'IN_PROGRESS'].includes(request.status) &&
    feasibilityResponses.length > 0;
  // For FEA Mark Failed: CRD/Chairman can mark failed anytime after submission (doesn't need routing or responses)
  const canMarkFailedFEA = (isCRD() || isChairman()) && request.type === 'FEA' && 
    !['SIGNED', 'COMPLETED', 'FAILED', 'WITHDRAWN', 'DRAFT'].includes(request.status);
  // Route to Consultants is now persistent: visible while FEA is active so CRD/Chairman can route at any time
  // Also visible to the delegated FEA Coordinator (Q5a — they can assign/unassign consultants).
  const isFeaDelegatedCoordinator = isCoordinator() && request.type === 'FEA' && request.fea_coordinator_user_id === user?.id;
  const canRouteToConsultants = (isCRD() || isChairman() || isFeaDelegatedCoordinator) && request.type === 'FEA' &&
    ['SUBMITTED', 'ROUTED_TO_CONSULTANTS', 'IN_PROGRESS'].includes(request.status);
  // Investigators (including legacy Consultants) can respond to FEA feasibility
  const openFeasibilityResponseForm = async () => {
    setFeasibilityFieldsLoading(true);
    try {
      const res = await requestsApi.getResponseFields(id);
      const fields = res.data?.response_fields || [];
      setFeasibilityResponseFields(fields);
      const existing = myExistingFeaResponse;
      const initialData = existing
        ? {
            can_participate: existing.can_participate ?? true,
            willing_to_participate_as_pi: existing.willing_to_participate_as_pi ?? false,
            estimated_patient_pool: existing.estimated_patient_pool ?? '',
            resources_available: existing.resources_available ?? '',
            notes: existing.notes ?? '',
            ...(existing.custom_responses || {}),
          }
        : { can_participate: true };
      if (!existing) {
        fields.forEach(field => {
          if (field.id !== 'can_participate') {
            if (field.type === 'boolean') initialData[field.id] = false;
            else initialData[field.id] = '';
          }
        });
      }
      setFeasibilityData(initialData);
      setShowFeasibilityModal(true);
    } catch (error) {
      toast.error('Failed to load response fields');
    } finally {
      setFeasibilityFieldsLoading(false);
    }
  };


  // until the request is closed (COMPLETED / SIGNED / FAILED / WITHDRAWN).
  const canRespondFeasibility = (isConsultant() || isInvestigator()) && request.type === 'FEA' &&
    !['COMPLETED', 'SIGNED', 'FAILED', 'WITHDRAWN'].includes(request.status);
  const myExistingFeaResponse = (feasibilityResponses || []).find(r => r.consultant_user_id === user?.id);
  const isEditingFeaResponse = !!myExistingFeaResponse;
  
  // EXECUTED status check - no modifications allowed
  const isExecuted = request.status === 'EXECUTED';
  const canUploadDocs = !isExecuted && !hasUserApproved && (isCompanyRep() || (isCoordinator() && !isPendingHandoverTarget && !wasHandoverAccepted) || isCRD() || isPI() || isReviewer() || isAdmin() || isChairman() || isDirector() || isExecDirector());
  // Specialty edit lock-down for FEA: once consultants have been routed/invited, only ADMIN can override
  const feaConsultantRouted = request.type === 'FEA' && (
    (request.assignments || []).some(a => ['CONSULTANT', 'INVESTIGATOR'].includes(a.assignment_role)) ||
    (request.status && !['DRAFT', 'SUBMITTED'].includes(request.status))
  );
  const feaSpecialtyLocked = request.type === 'FEA' && feaConsultantRouted && !isAdmin();
  const canEditSpecialties = (isCRD() || isChairman() || (isCoordinator() && !isPendingHandoverTarget && !wasHandoverAccepted) || isAdmin()) &&
    !['APPROVED', 'FAILED', 'WITHDRAWN', 'EXECUTED', 'COMPLETED', 'SIGNED'].includes(request.status) &&
    !feaSpecialtyLocked;
  // Company inline edit — FT only, admin-level users, ONLY while the company is still
  // assignable (coordinator-created study with no company yet — backend computes this)
  const canEditCompany = request.type === 'FT' &&
    request.company_editable === true &&
    (isAdmin() || isCRD() || isChairman() || isDirector() || isExecDirector()) &&
    !['EXECUTED', 'WITHDRAWN', 'FAILED'].includes(request.status);
  // Show responses tab for FEA - visible to CRD/Chairman, Coordinator, Company, Investigator (Consultant)
  const canViewResponses = request.type === 'FEA' && (isCRD() || isChairman() || isCoordinator() || isCompanyRep() || isConsultant() || isInvestigator() || isAdmin());
  // Handover permission - for CTA only - not allowed after EXECUTED
  // Chairman can only initiate handover if a coordinator is already assigned
  // New coordinator who hasn't accepted cannot initiate handover
  // Old coordinator whose handover was accepted cannot initiate handover
  const hasCoordinatorAssigned = !!currentCoordAssignment;
  const canInitiateHandover = request.type === 'CTA' && 
    !['APPROVED', 'FAILED', 'WITHDRAWN', 'EXECUTED'].includes(request.status) && 
    ((isChairman() && hasCoordinatorAssigned) || (isCurrentCoordinator && !isPendingHandoverTarget && !wasHandoverAccepted)) &&
    !pendingHandover;
  
  // Can add comments/discussions - not allowed after EXECUTED
  const canAddDiscussion = !isExecuted;

  return (
    <DashboardLayout title="Request Details">
      <div className="space-y-6" data-testid="request-details">
        {/* Header */}
        <div className="space-y-4">
          <div className="min-w-0">
            {/* Breadcrumb */}
            {request.study_id ? (
              <div className="flex items-center gap-2 mb-4 text-sm min-w-0">
                <button onClick={() => navigate('/studies')} className="text-slate-400 hover:text-slate-600 shrink-0">Studies</button>
                <span className="text-slate-300 shrink-0">/</span>
                <button
                  onClick={() => navigate(`/studies/${request.study_id}`)}
                  className="text-blue-600 hover:text-blue-800 font-medium truncate max-w-[16rem] sm:max-w-md"
                  title={request.study_title || 'Study'}
                >
                  {request.study_title || 'Study'}
                </button>
                <span className="text-slate-300 shrink-0">/</span>
                <span className="text-slate-500 shrink-0 font-mono text-xs sm:text-sm">{request.ref_no}</span>
              </div>
            ) : (
              <Button variant="ghost" onClick={() => navigate('/requests')} className="mb-4">
                <ArrowLeft size={16} className="mr-2" /> Back to Requests
              </Button>
            )}
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <Badge className={getTypeColor(request.type)}>{request.type}</Badge>
              <span className="font-mono text-slate-500 text-sm">{request.ref_no}</span>
              <Badge className={getStatusColor(request.status)}>
                {getStatusLabel(request.status, request.type)}
              </Badge>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Manrope'] break-words leading-tight">
              {request.title}
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Created by {request.creator_name} on {formatDate(request.created_at)}
            </p>
          </div>

          {/* Document Expiry Alerts moved inline next to each assignee's name on the Assignments tab. */}

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* iter163i — "Discuss in DM" entry point. Opens a private chat
                with the request's creator (or any internal eligible user) and
                pre-attaches a /req card so they immediately see what we're
                talking about. Hidden for Company / Company Rep — they can't use DMs. */}
            {dmEligible && request.created_by && request.created_by !== user?.id && (
              <Button
                variant="outline"
                onClick={() => {
                  startDMWith({
                    user_id: request.created_by,
                    peer_name: request.creator_name,
                    embed: {
                      kind: 'request',
                      ref_no: request.ref_no,
                      label: `${request.ref_no} — ${request.title || ''}`,
                    },
                  });
                }}
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                data-testid="discuss-in-dm-btn"
                title="Private message — only you and them can read"
              >
                <Mail size={16} className="mr-2" />
                Discuss in DM
              </Button>
            )}
            {request.type === 'FT' && <FtWorkflowActions
              request={request}
              assignments={assignments}
              onChange={fetchData}
              checkDocGate={checkDocThreadGate}
              onDiscussionChanged={() => setDiscussionRefreshKey(k => k + 1)}
              onStartRoundWithGates={async (openModal) => {
                // iter167 Issue 27 — Reuse the CTA gate flow for FT: doc-thread
                // approval gate + pending-approvals gate, then run the supplied
                // openModal callback to actually open the FT new-round picker.
                // Stash the opener immediately so the doc-gate bypass dialog
                // and the skip-approvals dialog both have a route back here.
                ftStartRoundOpenerRef.current = openModal;
                if (await checkDocThreadGate('all', 'start-round')) return;
                try {
                  const res = await approvalsApi.getPendingApprovals(id);
                  const pending = res.data?.pending || [];
                  if (pending.length > 0) {
                    setPendingApprovers(pending);
                    setSkipApprovalsTarget('start-round');
                    setShowSkipApprovalsDialog(true);
                    return;
                  }
                } catch (_e) { /* fall through to open the modal directly */ }
                ftStartRoundOpenerRef.current = null;
                openModal();
              }}
            />}
            {canSubmit && (
              <Button onClick={handleSubmitRequest} className="bg-blue-900 hover:bg-blue-950" data-testid="submit-btn" disabled={submitLoading}>
                {submitLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                {submitLoading ? 'Submitting...' : 'Submit Request'}
              </Button>
            )}
            {canResubmit && (
              <Button onClick={handleResubmit} className="bg-blue-900 hover:bg-blue-950" data-testid="resubmit-btn" disabled={submitLoading}>
                {submitLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                {submitLoading ? 'Resubmitting...' : 'Resubmit'}
              </Button>
            )}
            {canWithdraw && (
              <Button variant="outline" onClick={handleWithdraw} data-testid="withdraw-btn" disabled={withdrawLoading}>
                {withdrawLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                {withdrawLoading ? 'Withdrawing...' : 'Withdraw'}
              </Button>
            )}
            {canRouteToConsultants && (
              <Button 
                onClick={openRouteConsultantModal} 
                className="bg-teal-600 hover:bg-teal-700" 
                data-testid="route-btn"
              >
                <Users size={16} className="mr-2" />
                Route to Consultants
              </Button>
            )}
            {canAssignCoordinator && (
              <Button onClick={() => { setAssignData({ ...assignData, assignment_role: 'COORDINATOR' }); setShowAssignModal(true); }} className="bg-blue-900 hover:bg-blue-950" data-testid="assign-coordinator-btn">
                <Users size={16} className="mr-2" />
                Assign Coordinator
              </Button>
            )}
            {canAssignPIReviewer && (
              <>
                <Button onClick={openAssignPIModal} className="bg-violet-600 hover:bg-violet-700" data-testid="assign-pi-btn">
                  <User size={16} className="mr-2" />
                  Assign Investigators
                </Button>
                <Button onClick={openAssignReviewerModal} className="bg-blue-600 hover:bg-blue-700" data-testid="assign-reviewer-btn">
                  <Users size={16} className="mr-2" />
                  Assign to Reviewer(s)
                </Button>
              </>
            )}
            {/* iter143 — Admin/Chairman/CRD can also assign Investigators/Reviewers AT ANY
                stage prior to EXECUTED (was previously limited to a narrow status window).
                Per user: "let the chairman assigne investigators, reviewers until the request
                is executed, even if a doc or more is signed." */}
            {isAdminLevel && !canAssignPIReviewer && request.type === 'CTA' && request.status !== 'EXECUTED' && request.status !== 'FAILED' && request.status !== 'WITHDRAWN' && request.status !== 'REJECTED' && (
              <>
                {/* iter148h — Chairman must assign a Coordinator first. The Assign
                    Investigators / Assign to Reviewer(s) buttons stay visible but are
                    disabled with a tooltip until a Coordinator is on the request. */}
                <Button
                  onClick={openAssignPIModal}
                  disabled={!hasCoordinatorAssigned}
                  className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="assign-pi-btn"
                  title={hasCoordinatorAssigned ? undefined : 'Assign a Coordinator first'}
                >
                  <User size={16} className="mr-2" />
                  Assign Investigators
                </Button>
                <Button
                  onClick={openAssignReviewerModal}
                  disabled={!hasCoordinatorAssigned}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="assign-reviewer-btn"
                  title={hasCoordinatorAssigned ? undefined : 'Assign a Coordinator first'}
                >
                  <Users size={16} className="mr-2" />
                  Assign to Reviewer(s)
                </Button>
              </>
            )}
            {/* PI/Reviewer Approve button — iter148b: smart lock. The button hides
                after "Approve & lock" (hasUserApproved=true) and only reappears when
                (a) the approval is undone (userApproval gone) OR (b) the user gets
                re-assigned to a not-yet-APPROVED doc-thread (hasOpenDocThreadTasks
                flips back to true and the lock releases). */}
            {canApproveAsPIReviewer && !hasUserApproved && (
              <Button onClick={() => handleApproval('APPROVE')} className={`bg-emerald-600 hover:bg-emerald-700 ${approveButtonPulse ? 'approve-pulse-ring' : ''}`} data-testid="approve-btn" disabled={approveLoading}>
                {approveLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                {approveLoading ? 'Approving...' : 'Approve'}
              </Button>
            )}
            {/* Coordinator Approve button — opens the skip-pending-approvals dialog so the
                Coordinator can review (and skip) any unresponsive PI/Investigator/Reviewer
                approvals. The backend records the Coordinator's own APPROVE on confirm. */}
            {canApproveAsCoordinator && !hasUserApproved && (
              <Button onClick={openSkipApprovalsDialog} className={`bg-emerald-600 hover:bg-emerald-700 ${approveButtonPulse ? 'approve-pulse-ring' : ''}`} data-testid="approve-btn" disabled={approveLoading}>
                {approveLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                {approveLoading ? 'Approving...' : 'Approve'}
              </Button>
            )}
            {/* Final Approval buttons for Chairman/Admin/CRD */}
            {canGiveFinalApproval && (
              <>
                <Button onClick={handleCTAFinalApproveClick} className={`bg-emerald-600 hover:bg-emerald-700 ${approveButtonPulse ? 'approve-pulse-ring' : ''}`} data-testid="final-approve-btn">
                  <CheckCircle2 size={16} className="mr-2" />
                  Start Review & E-Signature Process
                </Button>
                <Button variant="outline" onClick={() => handleApproval('FINAL_FAIL')} className="text-red-600" data-testid="final-fail-btn" disabled={approveLoading}>
                  {approveLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <XCircle size={16} className="mr-2" />}
                  Confirm Failed
                </Button>
              </>
            )}
            {/* Admin-level direct approve (when not pending final) — opens Skip-Approvals dialog */}
            {canDirectApproval && !canGiveFinalApproval && (
              <>
                <Button onClick={openSkipApprovalsDialog} className={`bg-emerald-600 hover:bg-emerald-700 ${approveButtonPulse ? 'approve-pulse-ring' : ''}`} data-testid="approve-btn" disabled={approveLoading}>
                  {approveLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                  Approve
                </Button>
                <Button variant="outline" onClick={() => handleApproval('MARK_FAILED')} className="text-red-600" data-testid="mark-failed-btn">
                  <XCircle size={16} className="mr-2" />
                  Mark as Failed
                </Button>
              </>
            )}
            {canRespondFeasibility && (
              <Button 
                onClick={openFeasibilityResponseForm} 
                className="bg-teal-600 hover:bg-teal-700" 
                data-testid={isEditingFeaResponse ? "edit-feasibility-btn" : "feasibility-btn"}
                disabled={feasibilityFieldsLoading}
              >
                {feasibilityFieldsLoading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (isEditingFeaResponse ? 'Edit Response' : 'Respond to Feasibility')}
              </Button>
            )}
            {/* FEA: Complete & Close (requires routed + responses) */}
            {canCompleteCloseFEA && (
              <Button onClick={openCompleteCloseModal} className="bg-emerald-600 hover:bg-emerald-700" data-testid="complete-close-btn" disabled={closeLoading}>
                {closeLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                {closeLoading ? 'Closing...' : 'Complete & Close'}
              </Button>
            )}
            {/* FEA: Mark Failed (available anytime after submission) */}
            {canMarkFailedFEA && (
              <Button variant="outline" onClick={() => handleClose('FAILED')} className="text-red-600" data-testid="fail-btn" disabled={closeLoading}>
                {closeLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <XCircle size={16} className="mr-2" />}
                {closeLoading ? 'Closing...' : 'Mark Failed'}
              </Button>
            )}
            {/* Director Review Actions (legacy — stage-configured requests use the ReviewStagesPanel) */}
            {isDirector() && request.status === 'PENDING_DIRECTOR_REVIEW' && !(request.review_stages?.length) && (
              <>
                <Button onClick={handleDirectorApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="director-approve-btn" disabled={directorApproveLoading || directorRejectLoading}>
                  {directorApproveLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                  {directorApproveLoading ? 'Approving...' : 'Approve (Send to Exec Director)'}
                </Button>
                <Button variant="outline" onClick={handleDirectorReject} className="text-red-600" data-testid="director-reject-btn" disabled={directorApproveLoading || directorRejectLoading}>
                  {directorRejectLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <XCircle size={16} className="mr-2" />}
                  {directorRejectLoading ? 'Rejecting...' : 'Reject Request'}
                </Button>
              </>
            )}
            {/* Executive Director Review Actions (legacy — stage-configured requests use the ReviewStagesPanel) */}
            {isExecDirector() && request.status === 'PENDING_EXEC_DIRECTOR_REVIEW' && !(request.review_stages?.length) && (
              <>
                <Button onClick={handleExecDirectorApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="exec-director-approve-btn" disabled={execApproveLoading || execRejectLoading}>
                  {execApproveLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                  {execApproveLoading ? 'Approving...' : 'Approve (Start Signatures)'}
                </Button>
                <Button variant="outline" onClick={handleExecDirectorReject} className="text-red-600" data-testid="exec-director-reject-btn" disabled={execApproveLoading || execRejectLoading}>
                  {execRejectLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <XCircle size={16} className="mr-2" />}
                  {execRejectLoading ? 'Rejecting...' : 'Reject Request'}
                </Button>
              </>
            )}
            {/* iter140 — global "Edit E-Signature Configuration" REMOVED.
                Per-doc "Edit Signatories" buttons live inside each doc card in SignaturesTab. */}
            {/* Execute Request — Admin/CRD/Chairman/Director/Exec Director can manually execute after all sigs collected */}
            {(isAdmin() || isCRD() || isChairman() || isDirector() || isExecDirector()) && request.status === 'READY_TO_EXECUTE' && (
              <Button
                onClick={handleExecuteRequest}
                disabled={executeLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="execute-request-btn"
              >
                {executeLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                {executeLoading ? 'Executing…' : 'Execute Request'}
              </Button>
            )}
            {/* iter143 — "Edit Request Review" button removed (per user). Review stages
                are configured exclusively via the e-signature wizard. */}
            {/* iter146 — Start New Signature Round is DISABLED when there are still pending
                signatures on the current round (any doc not all_signed). Chairman/CRD must
                wait for the in-flight round to complete (or override docs individually).
                  Visible from PENDING_SIGNATURES → EXECUTED. */}
            {/* iter148f — Start New Signature Round HIDES once the request is EXECUTED
                (no further rounds make sense after final execution; admin who needs to
                re-open uses the "Reopen request" undo banner instead). */}
            {isAdminLevel && request.type === 'CTA' && ['PENDING_SIGNATURES', 'READY_TO_EXECUTE'].includes(request.status) &&
              ctaRoundsConfig?.allow_additional_rounds && (() => {
                const hasPendingSigs = (signatureStatus?.document_statuses || []).some(d => !d.all_signed);
                return (
                  <Button
                    variant="outline"
                    onClick={openStartRoundWithGates}
                    disabled={hasPendingSigs}
                    className="border-blue-500 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                    data-testid="start-signature-round-btn"
                    title={hasPendingSigs ? 'Wait until all signatures in the current round are collected (or override pending docs) before starting a new round.' : undefined}
                  >
                    <PenTool size={16} className="mr-2" />
                    Start New Signature Round
                  </Button>
                );
              })()}
            {/* iter146 — "Sign Now" highlighted button surfaces in the request header
                whenever the current user has a PENDING signature on this request. Pulsing
                animation + signature deadline tooltip nudge them to act immediately.
                iter147 — filter key fixed: SignatureResponse uses `user_id`, not
                `signatory_user_id` (the old name silently matched nothing). */}
            {/* iter146 — Sign Now button surfaces in the request header for any user
                with a PENDING signature. iter147 fixed the filter key (`user_id`).
                iter148h — also gated on `!hasActiveReviewStage` per user: signing must
                wait for any active request-level review (Director / Exec Director /
                Custom / Finance) to finish before signatories see the button. */}
            {(() => {
              if (hasActiveReviewStage) return null;
              const myPendingSigs = (signatures || []).filter(s => s.user_id === user?.id && s.status === 'PENDING');
              if (myPendingSigs.length === 0) return null;
              return (
                <Button
                  onClick={() => navigate(`/requests/${id}/sign`)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg ring-2 ring-emerald-300 animate-pulse"
                  data-testid="sign-now-header-btn"
                  title={`You have ${myPendingSigs.length} pending signature${myPendingSigs.length > 1 ? 's' : ''} on this request`}
                >
                  <PenTool size={16} className="mr-2" />
                  Sign Now ({myPendingSigs.length})
                </Button>
              );
            })()}

            {/* iter143 — header "Go to Signing Page" button removed; the per-doc + the
                SignaturesTab top-level button cover all entry points. */}
            {canInitiateHandover && (
              <Button 
                variant="outline" 
                onClick={openHandoverModal}
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="handover-btn"
              >
                <ArrowRightLeft size={16} className="mr-2" />
                Handover
              </Button>
            )}
            {/* Admin Override Status */}
            {isAdmin() && (
              <Button 
                variant="outline" 
                onClick={handleOpenAdminOverride}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="admin-override-btn"
              >
                <ShieldAlert size={16} className="mr-2" />
                Override Status
              </Button>
            )}
            {/* Admin Hard Delete */}
            {isAdmin() && (
              <Button
                variant="outline"
                className="border-red-400 text-red-800 hover:bg-red-50"
                data-testid="admin-delete-request-btn"
                onClick={async () => {
                  const msg = `Permanently delete ${request.ref_no} (${request.type}) and ALL related data — assignments, signatures, documents, audit, and files on disk?\n\nThis cannot be undone.`;
                  if (!window.confirm(msg)) return;
                  try {
                    const res = await requestsApi.adminDelete(request.id);
                    toast.success(res.data?.message || 'Request deleted');
                    if (request.study_id) navigate(`/studies/${request.study_id}`);
                    else navigate('/requests');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to delete request');
                  }
                }}
              >
                <Trash2 size={16} className="mr-2" />
                Delete Request
              </Button>
            )}
          </div>
          {/* iter132 (#3c) — sticky inline "Next step ↑" hint that fades in after the doc-thread
              gate clears, pointing the user back at the Approve button so they don't get lost.
              iter148c — also hide it whenever the Approve button is hidden (the lock has
              engaged or the user isn't an active approver) so the hint doesn't dangle
              pointing at nothing. */}
          {approveNextStepHint && canApproveAsPIReviewer && !hasUserApproved && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 animate-pulse"
              data-testid="approve-next-step-hint"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Next step ↑ — Approve the request to proceed
            </div>
          )}
        </div>
        {/* iter124 — green "you've approved, you're locked" banner. Only renders for
            non-privileged users (the lock predicate already excludes privileged roles).
            Includes an Undo my approval button so users don't feel stuck. */}
        {/* iter148f — once the request is EXECUTED the lock banner is no longer
            meaningful (the request itself is locked); hide it everywhere. */}
        {hasUserApproved && userApproval && request.status !== 'EXECUTED' && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6"
            data-testid="user-approved-lock-banner"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-900 text-sm">
                  You&apos;ve approved this request — interactions are locked.
                </p>
                <p className="text-emerald-800 text-xs mt-1">
                  You can no longer upload docs, reply on threads, change doc status, or use Assign-to.
                  To resume, click &ldquo;Undo my approval&rdquo; below or contact an Admin / CRD / Chairman / Director / Exec Director.
                </p>
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-300 text-emerald-900 hover:bg-emerald-100"
                    onClick={() => handleUndoApproval(userApproval.id)}
                    data-testid="user-approved-lock-undo-btn"
                  >
                    Undo my approval
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Chairman pre-emptive skip banner — visible to all roles, persists for the life of the request */}
        {/* iter148f — chairman_skipped_approvals banner is hidden once the request is
            EXECUTED. The audit history persists in a separate Admin-only "Executed with
            skipped items" banner that surfaces only on EXECUTED requests with an
            Undo/Reopen action. */}
        {/* iter163 — Hidden from Company / Company Rep entirely. Skip events are an
            internal workflow detail; the customer-facing view should not surface
            "Coordinator skipped N pending approvals" copy. Privileged staff still
            see the banner and the Undo Skip button. */}
        {request.chairman_skipped_approvals && request.status !== 'EXECUTED' && !isCompanyRep() && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="chairman-skipped-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-amber-900">
                  {(() => {
                    const ev = request.chairman_skipped_approvals;
                    // iter127 — display the actual skipper role rather than hard-coding "Chairman".
                    // Coordinator-initiated skips reuse the same backend field but label correctly.
                    const skipperLabel = ev.skipped_by_role === 'COORDINATOR' ? 'Coordinator' : 'Chairman';
                    const n = ev.skipped_assignees?.length || 0;
                    if (n === 0) return `${ev.skipped_by_name} approved without team review`;
                    if (n === 1) return `1 pending approval was skipped by ${skipperLabel}`;
                    return `${n} pending approvals were skipped by ${skipperLabel}`;
                  })()}
                </h3>
                <p className="text-amber-800 text-sm mt-1">
                  Skipped by <strong>{request.chairman_skipped_approvals.skipped_by_name}</strong>
                  {' '}({request.chairman_skipped_approvals.skipped_by_role})
                  {' '}on {formatDate(request.chairman_skipped_approvals.skipped_at)}.
                </p>
                {(request.chairman_skipped_approvals.skipped_assignees?.length || 0) > 0 && (
                  <ul className="text-amber-800 text-sm mt-2 list-disc pl-5">
                    {request.chairman_skipped_approvals.skipped_assignees.map((s) => (
                      <li key={s.assignment_id}>
                        <strong>{formatAssignmentRoleLabel(s.assignment_role)}</strong> — {s.user_name}
                      </li>
                    ))}
                  </ul>
                )}
                {/* iter123 — Undo Skip button. Visible to the original skipper OR any
                    Admin / CRD / Chairman / Director / Exec Director. Re-opens every
                    SKIPPED_BY_CHAIRMAN assignment back to PENDING in one click. */}
                {(() => {
                  const ev = request.chairman_skipped_approvals;
                  const isOriginalSkipper = ev.skipped_by_user_id === user?.id;
                  const isPriv = isAdmin() || isCRD() || isChairman() || isDirector() || isExecDirector();
                  if (!isOriginalSkipper && !isPriv) return null;
                  return (
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-900 hover:bg-amber-100"
                        data-testid="chairman-skipped-undo-btn"
                        onClick={async () => {
                          if (!window.confirm('Undo this skip? All skipped approvers will be re-opened as PENDING and the request status will be restored.')) return;
                          try {
                            await requestsApi.undoSkippedApprovals(id);
                            toast.success('Skip undone — approvers re-opened.');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to undo skip');
                            return;
                          }
                          // iter124 — run refetches in their own try/catch so a transient
                          // fetch failure doesn't surface as "Failed to undo skip" even
                          // though the undo itself already succeeded server-side.
                          try { await fetchData(); } catch (e) { /* noop */ }
                        }}
                      >
                        Undo Skip
                      </Button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
        {isPendingHandoverTarget && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">Handover Request Pending</h3>
                <p className="text-amber-800 mt-1">
                  <strong>{pendingHandover.from_coordinator_name}</strong> has requested to hand over this request to you.
                </p>
                <p className="text-amber-700 text-sm mt-1">
                  <strong>Reason:</strong> {pendingHandover.reason}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button 
                    onClick={handleAcceptHandover}
                    disabled={handoverLoading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="accept-handover-btn"
                  >
                    {handoverLoading ? 'Processing...' : 'Accept Handover'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowRejectHandoverModal(true)}
                    disabled={handoverLoading}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="reject-handover-btn"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* iter148f — Per-request "My Pending Doc Threads" banner. Surfaces ONLY when the
            current user is still an active status_assignee on at least one non-APPROVED
            doc thread of this request. Mirrors the dashboard widget but scoped to the
            current request so they can jump straight to the threads from inside the
            request page. Hides automatically once the user has no more pending tasks here
            (so it doesn't dangle), and hides on EXECUTED. */}
        {(() => {
          if (request.status === 'EXECUTED') return null;
          const myPending = (docThreadIndex || []).filter(t =>
            (t.document_status_assignees || []).includes(user?.id) &&
            t.document_status !== 'APPROVED' && t.document_status !== 'COMPLETED' &&
            // iter148h — also exclude threads where THIS user has already personally
            // approved (their slot is ticked even though others may still be pending).
            // Without this filter, a multi-assignee doc keeps the banner visible to a
            // user who has already done their part — confusing UX.
            !((t.document_status_approved_by || []).includes(user?.id))
          );
          if (myPending.length === 0) return null;
          return (
            <div
              className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
              data-testid="my-pending-doc-threads-banner"
            >
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-blue-900">
                    Your pending doc thread review{myPending.length > 1 ? 's' : ''} ({myPending.length})
                  </h3>
                  <p className="text-blue-700 text-sm mt-0.5">
                    You&apos;re assigned to review {myPending.length} doc thread{myPending.length > 1 ? 's' : ''} on this request. Click a chip to jump straight to the thread.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {myPending.map(t => (
                      <Button
                        key={t.id}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // iter148j — Discussion tab matches highlight by `thread.id`.
                          // iter161 — Robust scroll: the discussion tab content
                          // lazy-renders, so a single 200ms timeout often misses
                          // and the user has to click the chip twice. We now
                          // poll up to 1.6s in 100ms steps until the target row
                          // is in the DOM, then scroll once and stop.
                          setActiveTab('discussion');
                          setHighlightedThreadId(t.id);
                          let attempts = 0;
                          const tryScroll = () => {
                            const el = document.querySelector(`[data-thread-id="${t.id}"]`);
                            if (el && el.scrollIntoView) {
                              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              return;
                            }
                            attempts += 1;
                            if (attempts < 16) setTimeout(tryScroll, 100);
                          };
                          setTimeout(tryScroll, 60);
                          setTimeout(() => setHighlightedThreadId(null), 4500);
                        }}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        data-testid={`my-pending-thread-chip-${t.id}`}
                      >
                        {t.document_doc_type || t.doc_type || t.document_filename || 'Doc thread'}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* iter148f — Executed-with-skipped-items banner (Admin-only). Always renders on
            EXECUTED CTAs (per user: "Always — lists 'no items skipped' if empty").
            Shows what was skipped at any point during the workflow + an Undo/Reopen
            button that flips status from EXECUTED → READY_TO_EXECUTE while keeping
            signatures intact. */}
        {/* iter163f — Apply the same banner + Reopen/Undo behaviour to FT (was CTA-only).
            "Execute" should yield identical post-execute UX across request types. */}
        {request.status === 'EXECUTED' && (request.type === 'CTA' || request.type === 'FT') && isAdmin() && (
          <div
            className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-6"
            data-testid="executed-skipped-items-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-violet-900">Request executed — audit summary</h3>
                {(() => {
                  const ev = request.chairman_skipped_approvals;
                  const skipped = ev?.skipped_assignees || [];
                  const missedApprovals = request.executed_missed_approvals || [];
                  const missedDocThreads = request.executed_missed_doc_threads || [];
                  const nothingMissed = skipped.length === 0 && missedApprovals.length === 0 && missedDocThreads.length === 0;
                  if (nothingMissed) {
                    return (
                      <p className="text-violet-700 text-sm mt-0.5">✅ No approvals were skipped during this request&apos;s lifecycle.</p>
                    );
                  }
                  const skipperLabel = ev?.skipped_by_role === 'COORDINATOR' ? 'Coordinator' : 'Chairman';
                  return (
                    <>
                      {skipped.length > 0 && (
                        <>
                          <p className="text-violet-700 text-sm mt-0.5">
                            {skipped.length} approval{skipped.length > 1 ? 's were' : ' was'} skipped by{' '}
                            <strong>{ev.skipped_by_name}</strong> ({skipperLabel}) on {formatDate(ev.skipped_at)}.
                          </p>
                          <ul className="mt-2 text-xs text-violet-800 list-disc pl-5 space-y-0.5">
                            {skipped.map((s) => (
                              <li key={s.assignment_id}>
                                {s.assignment_role} — {s.user_name}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {missedApprovals.length > 0 && (
                        <div className="mt-3" data-testid="executed-missed-approvals">
                          <div className="text-sm font-semibold text-violet-900">✋ Missed request approvals ({missedApprovals.length})</div>
                          <ul className="mt-1 text-xs text-violet-800 list-disc pl-5 space-y-0.5">
                            {missedApprovals.map((a) => (
                              <li key={a.assignment_id}>{a.user_name || a.user_id} — <span className="opacity-70">{a.role}</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {missedDocThreads.length > 0 && (
                        <div className="mt-3" data-testid="executed-missed-doc-threads">
                          <div className="text-sm font-semibold text-violet-900">📄 Missed doc thread approvals ({missedDocThreads.length})</div>
                          <ul className="mt-1 text-xs text-violet-800 list-disc pl-5 space-y-0.5">
                            {missedDocThreads.map((d) => (
                              <li key={d.document_id}>{d.filename || d.doc_type || d.document_id} — <span className="opacity-70">{d.status}</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                })()}
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm('Reopen this executed request? Status will return to READY_TO_EXECUTE; signatures stay intact.')) return;
                      try {
                        await adminApi.reopenExecuted(id);
                        toast.success('Request reopened — status reverted to READY_TO_EXECUTE.');
                        await fetchData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to reopen request');
                      }
                    }}
                    className="border-violet-400 text-violet-700 hover:bg-violet-100"
                    data-testid="executed-reopen-btn"
                  >
                    Undo / Reopen request
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* iter143 — Unassigned doc-thread banner. Surfaces for Chairman + Coordinator when
            any doc thread on the request has zero status_assignees, so they can quickly
            assign reviewers before the workflow stalls. One-click jump + temporary highlight. */}
        {showUnassignedBanner && (
          <div className="rounded-lg p-4 mb-6 bg-amber-50 border-2 border-amber-300 shadow-sm" data-testid="unassigned-doc-thread-banner">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white flex-shrink-0 shadow">
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">
                    {unassignedDocThreads.length} doc thread{unassignedDocThreads.length > 1 ? 's' : ''} need your attention
                  </h3>
                  <p className="text-sm text-amber-800 mt-1">
                    The following doc thread{unassignedDocThreads.length > 1 ? 's have' : ' has'} no assigned reviewer{unassignedDocThreads.length > 1 ? 's' : ''} yet. Click a chip to jump there and assign.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {unassignedDocThreads.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => scrollToDocThread(t.id)}
                        data-testid={`unassigned-thread-chip-${t.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-amber-400 text-amber-900 text-xs font-medium hover:bg-amber-100 transition-colors"
                      >
                        <FileText className="w-3 h-3" />
                        {t.document_doc_type || t.document_filename || 'Document'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={dismissUnassignedBanner}
                className="text-amber-600 hover:text-amber-900 flex-shrink-0"
                aria-label="Dismiss unassigned threads banner"
                data-testid="unassigned-doc-thread-banner-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Handover Status Banner (for old coordinator) */}
        {shouldShowHandoverBanner && (
          <div className={`rounded-lg p-4 mb-6 ${
            handoverStatusForOldCoord.status === 'PENDING' 
              ? 'bg-blue-50 border border-blue-200' 
              : handoverStatusForOldCoord.status === 'ACCEPTED'
                ? 'bg-emerald-50 border border-emerald-200'
                : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                {handoverStatusForOldCoord.status === 'PENDING' ? (
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                ) : handoverStatusForOldCoord.status === 'ACCEPTED' ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  {handoverStatusForOldCoord.status === 'PENDING' && (
                    <>
                      <h3 className="font-semibold text-blue-900">Handover Pending Acceptance</h3>
                      <p className="text-blue-800 mt-1">
                        Waiting for <strong>{handoverStatusForOldCoord.to_coordinator_name}</strong> to accept the handover request.
                      </p>
                      <p className="text-blue-700 text-sm mt-1">
                        You can continue working on this request until the handover is accepted.
                      </p>
                    </>
                  )}
                  {handoverStatusForOldCoord.status === 'ACCEPTED' && (
                    <>
                      <h3 className="font-semibold text-emerald-900">Handover Accepted</h3>
                      <p className="text-emerald-800 mt-1">
                        Handover accepted by <strong>{handoverStatusForOldCoord.to_coordinator_name}</strong>. You no longer have access to actions on this request.
                      </p>
                    </>
                  )}
                  {handoverStatusForOldCoord.status === 'REJECTED' && (
                    <>
                      <h3 className="font-semibold text-red-900">Handover Rejected</h3>
                      <p className="text-red-800 mt-1">
                        Handover rejected by <strong>{handoverStatusForOldCoord.to_coordinator_name}</strong>.
                      </p>
                      <p className="text-red-700 text-sm mt-1">
                        <strong>Reason:</strong> {handoverStatusForOldCoord.rejection_reason}
                      </p>
                      <p className="text-red-700 text-sm mt-1">
                        This has been escalated to the Chairman.
                      </p>
                    </>
                  )}
                </div>
              </div>
              <button 
                onClick={handleCloseHandoverBanner}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Close banner"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Phase 5: Chairman-configured review flow progress + current-stage reviewer actions */}
        <div className="mb-6 empty:mb-0">
          <ReviewStagesPanel request={request} onChange={fetchData} />
        </div>

        {/* Tabs */}
        <Tabs 
          value={activeTab}
          onValueChange={(value) => {
            setActiveTab(value);
            // iter148g — Audit Log tab: refetch the audit log every time the tab is
            // opened so post-action entries (Execute / Reopen / Override / etc.) surface
            // without a manual page refresh.
            if (value === 'audit') {
              auditLogsApi.getAll(id).then(res => setAuditLogs(res.data)).catch(() => {});
            }
            // Refresh documents when switching to documents tab
            if (value === 'documents') {
              const params = docTypeFilter !== 'all' ? { doc_type: docTypeFilter } : {};
              documentsApi.getAll(id, params).then(res => setDocuments(res.data)).catch(() => {});
              // Also refresh available doc types
              documentsApi.getDocTypes(id).then(res => setAvailableDocTypes(res.data.doc_types || [])).catch(() => {});
            }
            // iter127 — refresh pending invitations whenever the user opens the Assignments
            // tab so external invite creations (by other users / testing) surface without F5.
            if (value === 'assignments') {
              requestsApi.getPendingConsultantInvites(id)
                .then(res => setPendingInvites(res.data || []))
                .catch(() => {});
            }
          }}
        >
          <TabsList>
            {(tabsConfig?.overview ?? true) && (
              <TabsTrigger value="overview" data-testid="tab-trigger-overview">Overview</TabsTrigger>
            )}
            {(tabsConfig?.assignments ?? true) && (
              <TabsTrigger value="assignments" data-testid="tab-trigger-assignments">Assignments ({assignments.filter(a => a.status !== 'HANDED_OVER').length})</TabsTrigger>
            )}
            {/* Documents tab - always visible for all users (unless admin restricts) */}
            {(tabsConfig?.documents ?? true) && (
              <TabsTrigger value="documents" data-testid="tab-trigger-documents">Documents ({docsTabCount})</TabsTrigger>
            )}
            {(tabsConfig?.discussion ?? true) && (
              <TabsTrigger value="discussion" data-testid="tab-trigger-discussion">
                <MessageSquare className="w-4 h-4 mr-1" />
                Discussion {commentsCount > 0 && `(${commentsCount})`}
              </TabsTrigger>
            )}
            {/* Approvals tab — CTA always; FT once the first workflow approval is recorded */}
            {(tabsConfig?.approvals ?? true) && (request.type === 'CTA' || (request.type === 'FT' && approvals.length > 0)) && (
              <TabsTrigger value="approvals" data-testid="tab-trigger-approvals">Approvals ({approvals.length})</TabsTrigger>
            )}
            {/* Signatures tab — CTA from PENDING_DIRECTOR_REVIEW onwards; FT from Chairman e-sign config onwards.
                iter167 Bug 3 — Override the admin tabsConfig: if the
                current user has at least ONE signature slot (PENDING or
                SIGNED) on this request, always show the tab so they can
                see what they've signed. Without this, COMPANY_REP /
                INVESTIGATOR signers lost the tab the moment they signed
                and could no longer view their own signed doc.
                iter167 Issue 21 — Company-account parity: any signature
                row visible to this viewer (after backend scoping that
                already unions Company-account teammates) is enough to
                show the tab — so a Company OWNER who never personally
                signed still sees the tab when their reps have signed. */}
            {(((tabsConfig?.signatures ?? true) || (signatures || []).length > 0) && showSignaturesTab) && (
              <TabsTrigger value="signatures" data-testid="tab-trigger-signatures">
                <PenTool className="w-4 h-4 mr-1" />
                Signatures ({signatures.filter(s => s.status === 'SIGNED').length}/{signatures.length})
              </TabsTrigger>
            )}
            {/* Responses tab for FEA - visible to CRD, Coordinator, Company, Consultant */}
            {(tabsConfig?.response ?? true) && canViewResponses && (
              <TabsTrigger value="responses" data-testid="tab-trigger-responses">Responses ({feasibilityResponses.length})</TabsTrigger>
            )}
            {(tabsConfig?.audit_log ?? true) && (
              <TabsTrigger value="audit" data-testid="tab-trigger-audit">Audit Log</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab 
              request={request}
              assignments={assignments}
              changeRequests={changeRequests}
              canEditSpecialties={canEditSpecialties}
              openEditSpecialtyModal={openEditSpecialtyModal}
              canEditCompany={canEditCompany}
              openEditCompanyModal={openEditCompanyModal}
              isCompanyRep={isCompanyRep}
              overviewLayout={overviewLayout}
              documents={documents}
            />
            {request.type === 'FEA' && (
              <div className="mt-6">
                <FeaCoordinatorPanel
                  request={request}
                  currentUser={user}
                  onChanged={() => fetchData()}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <DocumentsTab
              requestId={id}
              documents={documents}
              setDocuments={setDocuments}
              availableDocTypes={availableDocTypes}
              canUploadDocs={canUploadDocs}
              setShowUploadModal={setShowUploadModal}
              handleDownloadDocument={handleDownloadDocument}
              downloadingDoc={downloadingDoc}
              isAdmin={isAdmin()}
              bundleToolAvailable={bundleTool.available}
              onCountChange={setDocsTabCount}
              onOpenBundleDialog={() => setShowBundleDialog(true)}
              onGoToThread={(threadId, style = 'doc-preview') => {
                setActiveTab('discussion');
                // NOTE: for the doc-preview (soft-blue) path we deliberately DO NOT call
                // setHighlightedThreadId — the JS useEffect inside DiscussionThread already
                // paints the soft-blue ring from the URL's `highlight=doc-preview` param.
                // Calling setHighlightedThreadId would layer a legacy static RED ring
                // (ThreadCard.isHighlighted) on top of the blue and break the spec.
                setSearchParams({ tab: 'discussion', comment: threadId, highlight: style });
                setTimeout(() => {
                  const el = document.querySelector(`[data-thread-id="${threadId}"]`);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
            />
          </TabsContent>

          <TabsContent value="discussion" className="mt-6">
            <DiscussionThread 
              requestId={id} 
              requestType={request?.type}
              title="Request Discussion"
              highlightedCommentId={highlightedCommentId}
              highlightedThreadId={highlightedThreadId}
              highlightStyle={highlightStyle}
              assignedTeam={assignments.filter(a => a.status !== 'HANDED_OVER').map(a => ({ id: a.assignee_user_id, name: a.assignee_name }))}
              readOnly={isExecuted || hasUserApproved}
              userHasApproved={hasUserApproved}
              refreshKey={discussionRefreshKey}
              onPreviewDoc={(docId) => {
                if (!docId) return;
                // Minimal stub — DocumentPreviewModal will fetch the full record via documentsApi.getById
                setThreadPreviewDoc({ id: docId });
              }}
              onOpenBundleNewVersion={(docType) => {
                setBundleDialogLockedDocType(docType);
                setShowBundleDialog(true);
              }}
              onDocumentsChanged={() => {
                // Refresh documents list when attachments are added/modified
                const params = docTypeFilter !== 'all' ? { doc_type: docTypeFilter } : {};
                documentsApi.getAll(id, params).then(res => setDocuments(res.data)).catch(() => {});
                // Also refresh doc types
                documentsApi.getDocTypes(id).then(res => setAvailableDocTypes(res.data.doc_types || [])).catch(() => {});
              }}
            />
          </TabsContent>

          <TabsContent value="assignments" className="mt-6">
            <AssignmentsTab
              request={request}
              assignments={assignments}
              documents={documents}
              isCompanyRep={isCompanyRep}
              isAdmin={isAdmin}
              isChairman={isChairman}
              currentUserRole={user?.role}
              currentUserId={user?.id}
              docAlerts={requestDocAlerts}
              pendingInvitations={pendingInvites}
              onRefresh={async () => {
                // iter167 Issue 12 — after viewer-access toggle, re-pull
                // the request so `viewer_user_ids` is up to date.
                try {
                  const reqRes = await requestsApi.getById(id);
                  setRequest(reqRes.data);
                } catch (_e) { /* best-effort */ }
              }}
              onToggleAutoAssign={async (nextEnabled) => {
                // iter131 — Chairman/Admin toggle for the CTA Coordinator / FT FO thread
                // auto-assign. ON re-uses the existing "enable" endpoint (which also opens
                // the confirm modal flow), OFF hits the new disable endpoint that just
                // clears the flag (past assignments are preserved).
                try {
                  if (nextEnabled) {
                    if (request.type === 'CTA') {
                      await api.post(`/requests/${id}/coordinator-thread-autoassign`);
                      toast.success('Coordinator auto-assign enabled. Future doc uploads will auto-assign the Coordinator.');
                    } else if (request.type === 'FT') {
                      await api.post(`/requests/${id}/ft-fo-thread-autoassign`);
                      toast.success('Financial Officer auto-assign enabled. Future doc uploads will auto-assign the FO.');
                    }
                  } else {
                    if (request.type === 'CTA') {
                      await api.post(`/requests/${id}/coordinator-thread-autoassign/disable`);
                      toast.success('Coordinator auto-assign turned OFF. Past assignments retained.');
                    } else if (request.type === 'FT') {
                      await api.post(`/requests/${id}/ft-fo-thread-autoassign/disable`);
                      toast.success('Financial Officer auto-assign turned OFF. Past assignments retained.');
                    }
                  }
                  fetchData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to update auto-assign');
                }
              }}
              onRemoveAssignment={async (assignmentId) => {
                await assignmentsApi.remove(id, assignmentId);
                toast.success('Assignment removed');
                fetchData();
              }}
            />
          </TabsContent>

          <TabsContent value="approvals" className="mt-6">
            <ApprovalsTab
              request={request}
              approvals={approvals}
              isAdminLevel={isAdminLevel}
              isActiveCoordinator={isCurrentCoordinator && !isPendingHandoverTarget && !wasHandoverAccepted}
              currentUserId={user?.id}
              handleUndoApproval={handleUndoApproval}
            />
          </TabsContent>

          {canViewResponses && (
            <TabsContent value="responses" className="mt-6">
              <ResponsesTab
                request={request}
                feasibilityResponses={feasibilityResponses}
                onEditResponse={canRespondFeasibility ? openFeasibilityResponseForm : null}
              />
            </TabsContent>
          )}

          {/* Signatures Tab Content — visible once the e-signature config exists */}
          {showSignaturesTab && (
            <TabsContent value="signatures" className="mt-6">
              <SignaturesTab
                request={request}
                signatures={signatures}
                signatureStatus={signatureStatus}
                setSignatureStatus={setSignatureStatus}
                isAdmin={isAdmin}
                isCoordinator={isCoordinator}
                isChairman={isChairman}
                isExecDirector={isExecDirector}
                isCRD={isCRD}
                onEditSignatories={(docId) => {
                  // iter143 — pass the focused doc id so the modal can scope the picker
                  // to just that doc (mini-modal experience).
                  setCtaEsignBundle(!!request.ft_bundle_document_id);
                  setEditEsigFocusDocId(docId);
                  setShowEditESigModal(true);
                }}
                onPreviewSignedDoc={(signedPdfId) => {
                  // iter141 — reuse the existing thread-preview modal pointed at the signed PDF id.
                  setThreadPreviewDoc({ id: signedPdfId });
                }}
                onGoToSigningPage={(docId) => navigate(`/requests/${id}/sign?doc=${docId}`)}
                onRefresh={async () => {
                  // iter167 Issue 11 — refresh signatures + status after
                  // an inline delete / re-assign on the Signatures tab.
                  try {
                    const [statusRes, sigsRes, reqRes] = await Promise.all([
                      signaturesApi.getStatus(id),
                      signaturesApi.getAll(id),
                      requestsApi.getById(id),
                    ]);
                    setSignatureStatus(statusRes.data);
                    setSignatures(sigsRes.data || []);
                    setRequest(reqRes.data);
                  } catch (_e) { /* best-effort */ }
                }}
              />
            </TabsContent>
          )}

          <TabsContent value="audit" className="mt-6">
            <AuditTab
              request={request}
              auditLogs={auditLogs}
              handoverHistory={handoverHistory}
            />
          </TabsContent>
        </Tabs>

        {/* Assign Coordinator Modal (for Chairman) */}
        <Dialog open={showAssignModal && assignData.assignment_role === 'COORDINATOR'} onOpenChange={(open) => { if (!open) setShowAssignModal(false); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Coordinator</DialogTitle>
              <DialogDescription>Select a coordinator for this request</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Coordinator</Label>
                <Select value={assignData.assignee_user_id} onValueChange={(v) => setAssignData({ ...assignData, assignee_user_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.role === 'COORDINATOR').map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
              <Button onClick={handleAssign} disabled={assigning || !assignData.assignee_user_id} className="bg-blue-900 hover:bg-blue-950" data-testid="assign-coordinator-confirm-btn">
                {assigning ? <><Loader2 size={16} className="mr-2 animate-spin" /> Assigning...</> : 'Assign'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CTA Chairman: Coordinator → document threads auto-assign confirmation */}
        <Dialog open={showCoordAutoAssign} onOpenChange={setShowCoordAutoAssign}>
          <DialogContent data-testid="cta-coord-autoassign-modal">
            <DialogHeader>
              <DialogTitle>Auto-assign to document threads?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 py-1">
              <span className="font-semibold">{assignedCoordName}</span> has been assigned as Coordinator.
              Do you also want to auto-assign them to <span className="font-semibold">every document thread</span> on
              this request — both the threads that exist right now and any future document uploads?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCoordAutoAssign(false)} disabled={coordAutoAssigning} data-testid="cta-coord-autoassign-skip-btn">
                No, skip
              </Button>
              <Button onClick={handleCoordAutoAssign} disabled={coordAutoAssigning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cta-coord-autoassign-confirm-btn">
                {coordAutoAssigning ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                Yes, auto-assign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Route to Consultants Modal (for CRD) */}
        <Dialog open={showRouteConsultantModal} onOpenChange={setShowRouteConsultantModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Route to Consultants</DialogTitle>
              <DialogDescription>
                Pick from the matching consultants or invite by email. New emails get an Investigator registration invite and auto-route to this request once approved.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Customizable Response Fields Section */}
              <div className={`border rounded-lg overflow-hidden transition-shadow ${
                responseFieldsHintActive
                  ? 'border-amber-300 shadow-[0_0_0_4px_rgba(251,191,36,0.18)] animate-pulse'
                  : 'border-slate-200'
              }`}>
                <button
                  type="button"
                  onClick={() => {
                    setShowResponseFieldsSection(!showResponseFieldsSection);
                    // iter166e — Dismiss the hint as soon as the user
                    // interacts so it doesn't keep pulsing in their face.
                    if (responseFieldsHintActive) setResponseFieldsHintActive(false);
                  }}
                  className={`w-full flex items-center justify-between p-4 transition-colors ${
                    responseFieldsHintActive
                      ? 'bg-amber-50 hover:bg-amber-100'
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                  data-testid="customize-response-fields-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Settings2 className={`w-4 h-4 ${responseFieldsHintActive ? 'text-amber-600' : 'text-slate-600'}`} />
                    <span className="font-medium text-slate-900">Customize Response Fields</span>
                    <Badge variant="outline" className="text-xs">{responseFields.length} fields</Badge>
                    {responseFieldsHintActive && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">
                        See this first
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${showResponseFieldsSection ? 'rotate-180' : ''}`} />
                </button>
                
                {showResponseFieldsSection && (
                  <div className="p-4 border-t border-slate-200 bg-white space-y-4">
                    <p className="text-sm text-slate-600">
                      Customize the fields consultants will answer when responding to this feasibility request.
                    </p>
                    
                    {/* List of response fields */}
                    <div className="space-y-2">
                      {responseFields.map((field, index) => (
                        <div key={field.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{field.label}</span>
                              {field.locked && (
                                <Badge className="bg-slate-200 text-slate-600 text-xs">Required</Badge>
                              )}
                              <Badge variant="outline" className="text-xs capitalize">{field.type}</Badge>
                            </div>
                            {field.placeholder && (
                              <p className="text-xs text-slate-500 mt-1">{field.placeholder}</p>
                            )}
                          </div>
                          {!field.locked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setResponseFields(prev => prev.filter((_, i) => i !== index));
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Add new field button */}
                    <div className="flex items-center gap-2 pt-2">
                      <Select
                        onValueChange={(type) => {
                          const newField = {
                            id: `custom_${Date.now()}`,
                            type: type,
                            label: type === 'textarea' ? 'New Text Field' : type === 'number' ? 'New Number Field' : type === 'boolean' ? 'New Yes/No Question' : 'New Select Field',
                            required: false,
                            locked: false,
                            placeholder: '',
                            options: type === 'select' ? [{ value: 'option1', label: 'Option 1' }, { value: 'option2', label: 'Option 2' }] : undefined
                          };
                          setResponseFields(prev => [...prev, newField]);
                        }}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Add a field..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="textarea">Text Field</SelectItem>
                          <SelectItem value="number">Number Field</SelectItem>
                          <SelectItem value="boolean">Yes/No Question</SelectItem>
                          <SelectItem value="select">Dropdown Select</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-slate-500">Click to add a new response field</span>
                    </div>
                    
                    {/* Edit field inline (simplified - click to edit label) */}
                    {responseFields.filter(f => !f.locked).length > 0 && (
                      <div className="pt-2 border-t border-slate-200">
                        <p className="text-xs text-slate-500 mb-2">Edit field properties:</p>
                        <div className="space-y-3">
                          {responseFields.filter(f => !f.locked).map((field) => {
                            const actualIndex = responseFields.findIndex(f2 => f2.id === field.id);
                            return (
                              <div key={field.id} className="p-3 bg-slate-50 rounded-lg space-y-2">
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={field.label}
                                    onChange={(e) => {
                                      setResponseFields(prev => prev.map((f, i) => 
                                        i === actualIndex ? { ...f, label: e.target.value } : f
                                      ));
                                    }}
                                    className="flex-1 text-sm"
                                    placeholder="Field label"
                                  />
                                  <Checkbox
                                    checked={field.required || false}
                                    onCheckedChange={(checked) => {
                                      setResponseFields(prev => prev.map((f, i) => 
                                        i === actualIndex ? { ...f, required: checked } : f
                                      ));
                                    }}
                                  />
                                  <span className="text-xs text-slate-500">Required</span>
                                </div>
                                
                                {/* Options editor for select/dropdown fields */}
                                {field.type === 'select' && (
                                  <div className="pl-2 border-l-2 border-slate-300 space-y-2">
                                    <p className="text-xs text-slate-600 font-medium">Dropdown Options:</p>
                                    {(field.options || []).map((opt, optIdx) => (
                                      <div key={optIdx} className="flex items-center gap-2">
                                        <Input
                                          value={opt.label}
                                          onChange={(e) => {
                                            const newOptions = [...(field.options || [])];
                                            newOptions[optIdx] = { 
                                              value: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                                              label: e.target.value 
                                            };
                                            setResponseFields(prev => prev.map((f, i) => 
                                              i === actualIndex ? { ...f, options: newOptions } : f
                                            ));
                                          }}
                                          className="flex-1 text-xs h-8"
                                          placeholder={`Option ${optIdx + 1}`}
                                        />
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                          onClick={() => {
                                            const newOptions = (field.options || []).filter((_, i) => i !== optIdx);
                                            setResponseFields(prev => prev.map((f, i) => 
                                              i === actualIndex ? { ...f, options: newOptions } : f
                                            ));
                                          }}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    ))}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => {
                                        const newOptions = [...(field.options || []), { value: `option_${Date.now()}`, label: '' }];
                                        setResponseFields(prev => prev.map((f, i) => 
                                          i === actualIndex ? { ...f, options: newOptions } : f
                                        ));
                                      }}
                                    >
                                      <Plus className="w-3 h-3 mr-1" /> Add Option
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Unified Routing UI */}
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">Matching Consultants</h4>
                        <p className="text-xs text-slate-500">
                          Investigators whose specialties match this request. Select one or more.
                        </p>
                      </div>
                      {(() => {
                        const available = eligibleConsultants.filter(c => !c.already_assigned);
                        if (available.length === 0) return null;
                        const allChecked = available.every(c => routeSelectedIds.includes(c.id));
                        return (
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={(e) => {
                                if (e.target.checked) setRouteSelectedIds(available.map(c => c.id));
                                else setRouteSelectedIds([]);
                              }}
                              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                              data-testid="route-select-all"
                            />
                            Select all
                          </label>
                        );
                      })()}
                    </div>
                  </div>
                  {eligibleConsultants.length > 3 && (
                    <div className="px-3 py-2 border-b border-slate-200 bg-white">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        <input
                          type="text"
                          value={routeSearch}
                          onChange={(e) => setRouteSearch(e.target.value)}
                          placeholder="Search consultants by name or email…"
                          className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          data-testid="route-consultants-search"
                        />
                      </div>
                    </div>
                  )}
                  <div className="max-h-56 overflow-y-auto">
                    {eligibleConsultants.length === 0 ? (
                      <p className="p-4 text-sm text-amber-700 bg-amber-50">
                        No matching consultants found on the platform. Use the &quot;Invite by email&quot; section below to invite external investigators.
                      </p>
                    ) : (
                      eligibleConsultants
                        .filter(c => {
                          // iter166d — case-insensitive search across name + email.
                          if (!routeSearch.trim()) return true;
                          const q = routeSearch.trim().toLowerCase();
                          return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
                        })
                        .map(c => {
                        const isAssigned = c.already_assigned;
                        const checked = isAssigned || routeSelectedIds.includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 ${isAssigned ? 'bg-emerald-50/40' : 'hover:bg-slate-50 cursor-pointer'}`}
                            data-testid={`route-consultant-row-${c.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isAssigned}
                              onChange={(e) => {
                                if (isAssigned) return;
                                setRouteSelectedIds(prev => e.target.checked ? [...prev, c.id] : prev.filter(x => x !== c.id));
                              }}
                              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                            />
                            <User className="w-4 h-4 text-slate-400" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                              <div className="text-xs text-slate-500 truncate">{c.email}</div>
                            </div>
                            {isAssigned && (
                              <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Already routed
                              </Badge>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Invite by email (chips input) */}
                <div className="rounded-lg border border-slate-200 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <h4 className="font-medium text-slate-900">Invite by email</h4>
                  </div>
                  <p className="text-xs text-slate-500">
                    Type one or more emails (press <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">Enter</kbd>, <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">,</kbd> or <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">Tab</kbd>). Existing investigators will be routed; new emails get an Investigator registration invite — they auto-route once admin approves.
                  </p>
                  <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-lg bg-white min-h-[44px]" onClick={() => document.getElementById('route-email-input')?.focus()}>
                    {externalEmails.map((e, i) => (
                      <span key={`${e}-${i}`} className="inline-flex items-center gap-1 bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-2.5 py-0.5 text-xs" data-testid={`email-chip-${i}`}>
                        {e}
                        <button
                          type="button"
                          className="hover:text-teal-950"
                          onClick={(ev) => { ev.stopPropagation(); setExternalEmails(prev => prev.filter((_, j) => j !== i)); }}
                          aria-label={`Remove ${e}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      id="route-email-input"
                      type="email"
                      value={emailInputValue}
                      onChange={(e) => setEmailInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (['Enter', 'Tab', ','].includes(e.key)) {
                          e.preventDefault();
                          const v = emailInputValue.trim().replace(/,$/, '');
                          if (v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !externalEmails.includes(v.toLowerCase())) {
                            setExternalEmails(prev => [...prev, v.toLowerCase()]);
                          }
                          setEmailInputValue('');
                        } else if (e.key === 'Backspace' && !emailInputValue && externalEmails.length) {
                          setExternalEmails(prev => prev.slice(0, -1));
                        }
                      }}
                      onPaste={(e) => {
                        const text = e.clipboardData.getData('text');
                        if (text.includes(',') || text.includes(' ') || text.includes('\n')) {
                          e.preventDefault();
                          const parts = text.split(/[\s,;\n]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
                          const valid = parts.filter(s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && !externalEmails.includes(s));
                          if (valid.length) setExternalEmails(prev => [...prev, ...valid]);
                        }
                      }}
                      onBlur={() => {
                        const v = emailInputValue.trim();
                        if (v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !externalEmails.includes(v.toLowerCase())) {
                          setExternalEmails(prev => [...prev, v.toLowerCase()]);
                          setEmailInputValue('');
                        }
                      }}
                      placeholder={externalEmails.length === 0 ? "consultant@example.com, another@hospital.com" : ""}
                      className="flex-1 min-w-[160px] outline-none text-sm bg-transparent"
                      data-testid="route-external-email-input"
                    />
                  </div>
                </div>

                {/* Already routed list */}
                {eligibleConsultants.filter(c => c.already_assigned).length > 0 && (
                  <div>
                    <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Already routed</h4>
                    <div className="flex flex-wrap gap-2">
                      {eligibleConsultants.filter(c => c.already_assigned).map(c => (
                        <Badge key={c.id} className="bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {c.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending invitations */}
                {pendingInvites.length > 0 && (
                  <div>
                    <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Pending invitations</h4>
                    <div className="space-y-1.5">
                      {pendingInvites.map((inv, i) => (
                        <div key={`${inv.email}-${i}`} className="flex items-center gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                          <Clock className="w-3.5 h-3.5 text-amber-600" />
                          <span className="font-medium">{inv.email}</span>
                          <span className="text-amber-700">
                            {inv.status === 'awaiting_registration' && '— awaiting registration'}
                            {inv.status === 'registered_pending_admin_approval' && '— registered, awaiting admin approval'}
                            {inv.status === 'expired' && '— invitation expired'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* iter166c — Viewer (read-only) access toggle. ON by default in
                FEA Route to Consultants. */}
            <div
              className="mt-2 flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50"
              data-testid="route-viewer-toggle-row"
            >
              <Eye className={`w-5 h-5 mt-0.5 shrink-0 ${routeViewerEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">Grant view-only access to all docs</span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help">?</span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        Selected consultants will see all docs in the Documents tab with a &ldquo;View only&rdquo; badge — even if they&apos;re not assigned to a specific doc thread.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {routeViewerEnabled && (
                  <p className="mt-1 text-xs text-emerald-700">
                    This assignee will be able to see all existing and upcoming documents.
                  </p>
                )}
              </div>
              <Switch
                checked={routeViewerEnabled}
                onCheckedChange={setRouteViewerEnabled}
                data-testid="route-viewer-toggle"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRouteConsultantModal(false)} disabled={unifiedRouteLoading}>Cancel</Button>
              <Button
                onClick={handleUnifiedRoute}
                disabled={unifiedRouteLoading || (routeSelectedIds.length === 0 && externalEmails.length === 0 && !emailInputValue.trim())}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="unified-route-btn"
              >
                {unifiedRouteLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Routing…</> : (() => {
                  const r = routeSelectedIds.length;
                  const i = externalEmails.length + (emailInputValue.trim() ? 1 : 0);
                  if (r === 0 && i === 0) return 'Route';
                  const parts = [];
                  if (r > 0) parts.push(`Route ${r}`);
                  if (i > 0) parts.push(`Invite ${i}`);
                  return parts.join(' · ');
                })()}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* (Auto-route confirmation removed — unified Route button now drives the flow) */}

        {/* FEA Complete & Close dialog */}
        <Dialog open={showCompleteCloseModal} onOpenChange={setShowCompleteCloseModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Complete &amp; Close FEA</DialogTitle>
              <DialogDescription>
                Optional: pick the consultants to register as Principal Investigator(s) and Co-Investigator(s) for this study.
                Only consultants who answered <strong>Can participate: Yes</strong> are listed. The same consultant can&apos;t be both.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  className="pl-10"
                  placeholder="Search consultants by name or email..."
                  value={completeCloseSearch}
                  onChange={(e) => setCompleteCloseSearch(e.target.value)}
                  data-testid="complete-close-search"
                />
              </div>
              {piCandidates.length === 0 ? (
                <div className="rounded-lg border border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="complete-close-empty">
                  No consultants have responded with &quot;Can participate: Yes&quot;. You can still close without selecting a PI/Co-I.
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium text-slate-700">Consultant</th>
                        <th className="px-3 py-2 font-medium text-amber-700 text-center w-16">PI</th>
                        <th className="px-3 py-2 font-medium text-blue-700 text-center w-16">Co-I</th>
                      </tr>
                    </thead>
                    <tbody>
                      {piCandidates
                        .filter(c => {
                          if (!completeCloseSearch) return true;
                          const s = completeCloseSearch.toLowerCase();
                          return (c.name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s);
                        })
                        .map(c => {
                          const isPi = selectedPiIds.includes(c.id);
                          const isCoi = selectedCoiIds.includes(c.id);
                          return (
                            <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pi-row-${c.id}`}>
                              <td className="px-3 py-2.5">
                                <div className="font-medium text-slate-900">{c.name}</div>
                                <div className="text-xs text-slate-500">{c.email}</div>
                                {c.willing_to_participate_as_pi === true && (
                                  <span className="inline-block mt-1 text-xs text-amber-700">Indicated willing to be PI ⭐</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={isPi}
                                  onChange={() => togglePi(c.id)}
                                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                  data-testid={`pi-cb-${c.id}`}
                                />
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={isCoi}
                                  onChange={() => toggleCoi(c.id)}
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  data-testid={`coi-cb-${c.id}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  Closing will mark this FEA as <strong>Completed</strong> and notify the company account, involved company reps, and consultants who responded.
                  This action is final.
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompleteCloseModal(false)} disabled={closeLoading}>Cancel</Button>
              <Button
                onClick={handleCompleteClose}
                disabled={closeLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-complete-close-btn"
              >
                {closeLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Closing…</> : (() => {
                  const parts = [];
                  if (selectedPiIds.length) parts.push(`${selectedPiIds.length} PI`);
                  if (selectedCoiIds.length) parts.push(`${selectedCoiIds.length} Co-I`);
                  return parts.length ? `Complete & Close · ${parts.join(', ')}` : 'Complete & Close';
                })()}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={false} onOpenChange={() => {}}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Auto-Route</DialogTitle>
              <DialogDescription>
                The following consultants will receive this feasibility request:
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {eligibleConsultants.filter(c => !c.already_assigned).map(c => (
                <div key={c.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                  <User className="w-4 h-4 text-teal-600" />
                  <span className="font-medium">{c.name}</span>
                  <span className="text-slate-400 text-sm">{c.email}</span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAutoRouteConfirm(false)} disabled={autoRouteLoading}>Cancel</Button>
              <Button 
                onClick={handleAutoRouteConsultants}
                disabled={autoRouteLoading}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {autoRouteLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Routing...
                  </>
                ) : `Confirm & Route to ${eligibleConsultants.filter(c => !c.already_assigned).length} Consultant(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Investigators Modal (CTA) — mirrors FEA Route to Consultants */}
        <Dialog open={showAssignPIModal} onOpenChange={setShowAssignPIModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assign Investigators</DialogTitle>
              <DialogDescription>
                Pick from the matching investigators or invite by email. Tag each one as <strong>PI</strong> or <strong>Co-I</strong>. New emails get an Investigator registration invite and auto-assign once approved.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Matching investigators list */}
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900">Matching Investigators</h4>
                      <p className="text-xs text-slate-500">
                        Investigators whose specialties match this request. Select one or more, then tag each as PI or Co-I.
                      </p>
                    </div>
                    {(() => {
                      const available = eligiblePIs.filter(p => !p.already_assigned);
                      if (available.length === 0) return null;
                      const allChecked = available.every(p => piRouteSelectedIds.includes(p.id));
                      return (
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPiRouteSelectedIds(available.map(p => p.id));
                                // Default all newly selected to PI (coordinator can demote to Co-I)
                                setPiRoleByUser(prev => {
                                  const next = { ...prev };
                                  available.forEach(p => { if (!next[p.id]) next[p.id] = 'PI'; });
                                  return next;
                                });
                              } else {
                                setPiRouteSelectedIds([]);
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                            data-testid="assign-investigator-select-all"
                          />
                          Select all
                        </label>
                      );
                    })()}
                  </div>
                </div>
                {eligiblePIs.length > 3 && (
                  <div className="px-3 py-2 border-b border-slate-200 bg-white">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={piSearch}
                        onChange={(e) => setPiSearch(e.target.value)}
                        placeholder="Search investigators by name or email…"
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                        data-testid="assign-investigators-search"
                      />
                    </div>
                  </div>
                )}
                <div className="max-h-72 overflow-y-auto">
                  {eligiblePIs.length === 0 ? (
                    <p className="p-4 text-sm text-amber-700 bg-amber-50">
                      No matching investigators found on the platform. Use the &quot;Invite by email&quot; section below to invite externally.
                    </p>
                  ) : (
                    eligiblePIs
                      .map(pi => ({
                        ...pi,
                        _fea_pi: feaPiSuggestions.pi_user_ids?.includes(pi.id),
                        _fea_coi: feaPiSuggestions.co_investigator_user_ids?.includes(pi.id),
                      }))
                      .filter(pi => {
                        if (!piSearch.trim()) return true;
                        const q = piSearch.trim().toLowerCase();
                        return (pi.name || '').toLowerCase().includes(q) || (pi.email || '').toLowerCase().includes(q);
                      })
                      .sort((a, b) => {
                        const ar = a.already_assigned ? 2 : (a._fea_pi ? 0 : (a._fea_coi ? 1 : 1.5));
                        const br = b.already_assigned ? 2 : (b._fea_pi ? 0 : (b._fea_coi ? 1 : 1.5));
                        if (ar !== br) return ar - br;
                        return (a.name || '').localeCompare(b.name || '');
                      })
                      .map(pi => {
                        const isAssigned = pi.already_assigned;
                        const isChecked = isAssigned || piRouteSelectedIds.includes(pi.id);
                        const role = piRoleByUser[pi.id] || 'PI';
                        const sources = feaPiSuggestions.pi_sources?.[pi.id] || feaPiSuggestions.co_investigator_sources?.[pi.id] || [];
                        return (
                          <div
                            key={pi.id}
                            className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 ${
                              isAssigned
                                ? 'bg-emerald-50/40'
                                : (pi._fea_pi || pi._fea_coi)
                                  ? 'bg-emerald-50 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100/70'
                                  : 'hover:bg-slate-50'
                            }`}
                            data-testid={`assign-investigator-row-${pi.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={isAssigned}
                              onChange={(e) => {
                                if (isAssigned) return;
                                if (e.target.checked) {
                                  setPiRouteSelectedIds(prev => [...prev, pi.id]);
                                  setPiRoleByUser(prev => prev[pi.id] ? prev : { ...prev, [pi.id]: 'PI' });
                                } else {
                                  setPiRouteSelectedIds(prev => prev.filter(x => x !== pi.id));
                                }
                              }}
                              className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 disabled:opacity-50"
                              data-testid={`assign-investigator-checkbox-${pi.id}`}
                            />
                            <User className="w-4 h-4 text-slate-400" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                                {pi.name}
                                {pi._fea_pi && (
                                  <span className="inline-flex items-center gap-1 text-[10px] bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5" title={`Selected as PI on FEA ${sources.join(', ')}`}>
                                    <Star className="w-2.5 h-2.5" /> FEA PI
                                  </span>
                                )}
                                {pi._fea_coi && !pi._fea_pi && (
                                  <span className="inline-flex items-center gap-1 text-[10px] bg-blue-100 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5" title={`Selected as Co-Investigator on FEA ${sources.join(', ')}`}>
                                    FEA Co-I
                                  </span>
                                )}
                                {pi.recommended_from_fea && !pi._fea_pi && !pi._fea_coi && (
                                  <Star className="w-3 h-3 text-emerald-600" />
                                )}
                                {pi.doc_compliant === false && (
                                  <span className="text-[10px] text-red-500 bg-red-50 px-1 rounded" title={`Missing: ${(pi.doc_missing || []).join(', ')}${(pi.doc_expired || []).length ? '. Expired: ' + pi.doc_expired.join(', ') : ''}`}>
                                    Docs incomplete
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 truncate">{pi.email}</div>
                            </div>
                            {isAssigned ? (
                              <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Already assigned
                              </Badge>
                            ) : isChecked ? (
                              <div className="inline-flex items-center rounded-md border border-slate-200 bg-white text-xs overflow-hidden" data-testid={`assign-investigator-role-${pi.id}`}>
                                <button
                                  type="button"
                                  className={`px-2.5 py-1 ${role === 'PI' ? 'bg-amber-100 text-amber-800 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                                  onClick={() => setPiRoleByUser(prev => ({ ...prev, [pi.id]: 'PI' }))}
                                  data-testid={`assign-investigator-role-pi-${pi.id}`}
                                >
                                  PI
                                </button>
                                <button
                                  type="button"
                                  className={`px-2.5 py-1 border-l border-slate-200 ${role === 'COI' ? 'bg-blue-100 text-blue-800 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                                  onClick={() => setPiRoleByUser(prev => ({ ...prev, [pi.id]: 'COI' }))}
                                  data-testid={`assign-investigator-role-coi-${pi.id}`}
                                >
                                  Co-I
                                </button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })
                  )}
                </div>
              </div>

              {/* Invite by email (chips input) */}
              <div className="rounded-lg border border-slate-200 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <h4 className="font-medium text-slate-900">Invite by email</h4>
                </div>
                <p className="text-xs text-slate-500">
                  Type one or more emails (press <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">Enter</kbd>, <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">,</kbd> or <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">Tab</kbd>). Existing investigators will be assigned; new emails get an Investigator registration invite — they auto-assign once admin approves.
                </p>
                <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-lg bg-white min-h-[44px]" onClick={() => document.getElementById('assign-investigator-email-input')?.focus()}>
                  {piExternalEmails.map((e, i) => {
                    const r = piExternalEmailRoles[e] || 'PI';
                    return (
                      <span key={`${e}-${i}`} className="inline-flex items-center gap-1 bg-violet-50 text-violet-800 border border-violet-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs" data-testid={`assign-investigator-email-chip-${i}`}>
                        {e}
                        <span className="inline-flex rounded-full overflow-hidden border border-violet-200 ml-1">
                          <button
                            type="button"
                            className={`px-1.5 py-0 ${r === 'PI' ? 'bg-amber-200 text-amber-900' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                            onClick={(ev) => { ev.stopPropagation(); setPiExternalEmailRoles(prev => ({ ...prev, [e]: 'PI' })); }}
                            data-testid={`assign-investigator-email-role-pi-${i}`}
                          >PI</button>
                          <button
                            type="button"
                            className={`px-1.5 py-0 border-l border-violet-200 ${r === 'COI' ? 'bg-blue-200 text-blue-900' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                            onClick={(ev) => { ev.stopPropagation(); setPiExternalEmailRoles(prev => ({ ...prev, [e]: 'COI' })); }}
                            data-testid={`assign-investigator-email-role-coi-${i}`}
                          >Co-I</button>
                        </span>
                        <button
                          type="button"
                          className="ml-1 hover:text-violet-950"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setPiExternalEmails(prev => prev.filter((_, j) => j !== i));
                            setPiExternalEmailRoles(prev => { const n = { ...prev }; delete n[e]; return n; });
                          }}
                          aria-label={`Remove ${e}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                  <input
                    id="assign-investigator-email-input"
                    type="email"
                    value={piEmailInputValue}
                    onChange={(e) => setPiEmailInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (['Enter', 'Tab', ','].includes(e.key)) {
                        e.preventDefault();
                        const v = piEmailInputValue.trim().replace(/,$/, '').toLowerCase();
                        if (v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !piExternalEmails.includes(v)) {
                          setPiExternalEmails(prev => [...prev, v]);
                          setPiExternalEmailRoles(prev => ({ ...prev, [v]: 'PI' }));
                        }
                        setPiEmailInputValue('');
                      } else if (e.key === 'Backspace' && !piEmailInputValue && piExternalEmails.length) {
                        const last = piExternalEmails[piExternalEmails.length - 1];
                        setPiExternalEmails(prev => prev.slice(0, -1));
                        setPiExternalEmailRoles(prev => { const n = { ...prev }; delete n[last]; return n; });
                      }
                    }}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData('text');
                      if (text.includes(',') || text.includes(' ') || text.includes('\n')) {
                        e.preventDefault();
                        const parts = text.split(/[\s,;\n]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
                        const valid = parts.filter(s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && !piExternalEmails.includes(s));
                        if (valid.length) {
                          setPiExternalEmails(prev => [...prev, ...valid]);
                          setPiExternalEmailRoles(prev => { const n = { ...prev }; valid.forEach(v => { n[v] = 'PI'; }); return n; });
                        }
                      }
                    }}
                    onBlur={() => {
                      const v = piEmailInputValue.trim().toLowerCase();
                      if (v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !piExternalEmails.includes(v)) {
                        setPiExternalEmails(prev => [...prev, v]);
                        setPiExternalEmailRoles(prev => ({ ...prev, [v]: 'PI' }));
                        setPiEmailInputValue('');
                      }
                    }}
                    placeholder={piExternalEmails.length === 0 ? "investigator@example.com, another@hospital.com" : ""}
                    className="flex-1 min-w-[160px] outline-none text-sm bg-transparent"
                    data-testid="assign-investigator-external-email-input"
                  />
                </div>
              </div>

              {/* Already assigned list */}
              {eligiblePIs.filter(p => p.already_assigned).length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Already assigned</h4>
                  <div className="flex flex-wrap gap-2">
                    {eligiblePIs.filter(p => p.already_assigned).map(p => {
                      const isPI = (request.selected_pi_user_ids || []).includes(p.id);
                      const isCoI = (request.selected_co_investigator_user_ids || []).includes(p.id);
                      return (
                        <Badge key={p.id} className="bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {p.name}
                          {isPI && <span className="ml-1.5 text-[10px] bg-amber-200 text-amber-900 rounded px-1">PI</span>}
                          {isCoI && <span className="ml-1.5 text-[10px] bg-blue-200 text-blue-900 rounded px-1">Co-I</span>}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pending invitations */}
              {piPendingInvites.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Pending invitations</h4>
                  <div className="space-y-1.5">
                    {piPendingInvites.map((inv, i) => (
                      <div key={`${inv.email}-${i}`} className="flex items-center gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                        <Clock className="w-3.5 h-3.5 text-amber-600" />
                        <span className="font-medium">{inv.email}</span>
                        {inv.cta_role === 'PI' && <span className="text-[10px] bg-amber-200 text-amber-900 rounded px-1">PI</span>}
                        {inv.cta_role === 'CO_INVESTIGATOR' && <span className="text-[10px] bg-blue-200 text-blue-900 rounded px-1">Co-I</span>}
                        <span className="text-amber-700 ml-auto">
                          {inv.status === 'awaiting_registration' && '— awaiting registration'}
                          {inv.status === 'registered_pending_admin_approval' && '— registered, awaiting admin approval'}
                          {inv.status === 'expired' && '— invitation expired'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* iter166c — Viewer (read-only) access toggle. OFF by default in
                CTA Assign Investigators per product rule. */}
            <div
              className="mt-2 flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50"
              data-testid="pi-viewer-toggle-row"
            >
              <Eye className={`w-5 h-5 mt-0.5 shrink-0 ${piViewerEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">Grant view-only access to all docs</span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help">?</span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        Selected investigators will see all docs in the Documents tab with a &ldquo;View only&rdquo; badge — even if they&apos;re not assigned to a specific doc thread.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {piViewerEnabled && (
                  <p className="mt-1 text-xs text-emerald-700">
                    This assignee will be able to see all existing and upcoming documents.
                  </p>
                )}
              </div>
              <Switch
                checked={piViewerEnabled}
                onCheckedChange={setPiViewerEnabled}
                data-testid="pi-viewer-toggle"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignPIModal(false)} disabled={piRouteLoading}>Cancel</Button>
              <Button
                onClick={handleAssignInvestigatorsBatch}
                disabled={piRouteLoading || (piRouteSelectedIds.length === 0 && piExternalEmails.length === 0 && !piEmailInputValue.trim())}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="assign-investigators-submit-btn"
              >
                {piRouteLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning…</> : (() => {
                  const r = piRouteSelectedIds.length;
                  const i = piExternalEmails.length + (piEmailInputValue.trim() ? 1 : 0);
                  if (r === 0 && i === 0) return 'Assign';
                  const parts = [];
                  if (r > 0) parts.push(`Assign ${r}`);
                  if (i > 0) parts.push(`Invite ${i}`);
                  return parts.join(' · ');
                })()}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign to Reviewer(s) Modal */}
        <Dialog open={showAssignReviewerModal} onOpenChange={setShowAssignReviewerModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Assign to Reviewer(s)</DialogTitle>
              <DialogDescription>
                Select one or more reviewers to assign to this request
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Selected count */}
              {selectedReviewerIds.length > 0 && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-800">
                    {selectedReviewerIds.length} reviewer(s) selected
                  </Badge>
                </div>
              )}

              {/* iter166d — Search input for the Reviewers list. */}
              {allReviewers.length > 3 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={reviewerSearch}
                    onChange={(e) => setReviewerSearch(e.target.value)}
                    placeholder="Search reviewers by name or email…"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="assign-reviewers-search"
                  />
                </div>
              )}

              {/* Reviewers list */}
              <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
                {allReviewers.length === 0 ? (
                  <p className="p-4 text-center text-slate-500">No reviewers found</p>
                ) : (
                  allReviewers
                    .filter(reviewer => {
                      if (!reviewerSearch.trim()) return true;
                      const q = reviewerSearch.trim().toLowerCase();
                      return (reviewer.name || '').toLowerCase().includes(q) || (reviewer.email || '').toLowerCase().includes(q);
                    })
                    .map(reviewer => (
                    <div 
                      key={reviewer.id}
                      className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer ${
                        reviewer.already_assigned ? 'opacity-50 bg-slate-100' : ''
                      }`}
                      onClick={() => !reviewer.already_assigned && toggleReviewerSelection(reviewer.id)}
                    >
                      <Checkbox
                        checked={selectedReviewerIds.includes(reviewer.id) || reviewer.already_assigned}
                        disabled={reviewer.already_assigned}
                        onCheckedChange={() => !reviewer.already_assigned && toggleReviewerSelection(reviewer.id)}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{reviewer.name}</p>
                        <p className="text-sm text-slate-500">{reviewer.email}</p>
                      </div>
                      {reviewer.already_assigned && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          Already assigned
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* iter166c — Viewer (read-only) access toggle for reviewers.
                  OFF by default per product rule. */}
              <div
                className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50"
                data-testid="reviewer-viewer-toggle-row"
              >
                <Eye className={`w-5 h-5 mt-0.5 shrink-0 ${reviewerViewerEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">Grant view-only access to all docs</span>
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help">?</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          Selected reviewers will see all docs in the Documents tab with a &ldquo;View only&rdquo; badge — even if they&apos;re not assigned to a specific doc thread.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {reviewerViewerEnabled && (
                    <p className="mt-1 text-xs text-emerald-700">
                      This assignee will be able to see all existing and upcoming documents.
                    </p>
                  )}
                </div>
                <Switch
                  checked={reviewerViewerEnabled}
                  onCheckedChange={setReviewerViewerEnabled}
                  data-testid="reviewer-viewer-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignReviewerModal(false)} disabled={assignReviewersLoading}>Cancel</Button>
              <Button 
                onClick={handleBulkAssignReviewers}
                disabled={selectedReviewerIds.length === 0 || assignReviewersLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {assignReviewersLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : `Assign ${selectedReviewerIds.length > 0 ? `${selectedReviewerIds.length} Reviewer(s)` : 'Reviewers'}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Feasibility Response Modal */}
        <Dialog open={showFeasibilityModal} onOpenChange={setShowFeasibilityModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Feasibility Response</DialogTitle>
              <DialogDescription>Submit your feasibility assessment for this request</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {feasibilityResponseFields.map((field) => (
                <div key={field.id}>
                  {field.type === 'boolean' && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={field.id}
                        checked={feasibilityData[field.id] || false}
                        onCheckedChange={(checked) => setFeasibilityData({ ...feasibilityData, [field.id]: checked })}
                      />
                      <Label htmlFor={field.id} className="flex items-center gap-1">
                        {field.label}
                        {field.required && <span className="text-red-500">*</span>}
                      </Label>
                    </div>
                  )}
                  {field.type === 'number' && (
                    <div>
                      <Label className="flex items-center gap-1 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        type="number"
                        value={feasibilityData[field.id] || ''}
                        onChange={(e) => setFeasibilityData({ ...feasibilityData, [field.id]: e.target.value })}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                      />
                    </div>
                  )}
                  {field.type === 'textarea' && (
                    <div>
                      <Label className="flex items-center gap-1 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      <Textarea
                        value={feasibilityData[field.id] || ''}
                        onChange={(e) => setFeasibilityData({ ...feasibilityData, [field.id]: e.target.value })}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                        rows={3}
                      />
                    </div>
                  )}
                  {field.type === 'select' && field.options && (
                    <div>
                      <Label className="flex items-center gap-1 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      <Select
                        value={feasibilityData[field.id] || ''}
                        onValueChange={(value) => setFeasibilityData({ ...feasibilityData, [field.id]: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
              {feasibilityResponseFields.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No response fields configured for this request.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFeasibilityModal(false)} disabled={feasibilitySubmitting}>Cancel</Button>
              <Button onClick={handleSubmitFeasibility} className="bg-teal-600 hover:bg-teal-700" disabled={feasibilitySubmitting}>
                {feasibilitySubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : 'Submit Response'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>Add a document to this request</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Document Type */}
              <div>
                <Label>Document Type</Label>
                <Select value={uploadData.doc_type} onValueChange={handleDocTypeChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Admin-configured doc types for this request type */}
                    {uploadDocTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type === 'ICF' ? 'Informed Consent Form' : type.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                    {/* Custom doc types saved for this request */}
                    {customDocTypes.filter(type => !uploadDocTypes.includes(type)).map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other (custom)</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Show thread access info */}
                {threadAccessInfo && threadAccessInfo.thread_exists && threadAccessInfo.user_in_visibility && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                    <p className="text-xs text-blue-700 font-medium">
                      Existing {uploadData.doc_type} thread found
                    </p>
                    {/* Hide visibility info from Company Rep */}
                    {!isCompanyRep() && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 px-1 py-0.5 rounded flex items-center gap-1 transition-colors">
                          <Eye className="w-3 h-3" />
                          <span>Visible to: </span>
                          <span>
                            {threadAccessInfo.visible_to_names?.slice(0, 3).map(u => u.name).join(', ')}
                            {threadAccessInfo.visible_to_names?.length > 3 && (
                              <span className="text-blue-700 font-medium"> +{threadAccessInfo.visible_to_names.length - 3} more</span>
                            )}
                          </span>
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-3" align="start">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 pb-2 border-b">
                            <Eye className="w-4 h-4" />
                            <span>Users with access ({threadAccessInfo.visible_to_names?.length || 0})</span>
                          </div>
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {threadAccessInfo.visible_to_names?.map((u, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50">
                                <span className="text-sm text-slate-700">{u.name}</span>
                                <Badge className={`text-xs ${
                                  u.role === 'ADMIN' ? 'bg-purple-100 text-purple-700' :
                                  u.role === 'CHAIRMAN' ? 'bg-emerald-100 text-emerald-700' :
                                  u.role === 'CRD' ? 'bg-blue-100 text-blue-700' :
                                  u.role === 'COORDINATOR' ? 'bg-cyan-100 text-cyan-700' :
                                  u.role === 'PI' || u.role === 'INVESTIGATOR' ? 'bg-amber-100 text-amber-700' :
                                  u.role === 'REVIEWER' ? 'bg-orange-100 text-orange-700' :
                                  u.role === 'COMPANY_REP' ? 'bg-indigo-100 text-indigo-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {u.role === 'COMPANY_REP' ? 'Company Rep' : 
                                   u.role === 'CRD' ? 'CRD' :
                                   u.role === 'INVESTIGATOR' ? 'Investigator' :
                                   u.role.charAt(0) + u.role.slice(1).toLowerCase()}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    )}
                    {!threadAccessInfo.is_privileged && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ You can only select users within this thread&apos;s visibility
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Custom document type name when "Other" is selected */}
              {uploadData.doc_type === 'Other' && (
                <div>
                  <Label>Custom Document Type Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={uploadData.custom_doc_type}
                    onChange={(e) => setUploadData({ ...uploadData, custom_doc_type: e.target.value })}
                    placeholder="Enter document type name"
                    data-testid="custom-doc-type-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">This type will be saved for reuse in this request</p>
                </div>
              )}
              
              {/* File Input */}
              <div>
                <Label>File</Label>
                <Input
                  type="file"
                  onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                />
              </div>
              
              {/* Visibility options — hidden for COMPANY_REP (always company-visible)
                  AND hidden for FT requests (iter167 Issue 25 — FT is fully
                  internal; visibility selection adds noise for coordinators
                  uploading docs). Backend defaults to internal visibility
                  when visibility_groups / visibility_users are empty. */}
              {!isCompanyRep() && request?.type !== 'FT' && (
                <div>
                  <Label>Visibility</Label>
                  <div className="mt-2 space-y-2 border rounded-md p-3 bg-slate-50">
                    {/* Group options */}
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500 font-medium">Groups</p>
                      {['@internal', '@company', '@Reviewers', '@PIs'].map(group => (
                        <label key={group} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={uploadData.visibility_groups.includes(group)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setUploadData({ ...uploadData, visibility_groups: [...uploadData.visibility_groups, group] });
                              } else {
                                setUploadData({ ...uploadData, visibility_groups: uploadData.visibility_groups.filter(g => g !== group) });
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <span className="text-sm">
                            {group === '@internal' && 'Internal staff only'}
                            {group === '@company' && 'Company representatives'}
                            {group === '@Reviewers' && 'Reviewers'}
                            {group === '@PIs' && 'Principal Investigators'}
                          </span>
                        </label>
                      ))}
                    </div>
                    
                    {/* Specific users */}
                    {assignments.filter(a => a.status !== 'HANDED_OVER').length > 0 && (
                      <div className="pt-2 border-t">
                        <p className="text-xs text-slate-500 font-medium mb-1">Specific Users</p>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {assignments.filter(a => a.status !== 'HANDED_OVER').map(a => (
                            <label key={a.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={uploadData.visibility_users.includes(a.assignee_user_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setUploadData({ ...uploadData, visibility_users: [...uploadData.visibility_users, a.assignee_user_id] });
                                  } else {
                                    setUploadData({ ...uploadData, visibility_users: uploadData.visibility_users.filter(u => u !== a.assignee_user_id) });
                                  }
                                }}
                                className="w-4 h-4 rounded border-slate-300"
                              />
                              <span className="text-sm">{a.assignee_name}</span>
                              <Badge className="text-xs" variant="outline">{a.assignment_role}</Badge>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Info for company users */}
              {isCompanyRep() && (
                <p className="text-xs text-slate-500">
                  Documents uploaded by company accounts are visible to Coordinator, Chairman, CRD, and Admin.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadModal(false)} disabled={uploadingDoc}>Cancel</Button>
              <Button onClick={handleUpload} className="bg-blue-900 hover:bg-blue-950" disabled={uploadingDoc}>
                {uploadingDoc ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Specialties Modal */}
        {/* Edit Company Modal (FT only, admin-level) */}
        <Dialog open={showEditCompanyModal} onOpenChange={setShowEditCompanyModal}>
          <DialogContent className="max-w-md" data-testid="edit-company-modal">
            <DialogHeader>
              <DialogTitle>Assign Company</DialogTitle>
              <DialogDescription>
                Assign the Company for this Financial Transaction. This is a one-time assignment — once set, it cannot be changed. It also applies to its study.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <Select
                  value={editCompanyData.company_account_id || ''}
                  onValueChange={(v) => setEditCompanyData(prev => ({ ...prev, company_account_id: v }))}
                >
                  <SelectTrigger data-testid="edit-company-select"><SelectValue placeholder="Pick a Company..." /></SelectTrigger>
                  <SelectContent>
                    {companyOptions.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason for change *</Label>
                <Textarea
                  value={editCompanyData.reason}
                  onChange={(e) => setEditCompanyData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Why is the company being changed?"
                  rows={3}
                  data-testid="edit-company-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditCompanyModal(false)} disabled={savingCompany}>Cancel</Button>
              <Button
                onClick={handleUpdateCompany}
                disabled={savingCompany || !editCompanyData.company_account_id || !editCompanyData.reason.trim()}
                className="bg-blue-900 hover:bg-blue-950"
                data-testid="edit-company-save-btn"
              >
                {savingCompany ? 'Saving…' : 'Assign Company'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showEditSpecialtyModal} onOpenChange={setShowEditSpecialtyModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Request Specialties</DialogTitle>
              <DialogDescription>
                Change the specialties for this request. The company will be notified of this change.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Specialties <span className="text-red-500">*</span></Label>
                <div className="mt-2">
                  <SpecialtyMultiSelect
                    specialties={specialties}
                    value={editSpecialtyData.specialty_ids || []}
                    onChange={(ids) => setEditSpecialtyData((prev) => ({ ...prev, specialty_ids: ids }))}
                    testidPrefix="edit-spec"
                  />
                </div>
              </div>
              <div>
                <Label>Reason for Change <span className="text-red-500">*</span></Label>
                <Textarea
                  value={editSpecialtyData.reason}
                  onChange={(e) => setEditSpecialtyData({ ...editSpecialtyData, reason: e.target.value })}
                  placeholder="Explain why the specialties are being changed"
                  rows={3}
                  data-testid="edit-specialty-reason"
                />
                <p className="text-xs text-slate-500 mt-1">This reason will be included in the notification to the company.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditSpecialtyModal(false)}>Cancel</Button>
              <Button 
                onClick={handleUpdateSpecialties} 
                className="bg-blue-900 hover:bg-blue-950"
                disabled={editSpecialtyData.specialty_ids?.length === 0 || !editSpecialtyData.reason?.trim()}
                data-testid="save-specialties-btn"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Comments Modal */}
        <Dialog open={showDocCommentsModal} onOpenChange={setShowDocCommentsModal}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Comments on {selectedDocForComments?.filename}
              </DialogTitle>
              <DialogDescription>
                {selectedDocForComments?.doc_type} • Version {selectedDocForComments?.version}
              </DialogDescription>
            </DialogHeader>
            {selectedDocForComments && (
              <DiscussionThread 
                requestId={id} 
                requestType={request?.type}
                documentId={selectedDocForComments.id}
                title="Document Comments"
                readOnly={hasUserApproved}
                userHasApproved={hasUserApproved}
                onPreviewDoc={(docId) => {
                  if (!docId) return;
                  setThreadPreviewDoc({ id: docId });
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Handover Modal */}
        <Dialog open={showHandoverModal} onOpenChange={setShowHandoverModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-amber-600" />
                Handover Request
              </DialogTitle>
              <DialogDescription>
                Transfer coordinator responsibilities to another team member
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>New Coordinator <span className="text-red-500">*</span></Label>
                <Select 
                  value={handoverData.new_coordinator_id} 
                  onValueChange={(v) => setHandoverData(prev => ({ ...prev, new_coordinator_id: v }))}
                >
                  <SelectTrigger data-testid="handover-coordinator-select">
                    <SelectValue placeholder="Select a coordinator" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCoordinators.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason for Handover <span className="text-red-500">*</span></Label>
                <Textarea
                  value={handoverData.reason}
                  onChange={(e) => setHandoverData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Explain why this request needs to be handed over..."
                  rows={3}
                  data-testid="handover-reason-input"
                />
              </div>
              {isChairman() && (
                <p className="text-sm text-slate-500 bg-slate-50 p-2 rounded">
                  <strong>Note:</strong> As Chairman, this handover will be immediate without requiring acceptance.
                </p>
              )}
              {!isChairman() && (
                <p className="text-sm text-slate-500 bg-slate-50 p-2 rounded">
                  <strong>Note:</strong> The new coordinator will need to accept this handover request.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHandoverModal(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateHandover}
                disabled={handoverLoading || !handoverData.new_coordinator_id || !handoverData.reason?.trim()}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-handover-btn"
              >
                {handoverLoading ? 'Processing...' : 'Initiate Handover'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Handover Modal */}
        <Dialog open={showRejectHandoverModal} onOpenChange={setShowRejectHandoverModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Reject Handover</DialogTitle>
              <DialogDescription>
                This will reject the handover and escalate to the Chairman.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Reason for Rejection <span className="text-red-500">*</span></Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why you cannot accept this handover..."
                  rows={3}
                  data-testid="reject-reason-input"
                />
              </div>
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                <strong>Note:</strong> The Chairman will be notified and may reassign this request.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectHandoverModal(false)}>Cancel</Button>
              <Button 
                onClick={handleRejectHandover}
                disabled={handoverLoading || !rejectReason?.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-reject-handover-btn"
              >
                {handoverLoading ? 'Processing...' : 'Reject Handover'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Blocked Modal - User outside thread trying to upload */}
        <Dialog open={showBlockedModal} onOpenChange={setShowBlockedModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                Cannot Upload to This Thread
              </DialogTitle>
              <DialogDescription>
                {threadAccessInfo?.message || `A ${uploadData.doc_type} thread already exists but you don't have access to it.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Only show privileged contacts for non-Company Rep users */}
              {user?.role !== 'COMPANY_REP' && threadAccessInfo?.privileged_contacts?.length > 0 && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Contact one of the following to request access:</p>
                  <div className="space-y-1">
                    {threadAccessInfo?.privileged_contacts?.slice(0, 5).map((contact, idx) => (
                      <div key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {contact.name} <Badge variant="outline" className="text-xs">{contact.role}</Badge>
                        <span className="text-slate-400 text-xs">({contact.reason})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-sm text-slate-600">
                Click &quot;Send Message&quot; to create an access request in the Discussion thread.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowBlockedModal(false); setUploadData({ ...uploadData, doc_type: '' }); }}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleSendAccessRequest('upload', uploadData.visibility_users)}
                disabled={accessRequestSending}
                className="bg-blue-900 hover:bg-blue-950"
              >
                {accessRequestSending ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Visibility Warning Modal - Adding users outside thread */}
        <Dialog open={showVisibilityWarningModal} onOpenChange={setShowVisibilityWarningModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                {pendingUpload?.canProceed ? 'Expand Thread Visibility?' : 'Cannot Add Users Outside Thread'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {pendingUpload?.canProceed ? (
                <>
                  <p className="text-sm text-slate-600">
                    The following users will gain access to the entire {uploadData.doc_type} thread and all its documents:
                  </p>
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    {pendingUpload?.newUsers?.map(uid => {
                      const user = assignments.find(a => a.assignee_user_id === uid);
                      return (
                        <div key={uid} className="text-sm text-amber-800 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {user?.assignee_name || uid}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    You don&apos;t have permission to add users outside the current thread visibility.
                    {!isCompanyRep() && ' Please contact a privileged user to expand the thread visibility.'}
                  </p>
                  {/* Hide privileged users list from Company Rep */}
                  {!isCompanyRep() && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-2">Privileged users:</p>
                    <div className="space-y-1">
                      {threadAccessInfo?.visible_to_names?.filter(u => 
                        ['ADMIN', 'COORDINATOR', 'CRD', 'CHAIRMAN'].includes(u.role)
                      ).slice(0, 4).map((contact, idx) => (
                        <div key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          {contact.name} <Badge variant="outline" className="text-xs">{contact.role}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowVisibilityWarningModal(false); setPendingUpload(null); }}>
                Cancel
              </Button>
              {pendingUpload?.canProceed ? (
                <Button 
                  onClick={() => executeUpload()}
                  className="bg-blue-900 hover:bg-blue-950"
                >
                  Confirm & Upload
                </Button>
              ) : (
                <Button 
                  onClick={() => handleSendAccessRequest('visibility', pendingUpload?.newUsers || [])}
                  disabled={accessRequestSending}
                  className="bg-blue-900 hover:bg-blue-950"
                >
                  {accessRequestSending ? 'Sending...' : 'Send Message'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CTA Final Approval Modal — per-document (default) or bundle mode (Phase 4) */}
        <CTAFinalApprovalModal
          open={showCTAFinalApprovalModal && !ctaEsignBundle}
          onOpenChange={setShowCTAFinalApprovalModal}
          requestId={id}
          request={request}
          requestType={request?.type || 'CTA'}
          onSwitchMode={() => setCtaEsignBundle(true)}
          onSuccess={() => {
            fetchData();
            toast.success('Final approval submitted with e-signature configuration');
          }}
        />
        <FtChairmanApprovalModal
          open={showCTAFinalApprovalModal && ctaEsignBundle}
          onOpenChange={setShowCTAFinalApprovalModal}
          request={request}
          onSwitchMode={() => setCtaEsignBundle(false)}
          onSuccess={() => {
            fetchData();
            toast.success('Final approval submitted with e-signature configuration');
          }}
        />

        {/* Edit E-Signature Configuration Modal — modal picked by the request's e-sign mode */}
        <CTAFinalApprovalModal
          open={showEditESigModal && !ctaEsignBundle}
          onOpenChange={(open) => {
            setShowEditESigModal(open);
            if (!open) setEditEsigFocusDocId(null);
          }}
          requestId={id}
          request={request}
          requestType={request?.type || 'CTA'}
          editMode
          focusDocumentId={editEsigFocusDocId}
          onSuccess={async () => {
            // iter147 — Edit Signatories used to require a hard refresh to surface the
            // new signatory rows. Force a sequential refresh: full fetch then an explicit
            // signature-status re-pull so the SignaturesTab card refreshes immediately.
            await fetchData();
            try {
              const sigRes = await signaturesApi.getStatus(id);
              setSignatureStatus(sigRes.data);
              setSignatures(sigRes.data?.signatures || []);
            } catch (e) { /* noop */ }
          }}
        />
        <FtChairmanApprovalModal
          open={showEditESigModal && ctaEsignBundle}
          onOpenChange={setShowEditESigModal}
          request={request}
          editMode
          onSuccess={async () => {
            await fetchData();
            try {
              const sigRes = await signaturesApi.getStatus(id);
              setSignatureStatus(sigRes.data);
              setSignatures(sigRes.data?.signatures || []);
            } catch (e) { /* noop */ }
          }}
        />

        {/* Start New Signature Round Modal — CTA Phase 3/4 (after READY_TO_EXECUTE / EXECUTED) */}
        <CTAFinalApprovalModal
          open={showStartRoundModal && !ctaEsignBundle}
          onOpenChange={setShowStartRoundModal}
          requestId={id}
          request={request}
          requestType={request?.type || 'CTA'}
          roundMode
          onSwitchMode={() => setCtaEsignBundle(true)}
          onSuccess={() => {
            fetchData();
          }}
        />
        <FtChairmanApprovalModal
          open={showStartRoundModal && ctaEsignBundle}
          onOpenChange={setShowStartRoundModal}
          request={request}
          roundMode
          onSwitchMode={() => setCtaEsignBundle(false)}
          onSuccess={() => {
            fetchData();
          }}
        />

        {/* Skip Pending Approvals — Admin-level (Chairman/Admin/CRD) and active CTA Coordinator */}
        <AlertDialog open={showSkipApprovalsDialog} onOpenChange={(open) => { if (!skippingApprovals) setShowSkipApprovalsDialog(open); }}>
          <AlertDialogContent data-testid="skip-approvals-dialog" className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                {(() => {
                  const coordinatorFlow = canApproveAsCoordinator && !isAdminLevel;
                  if (coordinatorFlow) {
                    return pendingApprovers.length === 0
                      ? 'Approve this request?'
                      : `Approve and skip ${pendingApprovers.length} pending reviewer${pendingApprovers.length === 1 ? '' : 's'}?`;
                  }
                  return pendingApprovers.length === 0
                    ? 'Approve without team review?'
                    : pendingApprovers.length === 1
                      ? `Skip ${pendingApprovers[0].assignment_role === 'COORDINATOR' ? 'Coordinator' : (pendingApprovers[0].assignment_role || '').toLowerCase()} approval?`
                      : `Skip ${pendingApprovers.length} pending approvals?`;
                })()}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-slate-600">
                  {(() => {
                    const coordinatorFlow = canApproveAsCoordinator && !isAdminLevel;
                    if (coordinatorFlow) {
                      return pendingApprovers.length === 0 ? (
                        <p>
                          No PI / Investigator / Reviewer approvals are outstanding. Approving now will
                          record your Coordinator approval and send the request to the Chairman for
                          <strong> Final Approval &amp; E-Signature Configuration</strong>.
                        </p>
                      ) : (
                        <>
                          <p>The following reviewer{pendingApprovers.length === 1 ? ' has' : 's have'} not yet approved this request:</p>
                          <ul className="space-y-2">
                            {pendingApprovers.map((p) => (
                              <li
                                key={p.assignment_id}
                                data-testid={`pending-approver-${p.assignment_id}`}
                                className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2"
                              >
                                <div className="min-w-0">
                                  <div className="text-sm text-slate-800">
                                    <strong>{formatAssignmentRoleLabel(p.assignment_role)}</strong>
                                    {' — '}
                                    {p.user_name}
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 border-amber-300 text-amber-800 hover:bg-amber-100"
                                  data-testid={`nudge-approver-${p.assignment_id}`}
                                  disabled={nudgingApproverId === p.assignment_id || skippingApprovals}
                                  onClick={(e) => { e.preventDefault(); handleNudgeApprover(p); }}
                                >
                                  {nudgingApproverId === p.assignment_id ? 'Nudging…' : 'Nudge'}
                                </Button>
                              </li>
                            ))}
                          </ul>
                          <p>
                            Approving now will record your Coordinator approval, skip{' '}
                            {pendingApprovers.length === 1 ? 'this review' : 'ALL of these reviews'},
                            and send the request to the Chairman for{' '}
                            <strong>Final Approval &amp; E-Signature Configuration</strong>. Skipped
                            assignments are logged in the audit trail and shown on the request page.
                          </p>
                        </>
                      );
                    }
                    return pendingApprovers.length === 0 ? (
                      <p>
                        No team members have been assigned to review this request yet (no Coordinator, no PI, no Reviewers).
                        Approving now will bypass the entire team review stage and take you directly to{' '}
                        <strong>Final Approval &amp; E-Signature Configuration</strong>.
                      </p>
                    ) : (
                      <>
                        <p>The following team member{pendingApprovers.length === 1 ? ' has' : 's have'} not yet approved this request:</p>
                        <ul className="space-y-2">
                          {pendingApprovers.map((p) => (
                            <li
                              key={p.assignment_id}
                              data-testid={`pending-approver-${p.assignment_id}`}
                              className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2"
                            >
                              <div className="min-w-0">
                                <div className="text-sm text-slate-800">
                                  <strong>{formatAssignmentRoleLabel(p.assignment_role)}</strong>
                                  {' — '}
                                  {p.user_name}
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 border-amber-300 text-amber-800 hover:bg-amber-100"
                                data-testid={`nudge-approver-${p.assignment_id}`}
                                disabled={nudgingApproverId === p.assignment_id || skippingApprovals}
                                onClick={(e) => { e.preventDefault(); handleNudgeApprover(p); }}
                              >
                                {nudgingApproverId === p.assignment_id ? 'Nudging…' : 'Nudge'}
                              </Button>
                            </li>
                          ))}
                        </ul>
                        <p>
                          Approving now will skip{' '}
                          {pendingApprovers.length === 1 ? 'this review' : 'ALL of these reviews'} and take you directly to{' '}
                          <strong>Final Approval &amp; E-Signature Configuration</strong>. Skipped assignments will be
                          marked in the audit log and shown on the request page.
                        </p>
                      </>
                    );
                  })()}
                  <p className="text-xs text-slate-500">This action is logged in the audit trail.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-skip-approvals-btn" disabled={skippingApprovals}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); handleConfirmSkipApprovals(); }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-skip-approvals-btn"
                disabled={skippingApprovals}
              >
                {skippingApprovals ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
                ) : (() => {
                  const coordinatorFlow = canApproveAsCoordinator && !isAdminLevel;
                  if (coordinatorFlow) {
                    return pendingApprovers.length === 0
                      ? <>Approve &amp; Send to Chairman →</>
                      : <>Approve &amp; Skip {pendingApprovers.length} Reviewer{pendingApprovers.length === 1 ? '' : 's'} →</>;
                  }
                  return pendingApprovers.length === 0
                    ? <>Approve &amp; Continue →</>
                    : <>Skip {pendingApprovers.length} Approver{pendingApprovers.length === 1 ? '' : 's'} &amp; Continue →</>;
                })()}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Admin Status Override Modal */}
        <Dialog open={showAdminOverrideModal} onOpenChange={setShowAdminOverrideModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <ShieldAlert className="w-5 h-5" />
                Admin Status Override
              </DialogTitle>
              <DialogDescription>
                Force change the request status. This action is logged in the audit trail and visible to all users.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <span className="text-slate-500">Current status: </span>
                <Badge className={getStatusColor(request?.status)}>{getStatusLabel(request?.status, request?.type)}</Badge>
              </div>
              <div className="space-y-2">
                <Label>New Status *</Label>
                <Select value={adminOverrideStatus} onValueChange={setAdminOverrideStatus}>
                  <SelectTrigger data-testid="admin-override-status-select">
                    <SelectValue placeholder="Select target status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      // Group statuses by their `group` key, preserving order of first appearance.
                      const groups = [];
                      const map = new Map();
                      for (const s of validStatuses) {
                        const g = s.group || 'Other';
                        if (!map.has(g)) {
                          map.set(g, []);
                          groups.push(g);
                        }
                        map.get(g).push(s);
                      }
                      return groups.map(g => (
                        <SelectGroup key={g}>
                          <SelectLabel className="text-xs uppercase tracking-wide text-slate-500">{g}</SelectLabel>
                          {map.get(g).map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectGroup>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason * <span className="text-slate-400 text-xs">(required for compliance)</span></Label>
                <Textarea
                  value={adminOverrideReason}
                  onChange={(e) => setAdminOverrideReason(e.target.value)}
                  placeholder="e.g., Reopened per sponsor request, Protocol violation, etc."
                  rows={3}
                  data-testid="admin-override-reason"
                />
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                This override bypasses the normal workflow. It will be recorded in the audit log as an admin action and all relevant parties will be notified.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdminOverrideModal(false)}>Cancel</Button>
              <Button
                onClick={handleAdminForceStatus}
                disabled={!adminOverrideStatus || !adminOverrideReason.trim() || adminOverrideLoading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="admin-override-confirm-btn"
              >
                {adminOverrideLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ShieldAlert size={16} className="mr-2" />}
                {adminOverrideLoading ? 'Changing...' : 'Force Status Change'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pending Thread Approval Gate (iter112: rebranded from "Document approval" to
            "Thread approval" — gate shows threads not docs; the Preview button is
            replaced by a Nudge button) */}
        <Dialog open={docGateOpen} onOpenChange={setDocGateOpen}>
          <DialogContent className="max-w-3xl border-red-300" data-testid="doc-gate-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-900">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Thread approval pending
              </DialogTitle>
              <DialogDescription>
                You&apos;re assigned to review thread{pendingDocThreads.length > 1 ? 's' : ''} first.
                Please approve {pendingDocThreads.length > 1 ? 'each one' : 'it'} in the Discussion
                section before signing the request.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto" data-testid="doc-gate-list">
              {/* iter113 — dedupe by thread_id as a safety net (backend already dedupes,
                  but if anything slips through we still render one row per thread). */}
              {Array.from(new Map(pendingDocThreads.map((t) => [t.thread_id, t])).values()).map((t) => {
                const isApproved = docGateApproved.includes(t.document_id);
                const threadLabel = `${t.doc_type || 'Document'} thread`;
                return (
                  <div
                    key={t.thread_id}
                    className={`border rounded-lg p-3 transition-colors ${
                      isApproved ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'
                    }`}
                    data-testid={`doc-gate-item-${t.thread_id}`}
                  >
                    {/* Line 1: thread label (left) + status badge (right) */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <MessageSquare className="w-4 h-4 text-red-600 shrink-0" />
                        <span
                          className={`text-sm font-semibold truncate ${isApproved ? 'text-emerald-900' : 'text-red-900'}`}
                          title={threadLabel}
                          data-testid={`doc-gate-thread-label-${t.thread_id}`}
                        >
                          {threadLabel}
                        </span>
                      </div>
                      <Badge
                        className={`shrink-0 ${
                          isApproved
                            ? 'bg-emerald-100 text-emerald-700'
                            : t.current_status === 'CHANGES_REQUESTED'
                              ? 'bg-amber-100 text-amber-800 border-amber-200'
                              : 'bg-blue-100 text-blue-800 border-blue-200'
                        }`}
                        data-testid={`doc-gate-type-chip-${t.thread_id}`}
                      >
                        {isApproved
                          ? 'APPROVED'
                          : (t.current_status === 'CHANGES_REQUESTED'
                              ? 'Changes Requested'
                              : 'Pending')}
                      </Badge>
                    </div>
                    {/* Line 2: assigned-to */}
                    {!isApproved && t.assignee_names?.length > 0 && (
                      <div
                        className="text-xs text-red-700/80 mt-1 pl-6"
                        data-testid={`doc-gate-assignee-${t.thread_id}`}
                      >
                        Assigned to: {t.assignee_names.join(', ')}
                        {/* iter131 — per-assignee partial-approval progress so the remaining
                            reviewers know who's already ticked off. */}
                        {t.assignee_count > 1 && t.approved_count > 0 && (
                          <span
                            className="ml-2 text-emerald-700"
                            data-testid={`doc-gate-progress-${t.thread_id}`}
                          >
                            · {t.approved_count} of {t.assignee_count} approved
                            {t.approved_by_names?.length > 0 && ` (${t.approved_by_names.join(', ')})`}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Line 3: right-aligned buttons */}
                    {isApproved ? (
                      <div className="flex items-center justify-end pl-6 mt-1">
                        <span
                          className="flex items-center gap-1 text-emerald-700 text-sm font-medium"
                          data-testid={`doc-gate-approved-${t.thread_id}`}
                        >
                          <CheckCircle2 className="w-4 h-4" /> Approved
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-end gap-2 pl-6 mt-2">
                        {/* iter131 — Nudge button is only meaningful for privileged escalators
                            (Chairman/CRD/Admin/Director/ED/Coordinator). Assignees themselves
                            shouldn't be able to nudge — they ARE the ones who need to approve.
                            Backend already enforces this (403); we hide the button so reviewers
                            aren't tempted to click into a failed call.
                            iter148d — also hide when the only remaining pending assignee IS the
                            current user (no point in nudging yourself). */}
                        {(isChairman() || isCRD() || isAdmin() || isDirector() || isExecDirector() || isCoordinator()) &&
                          (((t.status_assignees || t.document_status_assignees || []).filter(uid => uid !== user?.id)).length > 0) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleNudgeAssignee(t)}
                            disabled={nudgingDocId === t.document_id}
                            className="border-amber-400 text-amber-700 hover:bg-amber-50"
                            data-testid={`doc-gate-nudge-${t.thread_id}`}
                          >
                            <Bell className="w-3.5 h-3.5 mr-1" />
                            {nudgingDocId === t.document_id ? 'Nudging…' : 'Nudge'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => setDocGateConfirm({ t, asOverride: true })}
                          disabled={docGateApproving === t.document_id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`doc-gate-approve-${t.thread_id}`}
                          title={isCoordinator() ? 'Approve and bypass the remaining assignees — recorded as override in the audit trail' : undefined}
                        >
                          {docGateApproving === t.document_id ? 'Approving…' : 'Approve'}
                        </Button>
                        {/* iter134 — Coordinator can opt into the per-assignee path with this
                            secondary button. Visible only when the Coordinator is in the
                            thread's status_assignees AND others are still pending. The
                            backend receives as_override=false → only their slot ticks. */}
                        {isCoordinator()
                          && (t.status_assignees || []).includes(user?.id)
                          && (t.assignee_count > 1)
                          && (t.approved_count < t.assignee_count) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDocGateConfirm({ t, asOverride: false })}
                            disabled={docGateApproving === t.document_id}
                            className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`doc-gate-approve-mine-${t.thread_id}`}
                            title="Record your personal approval only. The doc thread stays Pending Review until the other assignees approve."
                          >
                            Approve from my side
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => navigateToDocThread(t)}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid={`doc-gate-goto-${t.thread_id}`}
                        >
                          Go to thread →
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <DialogFooter className="mt-3">
              {/* iter148c — Chairman / Admin / CRD can bypass the pending doc-thread gate
                  and proceed with whatever IS already approved (per user request: "give
                  him the option to proceed with the approved docs to start the signature
                  and reviewing process"). The pending threads stay open in the background
                  so reviewers can still approve later. */}
              {(isChairman() || isAdmin() || isCRD()) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setDocGateOpen(false);
                    if (skipApprovalsTarget === 'start-round') {
                      // iter167 Issue 27 — FT routes via its own opener
                      // (FtWorkflowActions owns the modal). CTA stays on
                      // the parent's setShowStartRoundModal.
                      if (request?.type === 'FT' && typeof ftStartRoundOpenerRef.current === 'function') {
                        const opener = ftStartRoundOpenerRef.current;
                        ftStartRoundOpenerRef.current = null;
                        opener();
                      } else {
                        setCtaEsignBundle(false);
                        setShowStartRoundModal(true);
                      }
                    } else {
                      setShowCTAFinalApprovalModal(true);
                    }
                  }}
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="doc-gate-bypass-proceed"
                  title="Skip the pending doc threads and proceed with the docs that are already approved. Pending threads stay open for reviewers."
                >
                  Proceed with approved docs →
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setDocGateOpen(false)}
                data-testid="doc-gate-close"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter148d — Execute Request readiness modal. Aggregates the 3 buckets of
            outstanding work so the Chairman sees exactly what's still pending before
            locking the request. Proceed anyway is allowed (per user — they may
            intentionally execute despite open thread reviews). */}
        {(() => {
          if (!showExecuteConfirm) return null;
          const missedDocThreads = (documents || []).filter(d => !d.is_ft_bundle && d.status !== 'APPROVED');
          const missedRequestApprovals = (assignments || []).filter(a => a.status === 'PENDING');
          const pendingSignatures = (signatures || []).filter(s => s.status === 'PENDING');
          const hasAnyOutstanding = missedDocThreads.length + missedRequestApprovals.length + pendingSignatures.length > 0;
          return (
            <Dialog open={showExecuteConfirm} onOpenChange={setShowExecuteConfirm}>
              <DialogContent className="max-w-lg" data-testid="execute-confirm-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="w-5 h-5" />
                    Execute this request?
                  </DialogTitle>
                  <DialogDescription className="text-slate-600 pt-1">
                    This is <strong>final</strong> — the request will be locked and marked <strong>EXECUTED</strong>.
                    {hasAnyOutstanding && ' Please review the outstanding items below.'}
                  </DialogDescription>
                </DialogHeader>
                {hasAnyOutstanding ? (
                  <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                    {missedDocThreads.length > 0 && (
                      <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="execute-missed-doc-threads">
                        <div className="font-semibold text-amber-900 text-sm mb-1">📄 Missed doc thread approvals ({missedDocThreads.length})</div>
                        <ul className="text-xs text-amber-800 space-y-0.5 list-disc pl-5">
                          {missedDocThreads.map(d => (
                            <li key={d.id}>{d.filename || d.doc_type || d.id} — <span className="opacity-70">{d.status}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {missedRequestApprovals.length > 0 && (
                      <div className="border border-rose-200 bg-rose-50 rounded-md p-3" data-testid="execute-missed-request-approvals">
                        <div className="font-semibold text-rose-900 text-sm mb-1">✋ Missed request approvals ({missedRequestApprovals.length})</div>
                        <ul className="text-xs text-rose-800 space-y-0.5 list-disc pl-5">
                          {missedRequestApprovals.map(a => (
                            <li key={a.id}>{a.assignee_name || a.assignee_user_id} — <span className="opacity-70">{a.assignment_role}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {pendingSignatures.length > 0 && (
                      <div className="border border-violet-200 bg-violet-50 rounded-md p-3" data-testid="execute-pending-signatures">
                        <div className="font-semibold text-violet-900 text-sm mb-1">✍️ Pending signatures ({pendingSignatures.length})</div>
                        <ul className="text-xs text-violet-800 space-y-0.5 list-disc pl-5">
                          {pendingSignatures.map(s => (
                            <li key={s.id}>{s.typed_name || s.user_id} — <span className="opacity-70">{s.signature_role || s.user_role}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm text-emerald-900" data-testid="execute-all-clear">
                    ✅ All approvals, signatures, and doc threads are complete. Safe to execute.
                  </div>
                )}
                <DialogFooter className="mt-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowExecuteConfirm(false)}
                    data-testid="execute-confirm-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitExecuteRequest}
                    className={hasAnyOutstanding ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                    data-testid="execute-confirm-submit"
                    disabled={executeLoading}
                  >
                    {hasAnyOutstanding ? 'Execute anyway' : 'Execute & lock'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          );
        })()}


        {/* iter148c — confirmation dialog for the doc-gate "Approve" and "Approve from
            my side" buttons. Before iter148c the buttons fired immediately on click; now
            we ask the user to confirm so a stray click doesn't accidentally bypass a
            thread or log a personal approval. */}
        <Dialog open={!!docGateConfirm} onOpenChange={(o) => { if (!o) setDocGateConfirm(null); }}>
          <DialogContent className="max-w-md" data-testid="doc-gate-confirm-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="w-5 h-5" />
                {docGateConfirm?.asOverride ? 'Approve this doc thread?' : 'Approve from your side?'}
              </DialogTitle>
              <DialogDescription className="text-slate-600 pt-1">
                {docGateConfirm?.asOverride ? (
                  <>
                    You&apos;re about to mark <strong>{docGateConfirm?.t?.document_name || 'this document'}</strong> as approved
                    {docGateConfirm?.t && docGateConfirm.t.approved_count < docGateConfirm.t.assignee_count - 1
                      ? ` — bypassing ${docGateConfirm.t.assignee_count - (docGateConfirm.t.approved_count + 1)} pending reviewer(s).`
                      : '.'}
                  </>
                ) : (
                  <>
                    This records <strong>your personal approval only</strong>. The doc thread stays
                    “🔵 Pending Review / Changes Requested” until the other assignees approve from their side.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
              {docGateConfirm?.asOverride
                ? '💡 This action is logged in the audit trail as an override.'
                : '💡 Your slot will tick off but the overall thread status will not change until others approve.'}
            </div>
            <DialogFooter className="mt-3">
              <Button
                variant="outline"
                onClick={() => setDocGateConfirm(null)}
                data-testid="doc-gate-confirm-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const { t, asOverride } = docGateConfirm || {};
                  setDocGateConfirm(null);
                  if (!t) return;
                  if (!asOverride) {
                    toast.info(
                      `Recording your personal approval only. Doc thread status stays "🔵 Pending Review / Changes Requested" until the other ${t.assignee_count - (t.approved_count + 1)} assignee(s) approve from their side.`,
                      { duration: 6000 }
                    );
                  }
                  await handleDocGateApprove(t, !!asOverride);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="doc-gate-confirm-submit"
              >
                {docGateConfirm?.asOverride ? 'Approve' : 'Approve from my side'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter125 — friendly Approve confirmation modal (replaces window.confirm). */}
        <Dialog open={showApproveConfirm} onOpenChange={setShowApproveConfirm}>
          <DialogContent className="max-w-md" data-testid="approve-confirm-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="w-5 h-5" />
                Approve this request?
              </DialogTitle>
              <DialogDescription className="text-slate-600 pt-1">
                After approving, you&apos;ll no longer be able to:
              </DialogDescription>
            </DialogHeader>
            <ul className="text-sm space-y-1.5 pl-2 my-2 text-slate-700">
              <li className="flex items-start gap-2"><span className="text-emerald-600">•</span> Upload documents</li>
              <li className="flex items-start gap-2"><span className="text-emerald-600">•</span> Reply on doc threads</li>
              <li className="flex items-start gap-2"><span className="text-emerald-600">•</span> Change doc thread status</li>
              <li className="flex items-start gap-2"><span className="text-emerald-600">•</span> Use the Assign-to button</li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
              💡 To resume, ask the request&apos;s assigned <strong>Coordinator</strong> (who can undo your approval),
              or an <strong>Admin / CRD / Chairman / Director / Exec Director</strong>.
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setShowApproveConfirm(false)} data-testid="approve-confirm-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => submitApproval('APPROVE')}
                disabled={approveLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="approve-confirm-submit"
              >
                {approveLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Approve &amp; lock
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Doc-gate preview modal (uses the unified DocumentPreviewModal — Approve + Nudge live inside it now) */}
        <DocumentPreviewModal
          open={!!gatePreviewDoc}
          onOpenChange={(open) => { if (!open) setGatePreviewDoc(null); }}
          doc={gatePreviewDoc}
          requestId={id}
          allowNudge
          onApproved={(docId) => {
            // mark approved in the gate UI and close the preview
            setDocGateApproved((prev) => prev.includes(docId) ? prev : [...prev, docId]);
            setGatePreviewDoc(null);
          }}
          onGoToThread={(threadId) => {
            const t = pendingDocThreads.find(p => p.thread_id === threadId);
            setGatePreviewDoc(null);
            if (t) navigateToDocThread(t);
          }}
        />

        {/* Discussion-tab "Preview" button modal — opened from inside a thread.
            No Nudge here (Nudge is gate-only). "Go to discussion" just closes the
            modal since the user is already on the discussion tab. */}
        <DocumentPreviewModal
          open={!!threadPreviewDoc}
          onOpenChange={(open) => { if (!open) setThreadPreviewDoc(null); }}
          doc={threadPreviewDoc}
          requestId={id}
          onGoToThread={() => setThreadPreviewDoc(null)}
        />

        {/* iter110 — Generate Bundle dialog (lives at the root so it stays mounted across
            tab switches; both the Documents-tab button and the Discussion-tab bundle
            "Upload New Version" route open it via setShowBundleDialog). */}
        <GenerateBundleDialog
          open={showBundleDialog}
          onOpenChange={(open) => {
            setShowBundleDialog(open);
            if (!open) setBundleDialogLockedDocType(null);
          }}
          request={request}
          documents={documents}
          lockedDocType={bundleDialogLockedDocType}
          lockedReason={bundleDialogLockedDocType ? `New version will be added to the "${bundleDialogLockedDocType}" bundle chain.` : null}
          onGenerated={() => {
            documentsApi.getAll(id).then(res => setDocuments(res.data)).catch(() => {});
            // iter111 — also refetch the comments so the bundle-thread's new
            // `📎 Version N uploaded` marker appears immediately (no hard refresh).
            setDiscussionRefreshKey((k) => k + 1);
          }}
        />

        {/* iter143 — Edit Request Review dialog removed (button + dialog dropped per user). */}
      </div>
    </DashboardLayout>
  );
}
